import { DeliveryVectorMap } from '@seller/map/vector/DeliveryVectorMap';
import { ADMIN_DELIVERY_ZONE_LIVE_MAP_PATH, ADMIN_DELIVERY_MAP_GENERATIONS_PATH } from '../adminRoutes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CatalogPagination } from '../catalog/CatalogPagination';
import { CompanyDeliveryAddressesPanel } from '../company/CompanyDeliveryAddressesPanel';
import { CompanyDeliveryZonesPanel } from '../company/CompanyDeliveryZonesPanel';
import { CompanyAccessPanel } from '../company/CompanyAccessPanel';
import { CompanySupplierOffersTab } from '../company/CompanySupplierOffersTab';
import { CompanyChatsTab } from '../chat/CompanyChatsTab';
import { CatalogProductFormState, defaultCatalogProductForm, createCatalogProductCreatePayload } from '../catalog/catalogFormValues';
import { ADMIN_CATALOG_CATEGORY_CREATE_PATH, ADMIN_CATALOG_PRODUCT_CREATE_PATH, getAdminCatalogCategoryEditPath, getAdminCatalogCategoryDetailPath, getAdminCatalogProductEditPath } from '../adminRoutes';
import {
  FormEvent,
  FormHTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import type { GeoJsonObject } from 'geojson';
import { MapCanvas } from '@seller/map';
import { Link } from 'react-router';
import '../../../App.css';
import { CatalogEmptyState } from '../../../components/CatalogEmptyState';
import { CatalogSetupState } from '../../../components/CatalogSetupState';
import { CatalogCategoryMenu } from '../../../components/CatalogCategoryMenu';
import { LoadingState } from '../../../components/LoadingState';
import { AsyncSearchableSelect } from '../../../components/AsyncSearchableSelect';
import { AdminPageHeader } from '../../../components/AdminPageHeader';
import { SearchableSelect, SearchableSelectOption } from '../../../components/SearchableSelect';
import { FormErrorSummary } from '../../../components/FormValidation';
import { PhoneInput } from '../../../components/PhoneInput';
import { TabNavigation } from '../../../components/Tabs';
import { formatRussianCount } from '../../../utils/formatRussianCount';
import { formatFileSize } from '../../../utils/formatFileSize';
import { addFormError, FormErrors, hasFormErrors } from '../../../utils/formValidation';
import {
  AdminBootstrapDictionaries,
  AdminCatalogCategory,
  AdminCatalogViewProduct,
  AdminCatalogViewResult,
  AdminCatalogViewSort,
  AdminCatalogProduct,
  AdminCompany,
  AdminCompanyChat,
  AdminCompanyAgreement,
  AdminCompanyWorkflowCounts,
  AdminCompanyWorkflowStatus,
  AdminCompanyFile,
  AdminCompanyFileLinkCreateRequest,
  AdminCompanyDeliveryAddress,
  AdminCompanyDeliveryAddressSaveRequest,
  AdminDeliveryAddressSuggestion,
  AdminCompanyDeliveryZone,
  AdminCompanyDeliveryZoneItemRequest,
  AdminCompanyUser,
  AdminCompanyDeliveryZonesUpdateRequest,
  AdminDictionaryItem,
  AdminFileContext,
  AdminCompanyUpdateRequest,
  AdminDeliveryZone,
  AdminDeliveryZoneCreateRequest,
  AdminDeliveryZoneUpdateRequest,
  AdminListParams,
  AdminListResult,
  AdminPriceImport,
  AdminPriceImportListParams,
  AdminPriceImportStatusUpdateRequest,
  AdminPriceImportRow,
  AdminUser,
  AdminUserUpdateRequest,
} from '../../../api/types';
import { adminApi } from '../adminApi';
import { formatCatalogDeliveryDate } from '../catalogDeliveryDate';
import { priceImportApi } from '../../priceImport/priceImportApi';
import { AdminProductImage } from '../catalog/AdminProductImage';

export type CompanyDetailTab = 'info' | 'users' | 'chats' | 'files' | 'delivery-addresses' | 'delivery-zones' | 'prices' | 'offers';
export type CompanyDeliveryFormMode = 'create' | 'edit';
export type CatalogTab = 'showcase' | 'categories' | 'products';
const PRICE_FILE_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const COMPANY_STATUS_MODERATION = 1;
const COMPANY_STATUS_APPROVED = 2;
const COMPANY_STATUS_REJECTED = 3;
const COMPANY_STATUS_ARCHIVED = 4;
const COMPANY_STATUS_BLOCKED = 8;
const COMPANY_STATUS_CONTRACT_SIGNING = 5;
const COMPANY_STATUS_SIGNED_SCAN_UPLOADED = 6;
const COMPANY_STATUS_ORIGINAL_RECEIVED = 7;
type CompanyQuickFilter = 'all' | AdminCompanyWorkflowStatus;
const emptyCompanyWorkflowCounts: AdminCompanyWorkflowCounts = {
  all: 0,
  awaiting_original: 0,
  contract_signing: 0,
  moderation: 0,
};
const companyQuickFilters: Array<{ label: string; value: CompanyQuickFilter }> = [
  { label: 'Все', value: 'all' },
  { label: 'Ожидают проверки', value: 'moderation' },
  { label: 'Ожидают подписания', value: 'contract_signing' },
  { label: 'Ожидают получения оригинала', value: 'awaiting_original' },
];
const deliveryAddressMarkerIcon = L.icon({
  iconAnchor: [20, 50],
  iconSize: [40, 52],
  iconUrl: '/map-marker.svg',
  popupAnchor: [0, -46],
});

interface ValidatedFormProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}

function ValidatedForm({
  children,
  onSubmit,
  ...formProps
}: ValidatedFormProps) {
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const summaryId = useId();

  const handleInvalid = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const field = event.target;

    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
      return;
    }

    const fieldKey = getFormControlKey(field);
    const fieldLabel = getFormControlLabel(field);
    const message = getConstraintErrorMessage(field, fieldLabel);

    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', summaryId);
    setFieldErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };

      nextErrors[fieldKey] = [message];

      return nextErrors;
    });
    field.focus();
  };

  const handleInput = (event: FormEvent<HTMLFormElement>): void => {
    const field = event.target;

    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
      return;
    }

    const fieldKey = getFormControlKey(field);

    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    setFieldErrors((currentErrors) => {
      if (currentErrors[fieldKey] === undefined) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };

      delete nextErrors[fieldKey];

      return nextErrors;
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    const numericErrors = validateFormControls(event.currentTarget, summaryId);

    setFieldErrors(numericErrors);

    if (hasFormErrors(numericErrors)) {
      event.preventDefault();

      return;
    }

    onSubmit?.(event);
  };

  return (
    <form
      {...formProps}
      noValidate={false}
      onInput={handleInput}
      onInvalid={handleInvalid}
      onSubmit={handleSubmit}
    >
      {hasFormErrors(fieldErrors) ? (
        <div id={summaryId}>
          <FormErrorSummary errors={fieldErrors} />
        </div>
      ) : null}
      {children}
    </form>
  );
}

function validateFormControls(form: HTMLFormElement, summaryId: string): FormErrors {
  const errors: FormErrors = {};
  const controls = form.querySelectorAll<HTMLInputElement>('input[inputmode="numeric"]');

  controls.forEach((field) => {
    const value = field.value.trim();

    if (value === '' || /^\d+$/.test(value)) {
      return;
    }

    const fieldKey = getFormControlKey(field);
    const fieldLabel = getFormControlLabel(field);

    addFormError(errors, fieldKey, `Поле «${fieldLabel}» должно содержать целое число.`);
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', summaryId);
  });

  form.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach((field) => {
    const file = field.files?.[0] ?? null;

    if (file === null) {
      return;
    }

    const fieldKey = getFormControlKey(field);
    const fieldLabel = getFormControlLabel(field);

    if (file.size === 0) {
      addFormError(errors, fieldKey, `Файл в поле «${fieldLabel}» пуст.`);
    }

    if (!isFileAccepted(file, field.accept)) {
      addFormError(errors, fieldKey, `Формат файла в поле «${fieldLabel}» не поддерживается.`);
    }

    if (errors[fieldKey] === undefined) {
      return;
    }

    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', summaryId);
  });

  form.querySelectorAll<HTMLInputElement>('[data-validation="address-selection"]').forEach((field) => {
    if (field.dataset.selectionValid === 'true') {
      return;
    }

    const fieldKey = getFormControlKey(field);

    addFormError(errors, fieldKey, 'Введите адрес и выберите подходящий вариант из списка.');
    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', summaryId);
  });

  if (form.dataset.requireZoneSelection === 'true' && Number(form.dataset.selectedZoneCount ?? '0') < 1) {
    addFormError(errors, 'zone_ids', 'Выберите одну или несколько зон доставки.');
  }

  return errors;
}

function isFileAccepted(file: File, accept: string): boolean {
  if (accept.trim() === '') {
    return true;
  }

  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  return accept.split(',').some((acceptItem) => {
    const normalizedAcceptItem = acceptItem.trim().toLowerCase();

    if (normalizedAcceptItem.startsWith('.')) {
      return fileName.endsWith(normalizedAcceptItem);
    }

    if (normalizedAcceptItem.endsWith('/*')) {
      return mimeType.startsWith(normalizedAcceptItem.slice(0, -1));
    }

    return mimeType === normalizedAcceptItem;
  });
}

function getFormControlKey(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  return field.name || field.id || field.getAttribute('aria-label') || getFormControlLabel(field);
}

function getFormControlLabel(field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  const label = field.closest('label')?.querySelector('span');

  return label?.textContent?.trim() || field.getAttribute('aria-label') || field.name || 'Обязательное поле';
}

function getConstraintErrorMessage(
  field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  label: string,
): string {
  if (field.validity.valueMissing) {
    return `Заполните поле «${label}».`;
  }

  if (field.validity.typeMismatch && field instanceof HTMLInputElement && field.type === 'email') {
    return `Укажите корректный email в поле «${label}».`;
  }

  if (field.validity.rangeUnderflow) {
    return `Значение поля «${label}» не может быть меньше ${field.getAttribute('min') ?? 'допустимого минимума'}.`;
  }

  if (field.validity.rangeOverflow) {
    return `Значение поля «${label}» превышает допустимый максимум.`;
  }

  if (field.validity.stepMismatch || field.validity.badInput) {
    return `Укажите корректное число в поле «${label}».`;
  }

  if (field.validity.patternMismatch) {
    return `Проверьте формат поля «${label}».`;
  }

  return `Проверьте поле «${label}».`;
}

function IconButton({
  disabled = false,
  href,
  icon,
  label,
  onClick,
  type = 'button',
  variant = 'ghost',
}: {
  disabled?: boolean;
  href?: string;
  icon: string;
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'back' | 'ghost' | 'primary' | 'row';
}) {
  if (href !== undefined && !disabled) {
    return (
      <Link className={`admin-icon-action admin-icon-action_${variant}`} aria-label={label} to={href}>
        <span aria-hidden="true">{icon}</span>
      </Link>
    );
  }

  return (
    <button className={`admin-icon-action admin-icon-action_${variant}`} aria-label={label} disabled={disabled} onClick={onClick} type={type}>
      <span aria-hidden="true">{icon}</span>
    </button>
  );
}

function FilterButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="admin-icon-action" aria-label="Все фильтры" onClick={onClick} type="button">
      <svg className="admin-filter-icon" aria-hidden="true" viewBox="0 0 24 24">
        <path d="M4 6h16" />
        <path d="M7 12h10" />
        <path d="M10 18h4" />
      </svg>
    </button>
  );
}

interface UsersPageProps {
  dictionaries: AdminBootstrapDictionaries;
  onNavigate: (path: string) => void;
}

interface UserFilters {
  user_id: string;
  user_email: string;
  user_full_name: string;
  user_phone: string;
  status_id: string;
  user_email_confirmed: string;
}

const defaultUserFilters: UserFilters = {
  user_id: '',
  user_email: '',
  user_full_name: '',
  user_phone: '',
  status_id: '',
  user_email_confirmed: '',
};

export function UsersPage({ dictionaries, onNavigate }: UsersPageProps) {
  const [users, setUsers] = useState<AdminListResult<AdminUser>>({ items: [], meta: {} });
  const [filters, setFilters] = useState<UserFilters>(defaultUserFilters);
  const [appliedFilters, setAppliedFilters] = useState<UserFilters>(defaultUserFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers(defaultUserFilters);
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAppliedFilters(filters);
    fetchUsers(filters);
  };

  const resetFilters = (): void => {
    setFilters(defaultUserFilters);
    setAppliedFilters(defaultUserFilters);
    fetchUsers(defaultUserFilters);
  };

  function fetchUsers(nextFilters: UserFilters, page = 1): void {
    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getUsers(buildUserListParams(nextFilters, page))
      .then(setUsers)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <section className="admin-page">
      <AdminPageHeader
        description="Находите учётные записи, проверяйте доступ и переходите к связанным компаниям."
        eyebrow="Управление доступом"
        title="Пользователи"
        total={users.meta.total}
      />
      <ValidatedForm className="admin-filter-bar admin-filter-bar_users" onSubmit={handleSubmit}>
        <label className="admin-field">
          <span>Номер</span>
          <input
            inputMode="numeric"
            onChange={(event) => setFilters({ ...filters, user_id: event.target.value })}
            placeholder="42"
            type="search"
            value={filters.user_id}
          />
        </label>
        <label className="admin-field">
          <span>Электронная почта</span>
          <input
            onChange={(event) => setFilters({ ...filters, user_email: event.target.value })}
            placeholder="user@example.com"
            type="search"
            value={filters.user_email}
          />
        </label>
        <label className="admin-field">
          <span>ФИО</span>
          <input
            onChange={(event) => setFilters({ ...filters, user_full_name: event.target.value })}
            placeholder="Иван Петров"
            type="search"
            value={filters.user_full_name}
          />
        </label>
        <label className="admin-field">
          <span>Телефон</span>
          <input
            inputMode="tel"
            onChange={(event) => setFilters({ ...filters, user_phone: event.target.value })}
            placeholder="+79990000000"
            type="search"
            value={filters.user_phone}
          />
        </label>
        <label className="admin-field">
          <span>Статус</span>
          <select
            onChange={(event) => setFilters({ ...filters, status_id: event.target.value })}
            value={filters.status_id}
          >
            <option value="">Все</option>
            {renderDictionaryOptions(dictionaries.user_statuses)}
          </select>
        </label>
        <label className="admin-field">
          <span>Почта</span>
          <select
            onChange={(event) => setFilters({ ...filters, user_email_confirmed: event.target.value })}
            value={filters.user_email_confirmed}
          >
            <option value="">Все</option>
            {renderDictionaryOptions(dictionaries.user_email_confirmation_statuses)}
          </select>
        </label>
        <IconButton icon="⌕" label="Применить" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={resetFilters} />
      </ValidatedForm>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      <div className="admin-table" role="table" aria-label="Пользователи">
        <div className="admin-table__head admin-table__row_user" role="row">
          <span>Номер</span>
          <span>ФИО</span>
          <span>Электронная почта</span>
          <span>Телефон</span>
          <span>Статус</span>
          <span>Подтверждение</span>
          <span>Создан</span>
          <span />
        </div>
        {users.items.map((user) => (
          <article className="admin-table__row admin-table__row_user" key={user.user_id} role="row">
            <span>{user.user_id}</span>
            <strong>{formatOptionalString(user.user_full_name)}</strong>
            <strong>{user.user_email}</strong>
            <span>{formatOptionalString(user.user_phone)}</span>
            <span className={getUserStatusClassName(user.user_status_id)}>
              {getDictionaryNameById(dictionaries.user_statuses, user.user_status_id)}
            </span>
            <span>{getDictionaryNameByValue(dictionaries.user_email_confirmation_statuses, user.user_email_confirmed)}</span>
            <span>{formatDateTime(user.user_created_at)}</span>
            <IconButton icon="↗" label="Открыть" onClick={() => onNavigate(`/admin/users/${user.user_id}`)} variant="row" />
          </article>
        ))}
      </div>

      {!isLoading && users.items.length === 0 ? <p className="admin-empty">Пользователи не найдены.</p> : null}
      <PaginationMeta isLoading={isLoading} meta={users.meta} onPageChange={(page) => fetchUsers(appliedFilters, page)} />
    </section>
  );
}

interface CompaniesPageProps {
  dictionaries: AdminBootstrapDictionaries;
}

interface CompanyFilters {
  company_id: string;
  company_name: string;
  company_registration_address: string;
  company_actual_address: string;
  company_inn: string;
  company_kpp: string;
  company_ogrn: string;
  company_rs: string;
  company_ks: string;
  company_bik: string;
  company_bank_name: string;
  company_phone: string;
  company_email: string;
  company_edo_id: string;
  company_reject_comment: string;
  company_created_by: string;
  status_id: string;
}

const defaultCompanyFilters: CompanyFilters = {
  company_id: '',
  company_name: '',
  company_registration_address: '',
  company_actual_address: '',
  company_inn: '',
  company_kpp: '',
  company_ogrn: '',
  company_rs: '',
  company_ks: '',
  company_bik: '',
  company_bank_name: '',
  company_phone: '',
  company_email: '',
  company_edo_id: '',
  company_reject_comment: '',
  company_created_by: '',
  status_id: '',
};

export function CompaniesPage({ dictionaries }: CompaniesPageProps) {
  const [companies, setCompanies] = useState<AdminListResult<AdminCompany>>({ items: [], meta: {} });
  const [filters, setFilters] = useState<CompanyFilters>(defaultCompanyFilters);
  const [appliedFilters, setAppliedFilters] = useState<CompanyFilters>(defaultCompanyFilters);
  const [draftFilters, setDraftFilters] = useState<CompanyFilters>(defaultCompanyFilters);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState<CompanyQuickFilter>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);



  const fetchCompanies = useCallback((nextFilters: CompanyFilters, nextQuickFilter: CompanyQuickFilter, page = 1): void => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getCompanies(buildCompanyListParams(nextFilters, nextQuickFilter, page))
      .then((nextCompanies) => {
        if (requestIdRef.current === requestId) {
          setCompanies(nextCompanies);
        }
      })
      .catch((error: Error) => {
        if (requestIdRef.current === requestId) {
          setErrorMessage(error.message);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    fetchCompanies(defaultCompanyFilters, 'all');
  }, [fetchCompanies]);

  const handleQuickSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAppliedFilters(filters);
    fetchCompanies(filters, quickFilter);
  };

  const openFilterModal = (): void => {
    setDraftFilters(filters);
    setIsFilterModalOpen(true);
  };

  const applyAdvancedFilters = (): void => {
    setFilters(draftFilters);
    setAppliedFilters(draftFilters);
    setIsFilterModalOpen(false);
    fetchCompanies(draftFilters, quickFilter);
  };

  const resetFilters = (): void => {
    setFilters(defaultCompanyFilters);
    setAppliedFilters(defaultCompanyFilters);
    setDraftFilters(defaultCompanyFilters);
    setIsFilterModalOpen(false);
    setQuickFilter('all');
    fetchCompanies(defaultCompanyFilters, 'all');
  };

  const workflowCounts = companies.meta.workflow_counts ?? emptyCompanyWorkflowCounts;

  return (
    <section className="admin-page">
      <AdminPageHeader
        description="Проверяйте регистрацию, договоры и готовность компаний к покупке и продаже."
        eyebrow="Контрагенты"
        title="Компании"
        total={companies.meta.total}
      />
      <ValidatedForm className="admin-filter-bar admin-filter-bar_companies" onSubmit={handleQuickSubmit}>
        <label className="admin-field">
          <span>Название</span>
          <input
            onChange={(event) => setFilters({ ...filters, company_name: event.target.value })}
            placeholder="ООО Проверка"
            type="search"
            value={filters.company_name}
          />
        </label>
        <label className="admin-field">
          <span>ИНН</span>
          <input
            inputMode="numeric"
            onChange={(event) => setFilters({ ...filters, company_inn: event.target.value })}
            placeholder="7701234567"
            type="search"
            value={filters.company_inn}
          />
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <FilterButton onClick={openFilterModal} />
        <IconButton icon="↺" label="Сбросить" onClick={resetFilters} />
      </ValidatedForm>

      <div className="admin-company-quick-filters" aria-label="Быстрые фильтры компаний">
        {companyQuickFilters.map((filter) => (
          <button
            aria-pressed={quickFilter === filter.value}
            className={quickFilter === filter.value ? 'is-active' : ''}
            key={filter.value}
            onClick={() => {
              setQuickFilter(filter.value);
              fetchCompanies(appliedFilters, filter.value);
            }}
            type="button"
          >
            <span>{filter.label}</span>
            <strong>{workflowCounts[filter.value]}</strong>
          </button>
        ))}
      </div>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      <div className="admin-table" role="table" aria-label="Компании">
        <div className="admin-table__head admin-table__row_company" role="row">
          <span>Номер</span>
          <span>Компания</span>
          <span>ИНН</span>
          <span>Электронная почта</span>
          <span>Телефон</span>
          <span>Статус</span>
          <span />
        </div>
        {companies.items.map((company) => (
          <article className="admin-table__row admin-table__row_company" key={company.company_id} role="row">
            <span>{company.company_id}</span>
            <strong>{company.company_name}</strong>
            <span>{company.company_inn}</span>
            <span>{company.company_email}</span>
            <span>{company.company_phone}</span>
            <span className={getCompanyStatusClassName(company.company_status_id)}>
              {getDictionaryNameById(dictionaries.company_statuses, company.company_status_id)}
            </span>
            <IconButton href={`/admin/companies/${company.company_id}`} icon="↗" label="Открыть" variant="row" />
          </article>
        ))}
      </div>

      {!isLoading && companies.items.length === 0 ? <p className="admin-empty">Компании не найдены.</p> : null}
      <PaginationMeta isLoading={isLoading} meta={companies.meta} onPageChange={(page) => fetchCompanies(appliedFilters, quickFilter, page)} />

      {isFilterModalOpen ? (
        <CompanyFilterModal
          filters={draftFilters}
          companyStatuses={dictionaries.company_statuses}
          onApply={applyAdvancedFilters}
          onChange={setDraftFilters}
          onClose={() => setIsFilterModalOpen(false)}
          onReset={resetFilters}
        />
      ) : null}
    </section>
  );
}

interface CompanyFilterModalProps {
  companyStatuses: AdminDictionaryItem[];
  filters: CompanyFilters;
  onApply: () => void;
  onChange: (filters: CompanyFilters) => void;
  onClose: () => void;
  onReset: () => void;
}

function CompanyFilterModal({ companyStatuses, filters, onApply, onChange, onClose, onReset }: CompanyFilterModalProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onApply();
  };

  return (
    <section className="admin-modal" aria-label="Фильтры компаний" role="dialog" aria-modal="true">
      <ValidatedForm className="admin-modal__panel" onSubmit={handleSubmit}>
        <div className="admin-modal__header">
          <div>
            <p className="admin-eyebrow">Фильтрация</p>
            <h2>Фильтры компаний</h2>
          </div>
          <IconButton icon="×" label="Закрыть" onClick={onClose} variant="row" />
        </div>

        <div className="admin-modal__sections">
          <fieldset className="admin-filter-group">
            <legend>Основное</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>Номер компании</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_id: event.target.value })}
                  placeholder="10"
                  type="search"
                  value={filters.company_id}
                />
              </label>
              <label className="admin-field">
                <span>Название</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_name: event.target.value })}
                  placeholder="ООО Проверка"
                  type="search"
                  value={filters.company_name}
                />
              </label>
              <label className="admin-field">
                <span>ИНН</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_inn: event.target.value })}
                  placeholder="7701234567"
                  type="search"
                  value={filters.company_inn}
                />
              </label>
              <label className="admin-field">
                <span>Статус</span>
                <SearchableSelect
                  ariaLabel="Статус компании"
                  inputId="company-filter-status"
                  isClearable
                  onChange={(statusId) => onChange({ ...filters, status_id: statusId })}
                  options={dictionarySelectOptions(companyStatuses)}
                  placeholder="Все статусы"
                  value={filters.status_id}
                />
              </label>
              <label className="admin-field">
                <span>Номер пользователя</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_created_by: event.target.value })}
                  placeholder="1"
                  type="search"
                  value={filters.company_created_by}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Регистрационные данные</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>КПП</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_kpp: event.target.value })}
                  placeholder="770101001"
                  type="search"
                  value={filters.company_kpp}
                />
              </label>
              <label className="admin-field">
                <span>ОГРН / ОГРНИП</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_ogrn: event.target.value })}
                  placeholder="1027700132195"
                  type="search"
                  value={filters.company_ogrn}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Адреса</legend>
            <div className="admin-modal__grid">
              <label className="admin-field admin-field_wide">
                <span>Юридический адрес</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_registration_address: event.target.value })}
                  placeholder="Город, улица"
                  type="search"
                  value={filters.company_registration_address}
                />
              </label>
              <label className="admin-field admin-field_wide">
                <span>Фактический адрес</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_actual_address: event.target.value })}
                  placeholder="Город, склад, офис"
                  type="search"
                  value={filters.company_actual_address}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Банк</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>Р/С</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_rs: event.target.value })}
                  placeholder="4070..."
                  type="search"
                  value={filters.company_rs}
                />
              </label>
              <label className="admin-field">
                <span>К/С</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_ks: event.target.value })}
                  placeholder="3010..."
                  type="search"
                  value={filters.company_ks}
                />
              </label>
              <label className="admin-field">
                <span>БИК</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => onChange({ ...filters, company_bik: event.target.value })}
                  placeholder="044525225"
                  type="search"
                  value={filters.company_bik}
                />
              </label>
              <label className="admin-field">
                <span>Банк</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_bank_name: event.target.value })}
                  placeholder="АО Банк"
                  type="search"
                  value={filters.company_bank_name}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Контакты</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>Телефон</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_phone: event.target.value })}
                  placeholder="+7999"
                  type="search"
                  value={filters.company_phone}
                />
              </label>
              <label className="admin-field">
                <span>Электронная почта</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_email: event.target.value })}
                  placeholder="company@example.com"
                  type="search"
                  value={filters.company_email}
                />
              </label>
              <label className="admin-field">
                <span>ЭДО</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_edo_id: event.target.value })}
                  placeholder="2BM..."
                  type="search"
                  value={filters.company_edo_id}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Модерация</legend>
            <div className="admin-modal__grid">
              <label className="admin-field admin-field_wide">
                <span>Причина отказа</span>
                <input
                  onChange={(event) => onChange({ ...filters, company_reject_comment: event.target.value })}
                  placeholder="Текст причины"
                  type="search"
                  value={filters.company_reject_comment}
                />
              </label>
            </div>
          </fieldset>
        </div>

        <div className="admin-modal__actions">
          <IconButton icon="⌕" label="Применить" type="submit" variant="primary" />
          <IconButton icon="↺" label="Сбросить" onClick={onReset} />
        </div>
      </ValidatedForm>
    </section>
  );
}

interface DeliveryZoneFilters {
  search: string;
  zone_id: string;
}

interface DeliveryZoneFormState {
  zone_region_id: string;
  zone_name: string;
  zone_description: string;
  zone_coords: string;
  zone_fill_color: string;
  zone_stroke_color: string;
}

const defaultDeliveryZoneFilters: DeliveryZoneFilters = {
  search: '',
  zone_id: '',
};

const defaultDeliveryZoneForm: DeliveryZoneFormState = {
  zone_region_id: '',
  zone_name: '',
  zone_description: '',
  zone_coords: '',
  zone_fill_color: '#8ca0ff',
  zone_stroke_color: '#3432ff',
};

const deliveryTimeZones = [
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk',
  'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok', 'Asia/Magadan', 'Asia/Kamchatka',
];

const deliveryWeekDays = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 7, label: 'Вс' },
];

export function DeliveryZonesPage({ formMode, onNavigate, zoneId }: { formMode?: CompanyDeliveryFormMode; onNavigate?: (path: string) => void; zoneId?: string }) {
  const [deliveryZones, setDeliveryZones] = useState<AdminListResult<AdminDeliveryZone>>({ items: [], meta: {} });
  const [mapZones, setMapZones] = useState<AdminDeliveryZone[]>([]);
  const [filters, setFilters] = useState<DeliveryZoneFilters>(defaultDeliveryZoneFilters);
  const [zoneForm, setZoneForm] = useState<DeliveryZoneFormState | null>(null);
  const [editingZone, setEditingZone] = useState<AdminDeliveryZone | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchDeliveryZones = useCallback((nextFilters: DeliveryZoneFilters): void => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setErrorMessage(null);
    const params = { ...buildDeliveryZoneListParams(nextFilters), include_coords: formMode === 'edit' };

    adminApi
      .getDeliveryZoneCatalog(params, (firstPage) => {
        if (requestId !== requestIdRef.current) return;
        setDeliveryZones(firstPage);
        setIsLoading(false);
      })
      .then(({ firstPage, allZones }) => {
        if (requestId !== requestIdRef.current) return;
        setDeliveryZones(firstPage);
        setMapZones(allZones);
      })
      .catch((error: Error) => {
        if (requestId !== requestIdRef.current) return;
        setErrorMessage(error.message);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, [formMode]);

  useEffect(() => {
    if (formMode === 'create') {
      setEditingZone(null);
      setZoneForm(defaultDeliveryZoneForm);
      setIsLoading(false);

      return;
    }

    const initialFilters = formMode === 'edit' && zoneId !== undefined
      ? { ...defaultDeliveryZoneFilters, zone_id: zoneId }
      : defaultDeliveryZoneFilters;

    fetchDeliveryZones(initialFilters);
  }, [formMode, zoneId, fetchDeliveryZones]);

  useEffect(() => {
    if (formMode === undefined) {
      return;
    }

    if (formMode === 'create') {
      if (zoneForm === null || editingZone !== null) {
        setEditingZone(null);
        setZoneForm(defaultDeliveryZoneForm);
      }

      return;
    }

    const selectedZoneId = Number(zoneId);

    if (!Number.isInteger(selectedZoneId) || selectedZoneId < 1 || editingZone?.zone_id === selectedZoneId) {
      return;
    }

    const selectedZone = deliveryZones.items.find((deliveryZone) => deliveryZone.zone_id === selectedZoneId);

    if (selectedZone !== undefined) {
      setEditingZone(selectedZone);
      setZoneForm(deliveryZoneToForm(selectedZone));
    }
  }, [deliveryZones.items, editingZone, formMode, zoneForm, zoneId]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    fetchDeliveryZones(filters);
  };

  const resetFilters = (): void => {
    setFilters(defaultDeliveryZoneFilters);
    fetchDeliveryZones(defaultDeliveryZoneFilters);
  };

  const openCreateForm = (): void => {
    if (onNavigate !== undefined) {
      onNavigate('/admin/delivery-zones/new');

      return;
    }

    setEditingZone(null);
    setZoneForm(defaultDeliveryZoneForm);
    setErrorMessage(null);
  };

  const openEditForm = (deliveryZone: AdminDeliveryZone): void => {
    if (onNavigate !== undefined) {
      onNavigate(`/admin/delivery-zones/${deliveryZone.zone_id}/edit`);

      return;
    }

    setEditingZone(deliveryZone);
    setZoneForm(deliveryZoneToForm(deliveryZone));
    setErrorMessage(null);
  };

  const closeForm = (): void => {
    setEditingZone(null);
    setZoneForm(null);

    if (onNavigate !== undefined) {
      onNavigate('/admin/delivery-zones');
    }
  };

  const saveDeliveryZone = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (zoneForm === null) {
      return;
    }

    setErrorMessage(null);

    let payload: AdminDeliveryZoneCreateRequest | AdminDeliveryZoneUpdateRequest;

    try {
      payload = createDeliveryZonePayload(zoneForm);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Некорректно заданы границы зоны.';
      setErrorMessage(message);

      return;
    }

    setIsSaving(true);
    const savePromise = editingZone === null
      ? adminApi.createDeliveryZone(payload)
      : adminApi.updateDeliveryZone(editingZone.zone_id, payload);

    savePromise
      .then(() => {
        closeForm();
        fetchDeliveryZones(filters);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const deleteDeliveryZone = (deliveryZone: AdminDeliveryZone): void => {
    const shouldDelete = window.confirm(`Удалить зону "${deliveryZone.zone_name}"?`);

    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .deleteDeliveryZone(deliveryZone.zone_id)
      .then(() => {
        fetchDeliveryZones(filters);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function fetchDeliveryZonePage(page: number): void {
    const requestId = ++requestIdRef.current;
    const params = { ...buildDeliveryZoneListParams(filters), page, include_coords: false };
    setIsLoading(true);
    setErrorMessage(null);

    adminApi.getDeliveryZones(params)
      .then((nextPage) => {
        if (requestId !== requestIdRef.current) return;
        setDeliveryZones(nextPage);
      })
      .catch((error: Error) => {
        if (requestId !== requestIdRef.current) return;
        setErrorMessage(error.message);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }

  const zonesWithCoords = mapZones.filter(hasDeliveryZoneCoords);

  if (formMode !== undefined) {
    return (
      <section className="admin-page admin-zone-form-route">
        <ListState errorMessage={errorMessage} isLoading={isLoading} />
        {zoneForm !== null ? (
          <DeliveryZoneEditModal form={zoneForm} isPage isSaving={isSaving} mode={editingZone === null ? 'create' : 'update'} onChange={setZoneForm} onClose={closeForm} onSubmit={saveDeliveryZone} />
        ) : null}
        {!isLoading && errorMessage === null && zoneForm === null ? <p className="admin-empty">Зона доставки не найдена.</p> : null}
      </section>
    );
  }

  return (
    <section className="admin-page">
      <AdminPageHeader
        actions={<><Link className="workspace-secondary-button" to={ADMIN_DELIVERY_ZONE_LIVE_MAP_PATH}>Текущие границы</Link><Link className="workspace-secondary-button" to={ADMIN_DELIVERY_MAP_GENERATIONS_PATH}>Генерации карты</Link><IconButton icon="+" label="Добавить зону" onClick={openCreateForm} variant="primary" /></>}
        description="Настраивайте географию доставки и проверяйте зоны на карте до назначения компаниям."
        eyebrow="Логистика"
        title="Зоны доставки"
        total={deliveryZones.meta.total}
      />
      <div className="admin-zone-layout">
        <div className="admin-zone-map-panel">
          <DeliveryZoneOverviewMap zones={mapZones} />
        </div>
        <aside className="admin-zone-summary" aria-label="Сводка зон доставки">
          <p className="admin-eyebrow">Карта доставки</p>
          <h2>Зоны доставки</h2>
          <dl>
            <div>
              <dt>Всего зон</dt>
              <dd>{deliveryZones.meta.total ?? deliveryZones.items.length}</dd>
            </div>
            <div>
              <dt>С координатами</dt>
              <dd>{zonesWithCoords.length}</dd>
            </div>
            <div>
              <dt>Без границ</dt>
              <dd>{mapZones.length - zonesWithCoords.length}</dd>
            </div>
          </dl>
          <p>
            Если границы не заданы, зона сохранится в списке, но не появится на карте.
          </p>
        </aside>
      </div>

      <ValidatedForm className="admin-filter-bar admin-filter-bar_zones" onSubmit={handleSubmit}>
        <label className="admin-field">
          <span>Поиск</span>
          <input
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Название зоны"
            type="search"
            value={filters.search}
          />
        </label>
        <label className="admin-field">
          <span>Номер</span>
          <input
            inputMode="numeric"
            onChange={(event) => setFilters({ ...filters, zone_id: event.target.value })}
            placeholder="12"
            type="search"
            value={filters.zone_id}
          />
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={resetFilters} />
      </ValidatedForm>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      <div className="admin-table" role="table" aria-label="Зоны доставки">
        <div className="admin-table__head admin-table__row_zone" role="row">
          <span>Номер</span>
          <span>Название</span>
          <span>Регион</span>
          <span>Границы</span>
          <span>Цвет</span>
          <span>Обновлена</span>
          <span />
        </div>
        {deliveryZones.items.map((deliveryZone) => (
          <article className="admin-table__row admin-table__row_zone" key={deliveryZone.zone_id} role="row">
            <span>{deliveryZone.zone_id}</span>
            <strong>{deliveryZone.zone_name}</strong>
            <span>{formatOptionalString(deliveryZone.zone_region_name)}</span>
            <span>{hasDeliveryZoneCoords(deliveryZone) ? 'Заполнен' : 'Пусто'}</span>
            <span className="admin-zone-color">
              <i style={{ background: deliveryZone.zone_fill_color, borderColor: deliveryZone.zone_stroke_color }} />
              {deliveryZone.zone_fill_color}
            </span>
            <span>{formatDateTime(deliveryZone.zone_updated_at)}</span>
            <span className="admin-row-actions">
              <IconButton icon="✎" label="Редактировать" onClick={() => openEditForm(deliveryZone)} variant="row" />
              <IconButton icon="×" label="Удалить" onClick={() => deleteDeliveryZone(deliveryZone)} variant="row" />
            </span>
          </article>
        ))}
      </div>

      {!isLoading && deliveryZones.items.length === 0 ? <p className="admin-empty">Зоны доставки не найдены.</p> : null}
      <PaginationMeta meta={deliveryZones.meta} />
      {(deliveryZones.meta.page_count ?? 1) > 1 ? (
        <div className="admin-zone-pagination">
          <IconButton icon="←" label="Предыдущая страница зон" disabled={isLoading || (deliveryZones.meta.page ?? 1) <= 1} onClick={() => fetchDeliveryZonePage((deliveryZones.meta.page ?? 1) - 1)} />
          <IconButton icon="→" label="Следующая страница зон" disabled={isLoading || (deliveryZones.meta.page ?? 1) >= (deliveryZones.meta.page_count ?? 1)} onClick={() => fetchDeliveryZonePage((deliveryZones.meta.page ?? 1) + 1)} />
        </div>
      ) : null}

      {zoneForm !== null ? (
        <DeliveryZoneEditModal
          form={zoneForm}
          isSaving={isSaving}
          mode={editingZone === null ? 'create' : 'update'}
          onChange={setZoneForm}
          onClose={closeForm}
          onSubmit={saveDeliveryZone}
        />
      ) : null}
    </section>
  );
}

function DeliveryZoneEditModal({
  form,
  isPage = false,
  isSaving,
  mode,
  onChange,
  onClose,
  onSubmit,
}: {
  form: DeliveryZoneFormState;
  isPage?: boolean;
  isSaving: boolean;
  mode: 'create' | 'update';
  onChange: (form: DeliveryZoneFormState) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className={isPage ? 'admin-zone-editor-page' : 'admin-modal'} aria-label="Редактирование зоны доставки" aria-modal={isPage ? undefined : true} role={isPage ? undefined : 'dialog'}>
      <ValidatedForm className={isPage ? 'admin-zone-editor-page__form' : 'admin-modal__panel'} onSubmit={onSubmit}>
        <div className="admin-modal__header">
          <div>
            <p className="admin-eyebrow">Зона доставки</p>
            <h2>{mode === 'create' ? 'Новая зона' : 'Редактирование'}</h2>
          </div>
          <IconButton icon={isPage ? '←' : '×'} label={isPage ? 'Назад к зонам' : 'Закрыть'} onClick={onClose} variant="row" />
        </div>

        <div className={isPage ? 'admin-zone-editor-page__layout' : 'admin-modal__grid'}>
          <fieldset className={isPage ? 'admin-form-section admin-zone-editor-page__details' : 'admin-zone-editor-page__fieldset'}>
            {isPage ? <legend>Основные параметры</legend> : null}
            <label className="admin-field admin-field_wide">
            <span>Название</span>
            <input
              onChange={(event) => onChange({ ...form, zone_name: event.target.value })}
              required
              type="text"
              value={form.zone_name}
            />
            </label>
            <label className="admin-field">
            <span>Номер региона</span>
            <input
              inputMode="numeric"
              onChange={(event) => onChange({ ...form, zone_region_id: event.target.value })}
              placeholder="Опционально"
              type="text"
              value={form.zone_region_id}
            />
            </label>
            <div className="admin-zone-editor-page__colors">
              <label className="admin-field">
                <span>Заливка</span>
                <input onChange={(event) => onChange({ ...form, zone_fill_color: event.target.value })} type="color" value={form.zone_fill_color} />
              </label>
              <label className="admin-field">
                <span>Обводка</span>
                <input onChange={(event) => onChange({ ...form, zone_stroke_color: event.target.value })} type="color" value={form.zone_stroke_color} />
              </label>
            </div>
            <label className="admin-field admin-field_wide">
            <span>Описание</span>
            <textarea
              onChange={(event) => onChange({ ...form, zone_description: event.target.value })}
              value={form.zone_description}
            />
            </label>
            <label className="admin-field admin-field_wide">
            <span>Границы зоны</span>
            <textarea
              aria-label="Границы зоны"
              className="admin-code-textarea"
              onChange={(event) => onChange({ ...form, zone_coords: event.target.value })}
              placeholder='{"type":"FeatureCollection","features":[]}'
              spellCheck={false}
              value={form.zone_coords}
            />
              <small>Вставьте GeoJSON Feature или FeatureCollection.</small>
            </label>
          </fieldset>
          {isPage ? (
            <aside className="admin-zone-editor-page__preview">
              <DeliveryZoneMap zones={[deliveryZoneFormPreview(form)]} />
              <p>Предпросмотр обновляется по введённым границам. Проверьте геометрию перед сохранением.</p>
            </aside>
          ) : null}
        </div>

        <p className="admin-caption">Оставьте поле пустым, если зона пока не должна отображаться на карте.</p>

        <div className="admin-modal__actions">
          <IconButton icon="✓" label={isSaving ? 'Сохраняем' : 'Сохранить'} type="submit" variant="primary" />
        </div>
      </ValidatedForm>
    </section>
  );
}

function DeliveryZoneOverviewMap({ zones }: { zones: AdminDeliveryZone[] }) {
  const [queryClient] = useState(() => new QueryClient());
  const appearance = useMemo(() => new Map(zones.map((zone) => [zone.zone_id, { fill: zone.zone_fill_color, stroke: zone.zone_stroke_color, opacity: 0.28, width: 2, connected: false }])), [zones]);

  return <QueryClientProvider client={queryClient}><DeliveryVectorMap zones={zones} appearance={appearance} className="admin-zone-map" label="Карта зон доставки" /></QueryClientProvider>;
}

function DeliveryZoneMap({ zones }: { zones: AdminDeliveryZone[] }) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const handleMapReady = useCallback((map: L.Map) => {
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerRef.current;

    if (map === null || layerGroup === null) {
      return;
    }

    layerGroup.clearLayers();

    const boundsList: L.LatLngBounds[] = [];

    zones.forEach((zone) => {
      const geoJson = normalizeGeoJson(zone.zone_coords);

      if (geoJson === null) {
        return;
      }

      const geoJsonLayer = L.geoJSON(geoJson, {
        style: {
          color: zone.zone_stroke_color,
          fillColor: zone.zone_fill_color,
          fillOpacity: 0.28,
          opacity: 0.9,
          weight: 2,
        },
      });
      const zoneLabel = zone.zone_region_name ? `${zone.zone_name} · ${zone.zone_region_name}` : zone.zone_name;
      geoJsonLayer.bindTooltip(zoneLabel);
      geoJsonLayer.addTo(layerGroup);

      const bounds = geoJsonLayer.getBounds();

      if (bounds.isValid()) {
        boundsList.push(bounds);
      }
    });

    if (boundsList.length === 0) {
      map.setView([55.751244, 37.618423], 8);

      return;
    }

    const mapBounds = boundsList.reduce((currentBounds, nextBounds) => currentBounds.extend(nextBounds), boundsList[0]);
    map.fitBounds(mapBounds.pad(0.08));
  }, [zones]);

  return <MapCanvas className="admin-zone-map" onReady={handleMapReady} aria-label="Карта зон доставки" />;
}

interface CatalogPageProps {
  activeTab: CatalogTab;
  dictionaries: AdminBootstrapDictionaries;
  onNavigate: (path: string) => void;
}

interface CatalogCategoryFilters {
  search: string;
  category_id: string;
}

interface CatalogProductFilters {
  search: string;
  product_id: string;
  product_category_id: string;
  product_status_id: string;
}

interface CatalogShowcaseFilters {
  company_id: string;
  delivery_address_id: string;
  category_id: string;
  search: string;
  max_price: string;
  max_delivery_days: string;
  sort: AdminCatalogViewSort;
  page: number;
}

type AdminCatalogViewMode = 'cards' | 'table' | 'expanded';

const catalogTabs: Array<{ label: string; tab: CatalogTab }> = [
  { label: 'Витрина', tab: 'showcase' },
  { label: 'Категории', tab: 'categories' },
  { label: 'Товары', tab: 'products' },
];

const defaultCatalogCategoryFilters: CatalogCategoryFilters = {
  search: '',
  category_id: '',
};

const defaultCatalogProductFilters: CatalogProductFilters = {
  search: '',
  product_id: '',
  product_category_id: '',
  product_status_id: '',
};

const defaultCatalogShowcaseFilters: CatalogShowcaseFilters = {
  company_id: '',
  delivery_address_id: '',
  category_id: '',
  search: '',
  max_price: '',
  max_delivery_days: '',
  sort: 'name',
  page: 1,
};

const catalogShowcaseSortOptions: SearchableSelectOption[] = [
  { label: 'По названию', value: 'name' },
  { label: 'Сначала дешевле', value: 'price_asc' },
  { label: 'Сначала дороже', value: 'price_desc' },
  { label: 'Сначала быстрее', value: 'delivery_asc' },
  { label: 'Сначала дольше', value: 'delivery_desc' },
];

const emptyAdminCatalogShowcase: AdminListResult<AdminCatalogViewProduct> & AdminCatalogViewResult = {
  items: [],
  categories: [],
  matched_zone_ids: [],
  meta: {},
};

const defaultCompanyDeliveryAddressForm: CompanyDeliveryAddressFormState = {
  address_name: '',
  address_address: '',
  address_lat: '',
  address_lng: '',
  address_selection_key: '',
  address_egais_number: '',
  address_mercury_number: '',
};

const defaultCompanyDeliveryZoneForm: CompanyDeliveryZoneFormState = {
  zone_ids: [],
  group_name: '',
  is_enabled: true,
  schedule_timezone: 'Europe/Moscow',
  schedule_delivery_days: '1',
  schedule_days_of_week: [],
  schedule_order_cutoff_time: '',
  schedule_term_without_days: false,
};

export function CatalogPage({ activeTab, dictionaries, onNavigate }: CatalogPageProps) {
  const [categories, setCategories] = useState<AdminListResult<AdminCatalogCategory>>({ items: [], meta: {} });
  const [products, setProducts] = useState<AdminListResult<AdminCatalogProduct>>({ items: [], meta: {} });
  const [showcase, setShowcase] = useState<AdminListResult<AdminCatalogViewProduct> & AdminCatalogViewResult>(emptyAdminCatalogShowcase);
  const [showcaseCompanyOption, setShowcaseCompanyOption] = useState<SearchableSelectOption | null>(null);
  const [showcaseAddresses, setShowcaseAddresses] = useState<AdminCompanyDeliveryAddress[]>([]);
  const [categoryFilters, setCategoryFilters] = useState<CatalogCategoryFilters>(defaultCatalogCategoryFilters);
  const [productFilters, setProductFilters] = useState<CatalogProductFilters>(defaultCatalogProductFilters);
  const [showcaseFilters, setShowcaseFilters] = useState<CatalogShowcaseFilters>(defaultCatalogShowcaseFilters);
  const [showcaseViewMode, setShowcaseViewMode] = useState<AdminCatalogViewMode>('cards');
  const showcaseAddressesRequestIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories(defaultCatalogCategoryFilters);
    fetchProducts(defaultCatalogProductFilters);
  }, []);

  const openProductOffers = (showcaseProduct: AdminCatalogViewProduct): void => {
    onNavigate(`/admin/catalog/products/${showcaseProduct.product_id}#offers`);
  };

  const submitCategoryFilters = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    fetchCategories(categoryFilters);
  };

  const submitProductFilters = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    fetchProducts(productFilters);
  };

  const submitShowcaseFilters = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextFilters = { ...showcaseFilters, page: 1 };

    setShowcaseFilters(nextFilters);
    fetchShowcase(nextFilters);
  };

  const changeShowcaseViewMode = (viewMode: AdminCatalogViewMode): void => {
    setShowcaseViewMode(viewMode);

    if (showcaseFilters.delivery_address_id !== '') {
      fetchShowcase(showcaseFilters, viewMode);
    }
  };

  const selectShowcaseCategory = (categoryId: number | null): void => {
    const nextFilters = {
      ...showcaseFilters,
      category_id: categoryId === null ? '' : String(categoryId),
      page: 1,
    };

    setShowcaseFilters(nextFilters);
    fetchShowcase(nextFilters);
  };

  const changeShowcaseCompany = (companyOption: SearchableSelectOption | null): void => {
    const companyId = companyOption?.value ?? '';
    const requestId = showcaseAddressesRequestIdRef.current + 1;
    const nextFilters = {
      ...defaultCatalogShowcaseFilters,
      company_id: companyId,
    };

    showcaseAddressesRequestIdRef.current = requestId;
    setShowcaseCompanyOption(companyOption);
    setShowcaseFilters(nextFilters);
    setShowcase(emptyAdminCatalogShowcase);
    setShowcaseAddresses([]);

    const numericCompanyId = normalizeOptionalNumber(companyId);

    if (numericCompanyId === null) {
      return;
    }

    adminApi
      .getCompanyDeliveryAddresses(numericCompanyId)
      .then((addresses) => {
        if (showcaseAddressesRequestIdRef.current !== requestId) {
          return;
        }

        setShowcaseAddresses(addresses);
      })
      .catch((error: Error) => {
        if (showcaseAddressesRequestIdRef.current !== requestId) {
          return;
        }

        setErrorMessage(error.message);
      });
  };

  const changeShowcaseAddress = (deliveryAddressId: string): void => {
    const nextFilters = {
      ...showcaseFilters,
      delivery_address_id: deliveryAddressId,
      page: 1,
    };

    setShowcaseFilters(nextFilters);

    if (deliveryAddressId === '') {
      setShowcase(emptyAdminCatalogShowcase);

      return;
    }

    fetchShowcase(nextFilters);
  };

  const resetShowcaseProductFilters = (): void => {
    const nextFilters = {
      ...defaultCatalogShowcaseFilters,
      company_id: showcaseFilters.company_id,
      delivery_address_id: showcaseFilters.delivery_address_id,
    };

    setShowcaseFilters(nextFilters);
    fetchShowcase(nextFilters);
  };

  const resetCategoryFilters = (): void => {
    setCategoryFilters(defaultCatalogCategoryFilters);
    fetchCategories(defaultCatalogCategoryFilters);
  };

  const resetProductFilters = (): void => {
    setProductFilters(defaultCatalogProductFilters);
    fetchProducts(defaultCatalogProductFilters);
  };

  const deleteCategory = (category: AdminCatalogCategory): void => {
    const shouldDelete = window.confirm(`Удалить категорию "${category.category_name}"?`);

    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .deleteCatalogCategory(category.category_id)
      .then(() => {
        fetchCategories(categoryFilters);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteProduct = (product: AdminCatalogProduct): void => {
    const shouldDelete = window.confirm(`Удалить товар "${product.product_name}"?`);

    if (!shouldDelete) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .deleteCatalogProduct(product.product_id)
      .then(() => {
        fetchProducts(productFilters);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function fetchCategories(nextFilters: CatalogCategoryFilters, page = 1): void {
    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getCatalogCategories({ ...buildCatalogCategoryListParams(nextFilters), page })
      .then(setCategories)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function fetchProducts(nextFilters: CatalogProductFilters, page = 1): void {
    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getCatalogProducts({ ...buildCatalogProductListParams(nextFilters), page })
      .then(setProducts)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  async function loadShowcaseCompanyOptions(query: string): Promise<SearchableSelectOption[]> {
    const companyList = await adminApi.getCompanies({ company_name: query, page: 1, per_page: 20 });
    const options = companyList.items.map((company) => ({ label: company.company_name, value: String(company.company_id) }));

    return options;
  }

  function fetchShowcase(
    nextFilters: CatalogShowcaseFilters,
    requestedViewMode: AdminCatalogViewMode = showcaseViewMode,
  ): void {
    const deliveryAddressId = normalizeOptionalNumber(nextFilters.delivery_address_id);

    if (deliveryAddressId === null) {
      setShowcase(emptyAdminCatalogShowcase);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getCatalogView({
        delivery_address_id: deliveryAddressId,
        category_id: normalizeOptionalNumber(nextFilters.category_id),
        search: nextFilters.search.trim(),
        max_price: normalizeOptionalNumber(nextFilters.max_price),
        max_delivery_days: normalizeOptionalNumber(nextFilters.max_delivery_days),
        sort: nextFilters.sort,
        include_offers: requestedViewMode === 'expanded',
        page: nextFilters.page,
        per_page: 24,
      })
      .then(setShowcase)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <section className="admin-page">
      <article className="admin-detail-card admin-company-header admin-catalog-header">
        <AdminPageHeader
          actions={<button className="catalog-button" type="button" onClick={() => onNavigate('/admin/catalog/references/brand')}>Бренды и производители</button>}
          description="Управляйте категориями, товарами, брендами и предложениями поставщиков."
          eyebrow="Номенклатура"
          title="Каталог товаров"
        />
        <TabNavigation
          ariaLabel="Разделы каталога"
          items={catalogTabs.map((item) => ({ title: item.label, value: item.tab }))}
          onChange={(tab) => onNavigate(`/admin/catalog/${tab}`)}
          value={activeTab}
        />
      </article>

      <ListState errorMessage={errorMessage} isLoading={activeTab === 'showcase' ? false : isLoading} />

      {activeTab === 'showcase' ? (
        <CatalogShowcaseTab
          addresses={showcaseAddresses}
          companyOption={showcaseCompanyOption}
          loadCompanyOptions={loadShowcaseCompanyOptions}
          filters={showcaseFilters}
          isLoading={isLoading}
          onChangeAddress={changeShowcaseAddress}
          onChangeCompany={changeShowcaseCompany}
          onChangeFilters={setShowcaseFilters}
          onChangePage={(page) => {
            const nextFilters = { ...showcaseFilters, page };

            setShowcaseFilters(nextFilters);
            fetchShowcase(nextFilters);
          }}
          onSelectCategory={selectShowcaseCategory}
          onResetProductFilters={resetShowcaseProductFilters}
          onOpenProduct={openProductOffers}
          onSubmit={submitShowcaseFilters}
          onViewModeChange={changeShowcaseViewMode}
          showcase={showcase}
          viewMode={showcaseViewMode}
        />
      ) : null}

      {activeTab === 'categories' ? (
        <CatalogCategoriesTab
          onPageChange={(page) => fetchCategories(categoryFilters, page)}
          categories={categories}
          filters={categoryFilters}
          onAdd={() => onNavigate(ADMIN_CATALOG_CATEGORY_CREATE_PATH)}
          onChangeFilters={setCategoryFilters}
          onDelete={deleteCategory}
          onEdit={(category) => onNavigate(getAdminCatalogCategoryEditPath(category.category_id))}
          onReset={resetCategoryFilters}
          onSubmit={submitCategoryFilters}
        />
      ) : null}

      {activeTab === 'products' ? (
        <CatalogProductsTab
          onPageChange={(page) => fetchProducts(productFilters, page)}
          categories={categories.items}
          dictionaries={dictionaries}
          filters={productFilters}
          onAdd={() => onNavigate(ADMIN_CATALOG_PRODUCT_CREATE_PATH)}
          onChangeFilters={setProductFilters}
          onDelete={deleteProduct}
          onEdit={(product) => onNavigate(getAdminCatalogProductEditPath(product.product_id))}
          onOpen={(product) => onNavigate(`/admin/catalog/products/${product.product_id}`)}
          onReset={resetProductFilters}
          onSubmit={submitProductFilters}
          products={products}
        />
      ) : null}




    </section>
  );
}

function CatalogShowcaseTab({
  addresses,
  companyOption,
  loadCompanyOptions,
  filters,
  isLoading,
  onChangeAddress,
  onChangeCompany,
  onChangeFilters,
  onChangePage,
  onOpenProduct,
  onSelectCategory,
  onResetProductFilters,
  onSubmit,
  onViewModeChange,
  showcase,
  viewMode,
}: {
  addresses: AdminCompanyDeliveryAddress[];
  companyOption: SearchableSelectOption | null;
  loadCompanyOptions: (query: string) => Promise<SearchableSelectOption[]>;
  filters: CatalogShowcaseFilters;
  isLoading: boolean;
  onChangeAddress: (deliveryAddressId: string) => void;
  onChangeCompany: (companyOption: SearchableSelectOption | null) => void;
  onChangeFilters: (filters: CatalogShowcaseFilters) => void;
  onChangePage: (page: number) => void;
  onOpenProduct: (product: AdminCatalogViewProduct) => void;
  onSelectCategory: (categoryId: number | null) => void;
  onResetProductFilters: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onViewModeChange: (viewMode: AdminCatalogViewMode) => void;
  showcase: AdminListResult<AdminCatalogViewProduct> & AdminCatalogViewResult;
  viewMode: AdminCatalogViewMode;
}) {
  const addressOptions = useMemo<SearchableSelectOption[]>(() => {
    return addresses.map((address) => ({
      label: `${address.address_name} — ${address.address_address}`,
      value: String(address.address_id),
    }));
  }, [addresses]);
  const hasCompany = filters.company_id !== '';
  const hasDeliveryAddress = filters.delivery_address_id !== '';
  const hasActiveProductFilters = filters.search.trim() !== ''
    || filters.category_id !== ''
    || filters.max_price !== ''
    || filters.max_delivery_days !== '';

  return (
    <section className="catalog-showcase">
      {!hasDeliveryAddress ? (
        <CatalogSetupState
          action={(
            <div className="catalog-setup-selector" aria-label="Выбор компании и точки доставки">
              <CatalogCompanyField
                inputId="catalog-setup-company"
                loadOptions={loadCompanyOptions}
                onChange={onChangeCompany}
                value={companyOption}
              />
              <CatalogAddressField
                addresses={addressOptions}
                hasCompany={hasCompany}
                inputId="catalog-setup-delivery-address"
                onChange={onChangeAddress}
                value={filters.delivery_address_id}
              />
            </div>
          )}
          hasCompany={hasCompany}
          hasDeliveryAddresses={addresses.length > 0}
        />
      ) : (
        <>
          <ValidatedForm aria-label="Параметры каталога" className="catalog-sticky-toolbar" onSubmit={onSubmit}>
            <CatalogCompanyField
              inputId="catalog-toolbar-company"
              loadOptions={loadCompanyOptions}
              onChange={onChangeCompany}
              value={companyOption}
            />
            <CatalogAddressField
              addresses={addressOptions}
              hasCompany={hasCompany}
              inputId="catalog-toolbar-delivery-address"
              onChange={onChangeAddress}
              value={filters.delivery_address_id}
            />
            <label className="admin-field catalog-toolbar__search">
              <span>Поиск</span>
              <input
                onChange={(event) => onChangeFilters({ ...filters, search: event.target.value })}
                placeholder="Название или артикул"
                type="search"
                value={filters.search}
              />
            </label>
            <label className="admin-field">
              <span>Сортировка</span>
              <SearchableSelect
                ariaLabel="Сортировка каталога"
                inputId="catalog-sort"
                name="catalog_sort"
                onChange={(sort) => onChangeFilters({ ...filters, sort: sort as AdminCatalogViewSort, page: 1 })}
                options={catalogShowcaseSortOptions}
                placeholder="Сортировка"
                value={filters.sort}
              />
            </label>
            <label className="admin-field admin-field_compact">
              <span>Цена до, ₽</span>
              <input
                min="0"
                onChange={(event) => onChangeFilters({ ...filters, max_price: event.target.value, page: 1 })}
                placeholder="Любая"
                step="0.01"
                type="number"
                value={filters.max_price}
              />
            </label>
            <label className="admin-field admin-field_compact">
              <span>Доставка, дней</span>
              <input
                min="0"
                onChange={(event) => onChangeFilters({ ...filters, max_delivery_days: event.target.value, page: 1 })}
                placeholder="Любая"
                step="1"
                type="number"
                value={filters.max_delivery_days}
              />
            </label>
            <button className="admin-submit catalog-toolbar__submit" type="submit">
              Применить
            </button>
            <div className="catalog-view-toggle catalog-view-toggle_admin" aria-label="Вид витрины">
              <button className={viewMode === 'cards' ? 'is-active' : ''} onClick={() => onViewModeChange('cards')} type="button">
                Карточки
              </button>
              <button className={viewMode === 'table' ? 'is-active' : ''} onClick={() => onViewModeChange('table')} type="button">
                Таблица
              </button>
              <button className={viewMode === 'expanded' ? 'is-active' : ''} onClick={() => onViewModeChange('expanded')} type="button">
                Расширенный
              </button>
            </div>
          </ValidatedForm>

          <div className="catalog-layout catalog-layout_admin">
            <CatalogCategoryMenu
              categories={showcase.categories}
              onSelect={onSelectCategory}
              selectedCategoryId={normalizeOptionalNumber(filters.category_id)}
              totalCount={Number(showcase.meta.total ?? showcase.items.length)}
            />

            <section aria-busy={isLoading} className="catalog-content">
              <div className="catalog-summary">
                <span>{showcase.delivery_address_name ?? 'Точка доставки не выбрана'}</span>
                <strong>
                  {formatRussianCount(Number(showcase.meta.total ?? 0), {
                    one: 'товар',
                    few: 'товара',
                    many: 'товаров',
                  })}
                </strong>
                <small>
                  {showcase.matched_zone_ids.length > 0
                    ? formatRussianCount(showcase.matched_zone_ids.length, {
                      one: 'зона',
                      few: 'зоны',
                      many: 'зон',
                    })
                    : 'Зона не найдена'}
                </small>
              </div>
              {isLoading ? (
                <LoadingState ariaLabel="Загрузка товаров" />
              ) : (
                <>
                  {showcase.items.length > 0 ? (
                    <>
                      {viewMode === 'cards' ? (
                        <AdminCatalogProductCards onOpenProduct={onOpenProduct} products={showcase.items} />
                      ) : viewMode === 'table' ? (
                        <AdminCatalogProductTable onOpenProduct={onOpenProduct} products={showcase.items} />
                      ) : (
                        <AdminCatalogProductExpandedList
                          onOpenProduct={onOpenProduct}
                          products={showcase.items}
                          sort={filters.sort}
                        />
                      )}
                      <CatalogShowcasePagination
                        page={Number(showcase.meta.page ?? filters.page)}
                        pageCount={Number(showcase.meta.page_count ?? 1)}
                        onChange={onChangePage}
                      />
                    </>
                  ) : (
                    <CatalogEmptyState
                      hasActiveFilters={hasActiveProductFilters}
                      hasMatchedZone={showcase.matched_zone_ids.length > 0}
                      onChangeAddress={() => document.getElementById('catalog-toolbar-delivery-address')?.focus()}
                      onResetFilters={onResetProductFilters}
                    />
                  )}
                </>
              )}
            </section>
          </div>
        </>
      )}
    </section>
  );
}

function CatalogCompanyField({ inputId, loadOptions, onChange, value }: {
  inputId: string;
  loadOptions: (query: string) => Promise<SearchableSelectOption[]>;
  onChange: (companyOption: SearchableSelectOption | null) => void;
  value: SearchableSelectOption | null;
}) {
  return (
    <label className="admin-field" htmlFor={inputId}>
      <span>Компания</span>
      <AsyncSearchableSelect
        ariaLabel="Компания"
        emptyMessage="Компания не найдена"
        inputId={inputId}
        loadOptions={loadOptions}
        loadingMessage="Ищем компании…"
        onChange={onChange}
        placeholder="Введите название компании"
        value={value}
      />
    </label>
  );
}

function CatalogAddressField({
  addresses,
  hasCompany,
  inputId,
  onChange,
  value,
}: {
  addresses: SearchableSelectOption[];
  hasCompany: boolean;
  inputId: string;
  onChange: (deliveryAddressId: string) => void;
  value: string;
}) {
  const placeholder = hasCompany ? 'Выберите точку доставки' : 'Сначала выберите компанию';

  return (
    <label className="admin-field" htmlFor={inputId}>
      <span>Точка доставки</span>
      <SearchableSelect
        ariaLabel="Точка доставки"
        inputId={inputId}
        isDisabled={!hasCompany || addresses.length === 0}
        name="delivery_address_id"
        noOptionsMessage="Адрес не найден"
        onChange={onChange}
        options={addresses}
        placeholder={placeholder}
        value={value}
      />
    </label>
  );
}

function AdminCatalogProductCards({
  onOpenProduct,
  products,
}: {
  onOpenProduct: (product: AdminCatalogViewProduct) => void;
  products: AdminCatalogViewProduct[];
}) {
  return (
    <div className="catalog-card-grid">
      {products.map((product) => (
        <article className="catalog-product-card" key={product.product_id}>
          <AdminProductImage product={product} />
          <div className="catalog-product-card__body">
            <span>{product.product_category_name ?? 'Без категории'}</span>
            <h2>{product.product_name}</h2>
            <p>{product.product_sku}</p>
          </div>
          <div className="catalog-product-card__footer">
            <strong className="catalog-product-price">от {formatMoney(product.price_from)}</strong>
            <span>{formatCatalogDeliveryDate(product.delivery_days_min)}</span>
          </div>
          <small>
            {product.product_unit_name} • {formatRussianCount(product.company_count, {
              one: 'поставщик',
              few: 'поставщика',
              many: 'поставщиков',
            })}
          </small>
          <button className="catalog-product-card__action" onClick={() => onOpenProduct(product)} type="button">
            Все предложения <span aria-hidden="true">→</span>
          </button>
        </article>
      ))}
    </div>
  );
}

function AdminCatalogProductTable({
  onOpenProduct,
  products,
}: {
  onOpenProduct: (product: AdminCatalogViewProduct) => void;
  products: AdminCatalogViewProduct[];
}) {
  return (
    <div className="admin-table catalog-table" role="table" aria-label="Витрина каталога">
      <div className="admin-table__head catalog-table__row" role="row">
        <span>Товар</span>
        <span>Артикул</span>
        <span>Категория</span>
        <span>Ед.</span>
        <span>Цена от</span>
        <span>Доставка</span>
        <span>Поставщики</span>
        <span>Действие</span>
      </div>
      {products.map((product) => (
        <div className="admin-table__row catalog-table__row" key={product.product_id} role="row">
          <Link className="catalog-registry-link" to={`/admin/catalog/products/${product.product_id}`}>{product.product_name}</Link>
          <span>{product.product_sku}</span>
          <span>{product.product_category_name ?? '—'}</span>
          <span>{product.product_unit_name}</span>
          <span className="catalog-product-price">{formatMoney(product.price_from)}</span>
          <span>{formatCatalogDeliveryDate(product.delivery_days_min)}</span>
          <span>{product.company_count}</span>
          <button className="catalog-table__action" onClick={() => onOpenProduct(product)} type="button">
            Предложения
          </button>
        </div>
      ))}
    </div>
  );
}

function AdminCatalogProductExpandedList({
  onOpenProduct,
  products,
  sort,
}: {
  onOpenProduct: (product: AdminCatalogViewProduct) => void;
  products: AdminCatalogViewProduct[];
  sort: AdminCatalogViewSort;
}) {
  return (
    <div className="catalog-expanded-list">
      {products.map((product) => (
        <article className="catalog-expanded-product" key={product.product_id}>
          <header className="catalog-expanded-product__header">
            <div className="catalog-expanded-product__image">
              <AdminProductImage product={product} />
            </div>
            <div className="catalog-expanded-product__title">
              <span>{product.product_category_name ?? 'Без категории'}</span>
              <h2>{product.product_name}</h2>
              <p>{product.product_sku} · {product.product_unit_name}</p>
            </div>
            <div className="catalog-expanded-product__summary">
              <strong className="catalog-product-price">от {formatMoney(product.price_from)}</strong>
              <span>
                {formatRussianCount(product.offer_count, {
                  one: 'предложение',
                  few: 'предложения',
                  many: 'предложений',
                })}
              </span>
              <button onClick={() => onOpenProduct(product)} type="button">Открыть товар</button>
            </div>
          </header>

          <div className="catalog-expanded-offers" aria-label={`Предложения: ${product.product_name}`}>
            {(product.offers ?? []).map((offer, index) => (
              <div className={index === 0 ? 'catalog-expanded-offer is-first' : 'catalog-expanded-offer'} key={offer.offer_id}>
                <div className="catalog-expanded-offer__supplier">
                  <span>{index === 0 ? getFirstCatalogOfferLabel(sort) : `№ ${index + 1}`}</span>
                  <strong>{offer.company_name}</strong>
                  <small>{offer.supplier_product_name ?? product.product_name}</small>
                </div>
                <div>
                  <small>Цена</small>
                  <strong className="catalog-product-price">{formatMoney(offer.price)}</strong>
                </div>
                <div>
                  <small>Минимум</small>
                  <strong>{formatCatalogQuantity(offer.min_quantity)} {offer.supplier_product_unit_name ?? product.product_unit_name}</strong>
                </div>
                <div>
                  <small>Доставка</small>
                  <strong>{formatCatalogDeliveryDate(offer.delivery_days)}</strong>
                </div>
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}


function CatalogShowcasePagination({
  onChange,
  page,
  pageCount,
}: {
  onChange: (page: number) => void;
  page: number;
  pageCount: number;
}) {
  if (pageCount <= 1) {
    return null;
  }

  return (
    <div className="catalog-pagination">
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} type="button">
        Назад
      </button>
      <span>{page} / {pageCount}</span>
      <button disabled={page >= pageCount} onClick={() => onChange(page + 1)} type="button">
        Вперед
      </button>
    </div>
  );
}

function CatalogCategoriesTab({
  categories,
  filters,
  onAdd,
  onPageChange,
  onChangeFilters,
  onDelete,
  onEdit,
  onReset,
  onSubmit,
}: {
  categories: AdminListResult<AdminCatalogCategory>;
  filters: CatalogCategoryFilters;
  onAdd: () => void;
  onPageChange: (page: number) => void;
  onChangeFilters: (filters: CatalogCategoryFilters) => void;
  onDelete: (category: AdminCatalogCategory) => void;
  onEdit: (category: AdminCatalogCategory) => void;
  onReset: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <>
      <ValidatedForm className="admin-filter-bar admin-filter-bar_catalog" onSubmit={onSubmit}>
        <label className="admin-field">
          <span>Поиск</span>
          <input
            onChange={(event) => onChangeFilters({ ...filters, search: event.target.value })}
            placeholder="Молочная продукция"
            type="search"
            value={filters.search}
          />
        </label>
        <label className="admin-field">
          <span>Номер</span>
          <input
            inputMode="numeric"
            onChange={(event) => onChangeFilters({ ...filters, category_id: event.target.value })}
            placeholder="12"
            type="search"
            value={filters.category_id}
          />
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={onReset} />
        <button className="catalog-button catalog-button_primary" onClick={onAdd} type="button">Добавить категорию</button>
      </ValidatedForm>

      <div className="admin-table" role="table" aria-label="Категории каталога">
        <div className="admin-table__head admin-table__row_catalog_category" role="row">
          <span>Номер</span>
          <span>Категория</span>
          <span>Родитель</span>
          <span>Порядок</span>
          <span>Обновлена</span>
          <span />
        </div>
        {categories.items.map((category) => (
          <article className="admin-table__row admin-table__row_catalog_category" key={category.category_id} role="row">
            <span>{category.category_id}</span>
            <Link className="catalog-registry-link" to={getAdminCatalogCategoryDetailPath(category.category_id)}>{category.category_name}</Link>
            <span>{formatOptionalString(category.category_parent_name)}</span>
            <span>{category.category_order}</span>
            <span>{formatDateTime(category.category_updated_at)}</span>
            <span className="admin-row-actions">
              <IconButton icon="✎" label="Редактировать" onClick={() => onEdit(category)} variant="row" />
              <IconButton icon="×" label="Удалить" onClick={() => onDelete(category)} variant="row" />
            </span>
          </article>
        ))}
      </div>

      {categories.items.length === 0 ? <p className="admin-empty">Категории не найдены.</p> : null}
      <PaginationMeta meta={categories.meta} />
      <CatalogPagination page={categories.meta.page ?? 1} pageCount={categories.meta.page_count ?? 1} onChange={onPageChange} />
    </>
  );
}

function CatalogProductsTab({
  categories,
  dictionaries,
  filters,
  onAdd,
  onPageChange,
  onChangeFilters,
  onDelete,
  onEdit,
  onOpen,
  onReset,
  onSubmit,
  products,
}: {
  categories: AdminCatalogCategory[];
  dictionaries: AdminBootstrapDictionaries;
  filters: CatalogProductFilters;
  onAdd: () => void;
  onPageChange: (page: number) => void;
  onChangeFilters: (filters: CatalogProductFilters) => void;
  onDelete: (product: AdminCatalogProduct) => void;
  onEdit: (product: AdminCatalogProduct) => void;
  onOpen: (product: AdminCatalogProduct) => void;
  onReset: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  products: AdminListResult<AdminCatalogProduct>;
}) {
  return (
    <>
      <ValidatedForm className="admin-filter-bar admin-filter-bar_catalog_products" onSubmit={onSubmit}>
        <label className="admin-field">
          <span>Поиск</span>
          <input
            onChange={(event) => onChangeFilters({ ...filters, search: event.target.value })}
            placeholder="Артикул или название"
            type="search"
            value={filters.search}
          />
        </label>
        <label className="admin-field">
          <span>Номер</span>
          <input
            inputMode="numeric"
            onChange={(event) => onChangeFilters({ ...filters, product_id: event.target.value })}
            placeholder="24"
            type="search"
            value={filters.product_id}
          />
        </label>
        <label className="admin-field">
          <span>Категория</span>
          <SearchableSelect inputId="catalog-filter-category" ariaLabel="Категория" isClearable options={catalogCategorySelectOptions(categories)} value={filters.product_category_id} onChange={(value) => onChangeFilters({ ...filters, product_category_id: value })} placeholder="Все категории" />
        </label>
        <label className="admin-field">
          <span>Статус</span>
          <SearchableSelect inputId="catalog-filter-status" ariaLabel="Статус" isClearable options={dictionarySelectOptions(dictionaries.product_statuses)} value={filters.product_status_id} onChange={(value) => onChangeFilters({ ...filters, product_status_id: value })} placeholder="Все статусы" />
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={onReset} />
        <button className="catalog-button catalog-button_primary" onClick={onAdd} type="button">Добавить товар</button>
      </ValidatedForm>

      <div className="admin-table" role="table" aria-label="Товары каталога">
        <div className="admin-table__head admin-table__row_catalog_product" role="row">
          <span>Номер</span>
          <span>Артикул</span>
          <span>Товар</span>
          <span>Категория</span>
          <span>Ед.</span>
          <span>Статус</span>
          <span />
        </div>
        {products.items.map((product) => (
          <article className="admin-table__row admin-table__row_catalog_product" key={product.product_id} role="row">
            <span>{product.product_id}</span>
            <strong>{product.product_sku}</strong>
            <div className="catalog-registry-product"><Link className="catalog-registry-link" to={`/admin/catalog/products/${product.product_id}`}>{product.product_name}</Link><small>{[product.product_brand_name, product.product_properties?.country_name].filter(Boolean).join(' · ')}</small>{product.product_requires_unit_review ? <small className="catalog-unit-review">Проверить единицу</small> : null}</div>
            <span>{product.product_categories?.map((category) => category.category_name).join(', ') || formatOptionalString(product.product_category_name)}</span>
            <span>{formatOptionalString(product.product_unit_name)}</span>
            <span className={getProductStatusClassName(product.product_status_id)}>
              {formatOptionalString(product.product_status_name)}
            </span>
            <span className="admin-row-actions">
              <IconButton icon="↗" label="Открыть" onClick={() => onOpen(product)} variant="row" />
              <IconButton icon="✎" label="Редактировать" onClick={() => onEdit(product)} variant="row" />
              <IconButton icon="×" label="Удалить" onClick={() => onDelete(product)} variant="row" />
            </span>
          </article>
        ))}
      </div>

      {products.items.length === 0 ? <p className="admin-empty">Товары не найдены.</p> : null}
      <PaginationMeta meta={products.meta} />
      <CatalogPagination page={products.meta.page ?? 1} pageCount={products.meta.page_count ?? 1} onChange={onPageChange} />
    </>
  );
}

function ListState({ errorMessage, isLoading }: { errorMessage: string | null; isLoading: boolean }) {
  if (errorMessage !== null) {
    return <p className="admin-message admin-message_error">{errorMessage}</p>;
  }

  if (isLoading) {
    return <LoadingState compact />;
  }

  return null;
}

interface PaginationMetaProps {
  isLoading?: boolean;
  meta: AdminListResult<unknown>['meta'];
  onPageChange?: (page: number) => void;
}

function PaginationMeta({ isLoading = false, meta, onPageChange }: PaginationMetaProps) {
  if (meta.total === undefined) {
    return null;
  }

  const page = meta.page ?? 1;

  if (onPageChange === undefined) {
    return (
      <p className="admin-pagination">
        Всего: {meta.total}. Страница {page} из {meta.page_count ?? 1}.
      </p>
    );
  }

  const pageCount = Math.max(meta.page_count ?? 1, 1);

  return (
    <div className="admin-pagination-row">
      <p className="admin-pagination">
        Всего: {meta.total}. Страница {page} из {pageCount}.
      </p>
      <div className="catalog-pagination">
        <button disabled={isLoading || page <= 1} onClick={() => onPageChange(page - 1)} type="button">
          Предыдущая страница
        </button>
        <button disabled={isLoading || page >= pageCount} onClick={() => onPageChange(page + 1)} type="button">
          Следующая страница
        </button>
      </div>
    </div>
  );
}

interface UserDetailPageProps {
  dictionaries: AdminBootstrapDictionaries;
  entityId: string;
  onNavigate: (path: string) => void;
}

interface UserEditFormState {
  user_email: string;
  user_full_name: string;
  user_phone: string;
  user_status_id: string;
  user_email_confirmed: string;
}

export function UserDetailPage({ dictionaries, entityId, onNavigate }: UserDetailPageProps) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [userForm, setUserForm] = useState<UserEditFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const userId = Number(entityId);

    if (Number.isNaN(userId)) {
      setErrorMessage('Пользователь выбран неверно.');

      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getUsers({
        page: 1,
        per_page: 1,
        user_id: userId,
      })
      .then((result) => {
        const nextUser = result.items[0] ?? null;
        setUser(nextUser);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [entityId]);

  const openEditForm = (): void => {
    if (user === null) {
      return;
    }

    setUserForm(userToEditForm(user));
    setErrorMessage(null);
  };

  const closeEditForm = (): void => {
    setUserForm(null);
  };

  const saveUser = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (user === null || userForm === null) {
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    const payload = createUserUpdatePayload(userForm);

    adminApi
      .updateUser(user.user_id, payload)
      .then((updatedUser) => {
        setUser(updatedUser);
        setUserForm(null);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <section className="admin-page">
      <IconButton icon="←" label="Назад к списку" onClick={() => onNavigate('/admin/users')} variant="back" />

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {user !== null ? (
        <DetailCard
          title={getUserDetailTitle(user)}
          eyebrow="Пользователь"
          actions={<IconButton icon="✎" label="Редактировать" onClick={openEditForm} />}
          sections={[
            {
              title: 'Профиль',
              fields: [
                ['ID', String(user.user_id)],
                ['ФИО', formatOptionalString(user.user_full_name)],
                ['Электронная почта', user.user_email],
                ['Телефон', formatOptionalString(user.user_phone)],
                ['Статус', getDictionaryNameById(dictionaries.user_statuses, user.user_status_id)],
                ['Почта подтверждена', getDictionaryNameByValue(dictionaries.user_email_confirmation_statuses, user.user_email_confirmed)],
                ['Создан', formatDateTime(user.user_created_at)],
                ['Обновлен', formatDateTime(user.user_updated_at)],
              ],
            },
            {
              title: 'Служебные сведения',
              fields: [
                ['Часовой пояс', formatOptionalString(user.user_timezone)],
                ['Согласие на обработку персональных данных', formatDateTime(user.user_pd_consent_at ?? '')],
              ],
            },
          ]}
        />
      ) : null}

      {userForm !== null ? (
        <UserEditModal
          form={userForm}
          isSaving={isSaving}
          userEmailConfirmationStatuses={dictionaries.user_email_confirmation_statuses}
          userStatuses={dictionaries.user_statuses}
          onChange={setUserForm}
          onClose={closeEditForm}
          onSubmit={saveUser}
        />
      ) : null}

      {!isLoading && errorMessage === null && user === null ? <p className="admin-empty">Пользователь не найден.</p> : null}
    </section>
  );
}

function UserEditModal({
  form,
  isSaving,
  userEmailConfirmationStatuses,
  userStatuses,
  onChange,
  onClose,
  onSubmit,
}: {
  form: UserEditFormState;
  isSaving: boolean;
  userEmailConfirmationStatuses: AdminDictionaryItem[];
  userStatuses: AdminDictionaryItem[];
  onChange: (form: UserEditFormState) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-modal" aria-label="Редактирование пользователя" role="dialog" aria-modal="true">
      <ValidatedForm className="admin-modal__panel" onSubmit={onSubmit}>
        <div className="admin-modal__header">
          <div>
            <p className="admin-eyebrow">Пользователь</p>
            <h2>Редактирование</h2>
          </div>
          <IconButton icon="×" label="Закрыть" onClick={onClose} variant="row" />
        </div>

        <div className="admin-modal__grid">
          <label className="admin-field">
            <span>ФИО</span>
            <input
              onChange={(event) => onChange({ ...form, user_full_name: event.target.value })}
              placeholder="Иван Петров"
              type="text"
              value={form.user_full_name}
            />
          </label>
          <label className="admin-field">
            <span>Электронная почта</span>
            <input
              onChange={(event) => onChange({ ...form, user_email: event.target.value })}
              required
              type="email"
              value={form.user_email}
            />
          </label>
          <label className="admin-field">
            <span>Телефон</span>
            <input
              inputMode="tel"
              onChange={(event) => onChange({ ...form, user_phone: event.target.value })}
              placeholder="+79990000000"
              type="tel"
              value={form.user_phone}
            />
          </label>
          <label className="admin-field">
            <span>Статус</span>
            <select onChange={(event) => onChange({ ...form, user_status_id: event.target.value })} value={form.user_status_id}>
              {renderDictionaryOptions(userStatuses)}
            </select>
          </label>
          <label className="admin-field">
            <span>Почта</span>
            <select
              onChange={(event) => onChange({ ...form, user_email_confirmed: event.target.value })}
              value={form.user_email_confirmed}
            >
              {renderDictionaryOptions(userEmailConfirmationStatuses)}
            </select>
          </label>
        </div>

        <div className="admin-modal__actions">
          <IconButton icon="✓" label={isSaving ? 'Сохраняем' : 'Сохранить'} type="submit" variant="primary" />
        </div>
      </ValidatedForm>
    </section>
  );
}

interface CompanyDetailPageProps {
  activeTab: CompanyDetailTab;
  deliveryAddressFormAddressId?: string;
  deliveryAddressFormMode?: CompanyDeliveryFormMode;
  deliveryZoneFormGroupId?: string;
  deliveryZoneFormMode?: CompanyDeliveryFormMode;
  dictionaries: AdminBootstrapDictionaries;
  entityId: string;
  onNavigate: (path: string) => void;
}

interface CompanyEditFormState {
  company_name: string;
  company_logo_file_id: number | null;
  company_status_id: string;
  company_registration_address: string;
  company_actual_address: string;
  company_inn: string;
  company_kpp: string;
  company_ogrn: string;
  company_rs: string;
  company_ks: string;
  company_bik: string;
  company_bank_name: string;
  company_phone: string;
  company_email: string;
  company_edo_id: string;
  company_default_min_order_sum: string;
  company_reject_comment: string;
}

interface CompanyDeliveryZoneFormState {
  zone_ids: string[];
  group_name: string;
  is_enabled: boolean;
  schedule_timezone: string;
  schedule_delivery_days: string;
  schedule_days_of_week: string[];
  schedule_order_cutoff_time: string;
  schedule_term_without_days: boolean;
}

interface CompanyDeliveryAddressFormState {
  address_name: string;
  address_address: string;
  address_lat: string;
  address_lng: string;
  address_selection_key: string;
  address_egais_number: string;
  address_mercury_number: string;
}

interface CompanyFileFormState {
  context_code: string;
  file: File | null;
}

const companyDetailTabs: Array<{ label: string; tab: CompanyDetailTab }> = [
  { label: 'Информация', tab: 'info' },
  { label: 'Пользователи и роли', tab: 'users' },
  { label: 'Чаты', tab: 'chats' },
  { label: 'Файлы', tab: 'files' },
  { label: 'Адреса доставки', tab: 'delivery-addresses' },
  { label: 'Зоны доставки', tab: 'delivery-zones' },
  { label: 'Прайсы', tab: 'prices' },
  { label: 'Предложения', tab: 'offers' },
];

export function CompanyDetailPage({
  activeTab,
  deliveryAddressFormAddressId: routeDeliveryAddressFormAddressId,
  deliveryAddressFormMode,
  deliveryZoneFormGroupId: routeDeliveryZoneFormGroupId,
  deliveryZoneFormMode,
  dictionaries,
  entityId,
  onNavigate,
}: CompanyDetailPageProps) {
  const [company, setCompany] = useState<AdminCompany | null>(null);
  const [companyAgreement, setCompanyAgreement] = useState<AdminCompanyAgreement | null>(null);
  const [companyUsers, setCompanyUsers] = useState<AdminListResult<AdminCompanyUser>>({ items: [], meta: {} });
  const [companyChats, setCompanyChats] = useState<AdminCompanyChat[]>([]);
  const [companyFiles, setCompanyFiles] = useState<AdminCompanyFile[]>([]);
  const [companyFileContexts, setCompanyFileContexts] = useState<AdminFileContext[]>([]);
  const [companyDeliveryAddresses, setCompanyDeliveryAddresses] = useState<AdminCompanyDeliveryAddress[]>([]);
  const [companyDeliveryZones, setCompanyDeliveryZones] = useState<AdminCompanyDeliveryZone[]>([]);
  const [availableDeliveryZones, setAvailableDeliveryZones] = useState<AdminDeliveryZone[]>([]);
  const [companyPriceImports, setCompanyPriceImports] = useState<AdminListResult<AdminPriceImport>>({ items: [], meta: {} });
  const [companyPriceImportFilters, setCompanyPriceImportFilters] = useState<PriceImportFilters>({
    ...defaultPriceImportFilters,
    company_id: entityId,
  });
  const [appliedCompanyPriceImportFilters, setAppliedCompanyPriceImportFilters] = useState<PriceImportFilters>({
    ...defaultPriceImportFilters,
    company_id: entityId,
  });
  const [companyPriceImportFile, setCompanyPriceImportFile] = useState<File | null>(null);
  const [companyStatuses, setCompanyStatuses] = useState<AdminDictionaryItem[]>(dictionaries.company_statuses);
  const [companyForm, setCompanyForm] = useState<CompanyEditFormState | null>(null);
  const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
  const [companyFileForm, setCompanyFileForm] = useState<CompanyFileFormState | null>(null);
  const [deliveryAddressForm, setDeliveryAddressForm] = useState<CompanyDeliveryAddressFormState | null>(null);
  const [deliveryAddressFormAddressId, setDeliveryAddressFormAddressId] = useState<number | null>(null);
  const [deliveryZoneForm, setDeliveryZoneForm] = useState<CompanyDeliveryZoneFormState | null>(null);
  const [deliveryZoneFormGroupId, setDeliveryZoneFormGroupId] = useState<number | null>(null);
  const [deliveryZoneFormEditableZoneIds, setDeliveryZoneFormEditableZoneIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [isDeliveryAddressesLoading, setIsDeliveryAddressesLoading] = useState(false);
  const [isDeliveryZonesLoading, setIsDeliveryZonesLoading] = useState(false);
  const [isPriceImportsLoading, setIsPriceImportsLoading] = useState(false);
  const [isPriceImportSaving, setIsPriceImportSaving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStatusTransitionSaving, setIsStatusTransitionSaving] = useState(false);
  const [isFilesSaving, setIsFilesSaving] = useState(false);
  const [downloadingCompanyFileId, setDownloadingCompanyFileId] = useState<number | null>(null);
  const [isDeliveryAddressesSaving, setIsDeliveryAddressesSaving] = useState(false);
  const [isDeliveryZonesSaving, setIsDeliveryZonesSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [usersErrorMessage, setUsersErrorMessage] = useState<string | null>(null);
  const [chatsErrorMessage, setChatsErrorMessage] = useState<string | null>(null);
  const [filesErrorMessage, setFilesErrorMessage] = useState<string | null>(null);
  const [deliveryAddressesErrorMessage, setDeliveryAddressesErrorMessage] = useState<string | null>(null);
  const [deliveryZonesErrorMessage, setDeliveryZonesErrorMessage] = useState<string | null>(null);
  const [priceImportsErrorMessage, setPriceImportsErrorMessage] = useState<string | null>(null);
  const currentCompanyId = Number(entityId);
  const companyUsersRequestId = useRef(0);
  const fetchCompanyUsers = useCallback((companyId: number, page = 1): void => {
    companyUsersRequestId.current += 1;
    const requestId = companyUsersRequestId.current;
    setIsUsersLoading(true);
    setUsersErrorMessage(null);

    adminApi
      .getCompanyUsers(companyId, page)
      .then((result) => {
        if (requestId === companyUsersRequestId.current) {
          setCompanyUsers(result);
        }
      })
      .catch((error: Error) => {
        if (requestId === companyUsersRequestId.current) {
          setUsersErrorMessage(error.message);
        }
      })
      .finally(() => {
        if (requestId === companyUsersRequestId.current) {
          setIsUsersLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    const companyId = Number(entityId);

    if (Number.isNaN(companyId)) {
      setErrorMessage('Компания выбрана неверно.');

      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    adminApi
      .getCompanies({
        page: 1,
        per_page: 1,
        company_id: companyId,
      })
      .then((result) => {
        const nextCompany = result.items[0] ?? null;
        setCompany(nextCompany);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [entityId]);

  useEffect(() => {
    if (
      company === null
      || (company.company_sale_status_id !== undefined && company.company_status_id !== COMPANY_STATUS_APPROVED)
      || ![
        COMPANY_STATUS_CONTRACT_SIGNING,
        COMPANY_STATUS_SIGNED_SCAN_UPLOADED,
        COMPANY_STATUS_APPROVED,
        COMPANY_STATUS_ORIGINAL_RECEIVED,
      ].includes(company.company_sale_status_id ?? company.company_status_id)
    ) {
      setCompanyAgreement(null);
      return;
    }

    adminApi
      .getCompanyAgreement(company.company_id)
      .then(setCompanyAgreement)
      .catch((error: Error) => setErrorMessage(error.message));
  }, [company]);

  useEffect(() => {
    const nextFilters = {
      ...defaultPriceImportFilters,
      company_id: entityId,
    };

    setCompanyPriceImportFilters(nextFilters);
    setAppliedCompanyPriceImportFilters(nextFilters);
    setCompanyPriceImportFile(null);
  }, [entityId]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'users' || Number.isNaN(companyId)) {
      return;
    }

    setCompanyUsers({ items: [], meta: {} });
    fetchCompanyUsers(companyId);
  }, [activeTab, entityId, fetchCompanyUsers]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'chats' || Number.isNaN(companyId)) {
      return;
    }

    setIsChatsLoading(true);
    setChatsErrorMessage(null);

    adminApi
      .getCompanyChats(companyId)
      .then(setCompanyChats)
      .catch((error: Error) => {
        setChatsErrorMessage(error.message);
      })
      .finally(() => {
        setIsChatsLoading(false);
      });
  }, [activeTab, entityId]);

  useEffect(() => {
    if (activeTab !== 'delivery-addresses') {
      return;
    }

    if (deliveryAddressFormMode === undefined) {
      setDeliveryAddressForm(null);
      setDeliveryAddressFormAddressId(null);

      return;
    }

    if (deliveryAddressFormMode === 'create') {
      if (deliveryAddressForm === null || deliveryAddressFormAddressId !== null) {
        setDeliveryAddressForm(defaultCompanyDeliveryAddressForm);
        setDeliveryAddressFormAddressId(null);
      }

      return;
    }

    const addressId = Number(routeDeliveryAddressFormAddressId);

    if (Number.isNaN(addressId) || deliveryAddressFormAddressId === addressId) {
      return;
    }

    const deliveryAddress = companyDeliveryAddresses.find((item) => item.address_id === addressId);

    if (deliveryAddress === undefined) {
      return;
    }

    setDeliveryAddressForm(companyDeliveryAddressToForm(deliveryAddress));
    setDeliveryAddressFormAddressId(deliveryAddress.address_id);
  }, [
    activeTab,
    companyDeliveryAddresses,
    deliveryAddressForm,
    deliveryAddressFormAddressId,
    deliveryAddressFormMode,
    routeDeliveryAddressFormAddressId,
  ]);

  useEffect(() => {
    if (activeTab !== 'delivery-zones') {
      return;
    }

    if (deliveryZoneFormMode === undefined) {
      setDeliveryZoneForm(null);
      setDeliveryZoneFormGroupId(null);
      setDeliveryZoneFormEditableZoneIds([]);

      return;
    }

    if (deliveryZoneFormMode === 'create') {
      if (deliveryZoneForm === null || deliveryZoneFormGroupId !== null) {
        setDeliveryZoneForm(defaultCompanyDeliveryZoneForm);
        setDeliveryZoneFormGroupId(null);
        setDeliveryZoneFormEditableZoneIds([]);
      }

      return;
    }

    const groupId = Number(routeDeliveryZoneFormGroupId);

    if (Number.isNaN(groupId)) {
      return;
    }

    const deliveryZone = companyDeliveryZones.find((item) => item.group_id === groupId);

    if (deliveryZone === undefined || deliveryZoneFormGroupId === deliveryZone.group_id) {
      return;
    }

    setDeliveryZoneForm(companyDeliveryZoneToForm(deliveryZone));
    setDeliveryZoneFormGroupId(deliveryZone.group_id);
    setDeliveryZoneFormEditableZoneIds(deliveryZone.zone_ids);
  }, [
    activeTab,
    companyDeliveryZones,
    deliveryZoneForm,
    deliveryZoneFormGroupId,
    deliveryZoneFormMode,
    routeDeliveryZoneFormGroupId,
  ]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'files' || Number.isNaN(companyId)) {
      return;
    }

    setIsFilesLoading(true);
    setFilesErrorMessage(null);

    const companyFilesPromise = adminApi.getCompanyFiles(companyId);
    const companyFileContextsPromise = adminApi.getCompanyFileContexts();

    Promise.all([companyFilesPromise, companyFileContextsPromise])
      .then(([nextCompanyFiles, nextCompanyFileContexts]) => {
        setCompanyFiles(nextCompanyFiles);
        setCompanyFileContexts(nextCompanyFileContexts);
      })
      .catch((error: Error) => {
        setFilesErrorMessage(error.message);
      })
      .finally(() => {
        setIsFilesLoading(false);
      });
  }, [activeTab, entityId]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'delivery-addresses' || Number.isNaN(companyId)) {
      return;
    }

    setIsDeliveryAddressesLoading(true);
    setDeliveryAddressesErrorMessage(null);

    adminApi
      .getCompanyDeliveryAddresses(companyId)
      .then(setCompanyDeliveryAddresses)
      .catch((error: Error) => {
        setDeliveryAddressesErrorMessage(error.message);
      })
      .finally(() => {
        setIsDeliveryAddressesLoading(false);
      });
  }, [activeTab, entityId]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'delivery-zones' || Number.isNaN(companyId)) {
      return;
    }

    setIsDeliveryZonesLoading(true);
    setDeliveryZonesErrorMessage(null);

    const companyDeliveryZonesPromise = adminApi.getCompanyDeliveryZones(companyId);
    const availableDeliveryZonesPromise = adminApi.getDeliveryZoneCatalog({
      page: 1,
      per_page: 200,
    });

    Promise.all([companyDeliveryZonesPromise, availableDeliveryZonesPromise])
      .then(([nextCompanyDeliveryZones, nextAvailableDeliveryZones]) => {
        setCompanyDeliveryZones(nextCompanyDeliveryZones);
        setAvailableDeliveryZones(nextAvailableDeliveryZones.allZones);
      })
      .catch((error: Error) => {
        setDeliveryZonesErrorMessage(error.message);
      })
      .finally(() => {
        setIsDeliveryZonesLoading(false);
      });
  }, [activeTab, entityId]);

  useEffect(() => {
    const companyId = Number(entityId);

    if (activeTab !== 'prices' || Number.isNaN(companyId)) {
      return;
    }

    const nextFilters: PriceImportFilters = {
      ...defaultPriceImportFilters,
      company_id: String(companyId),
    };

    setCompanyPriceImportFilters(nextFilters);
    setAppliedCompanyPriceImportFilters(nextFilters);
    fetchCompanyPriceImports(nextFilters);
  }, [activeTab, entityId]);

  const openEditForm = (): void => {
    if (company === null) {
      return;
    }

    setCompanyForm(companyToEditForm(company));
    setCompanyLogoFile(null);
    setErrorMessage(null);

    adminApi
      .getBootstrapDictionaries()
      .then((nextDictionaries) => {
        setCompanyStatuses(nextDictionaries.company_statuses);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      });
  };

  const closeEditForm = (): void => {
    setCompanyForm(null);
    setCompanyLogoFile(null);
  };

  const openCompanyFileForm = (): void => {
    setCompanyFileForm({
      context_code: companyFileContexts[0]?.context_code ?? '',
      file: null,
    });
    setFilesErrorMessage(null);
  };

  const closeCompanyFileForm = (): void => {
    setCompanyFileForm(null);
  };

  const openDeliveryAddressForm = (deliveryAddress?: AdminCompanyDeliveryAddress): void => {
    const addressPath = deliveryAddress === undefined
      ? `/admin/companies/${entityId}/delivery-addresses/new`
      : `/admin/companies/${entityId}/delivery-addresses/${deliveryAddress.address_id}/edit`;

    onNavigate(addressPath);
  };

  const closeDeliveryAddressForm = (): void => {
    setDeliveryAddressForm(null);
    setDeliveryAddressFormAddressId(null);
    onNavigate(`/admin/companies/${entityId}/delivery-addresses`);
  };

  const saveDeliveryAddress = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (company === null || deliveryAddressForm === null) {
      return;
    }

    const payload = createCompanyDeliveryAddressPayload(deliveryAddressForm);

    if (payload === null) {
      setDeliveryAddressesErrorMessage('Заполните название и выберите адрес из списка подсказок.');

      return;
    }

    setIsDeliveryAddressesSaving(true);
    setDeliveryAddressesErrorMessage(null);

    const savePromise = deliveryAddressFormAddressId === null
      ? adminApi.createCompanyDeliveryAddress(company.company_id, payload)
      : adminApi.updateCompanyDeliveryAddress(company.company_id, deliveryAddressFormAddressId, payload);

    savePromise
      .then((deliveryAddress) => {
        const nextDeliveryAddresses = deliveryAddressFormAddressId === null
          ? [...companyDeliveryAddresses, deliveryAddress]
          : replaceCompanyDeliveryAddress(companyDeliveryAddresses, deliveryAddress);

        setCompanyDeliveryAddresses(nextDeliveryAddresses);
        setDeliveryAddressForm(null);
        setDeliveryAddressFormAddressId(null);
        onNavigate(`/admin/companies/${entityId}/delivery-addresses`);
      })
      .catch((error: Error) => {
        setDeliveryAddressesErrorMessage(error.message);
      })
      .finally(() => {
        setIsDeliveryAddressesSaving(false);
      });
  };

  const deleteDeliveryAddress = (deliveryAddress: AdminCompanyDeliveryAddress): void => {
    if (company === null) {
      return;
    }

    const shouldDelete = window.confirm(`Архивировать адрес "${deliveryAddress.address_name}"?`);

    if (!shouldDelete) {
      return;
    }

    setIsDeliveryAddressesSaving(true);
    setDeliveryAddressesErrorMessage(null);

    adminApi
      .deleteCompanyDeliveryAddress(company.company_id, deliveryAddress.address_id)
      .then(() => {
        const nextDeliveryAddresses = companyDeliveryAddresses.filter((currentAddress) => {
          return currentAddress.address_id !== deliveryAddress.address_id;
        });

        setCompanyDeliveryAddresses(nextDeliveryAddresses);

        if (deliveryAddressFormAddressId === deliveryAddress.address_id) {
          setDeliveryAddressForm(null);
          setDeliveryAddressFormAddressId(null);
          onNavigate(`/admin/companies/${entityId}/delivery-addresses`);
        }
      })
      .catch((error: Error) => {
        setDeliveryAddressesErrorMessage(error.message);
      })
      .finally(() => {
        setIsDeliveryAddressesSaving(false);
      });
  };

  const saveCompanyFile = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (company === null || companyFileForm === null) {
      return;
    }

    if (companyFileForm.file === null) {
      setFilesErrorMessage('Выберите файл для загрузки.');

      return;
    }

    if (companyFileForm.context_code.trim() === '') {
      setFilesErrorMessage('Выберите контекст файла.');

      return;
    }

    setIsFilesSaving(true);
    setFilesErrorMessage(null);

    adminApi
      .uploadCompanyFile(companyFileForm.file)
      .then((storedFile) => {
        const payload: AdminCompanyFileLinkCreateRequest = {
          file_id: storedFile.file_id,
          context_code: companyFileForm.context_code,
        };

        return adminApi.linkCompanyFile(company.company_id, payload);
      })
      .then((fileLink) => {
        setCompanyFiles([...companyFiles, fileLink]);
        closeCompanyFileForm();
      })
      .catch((error: Error) => {
        setFilesErrorMessage(error.message);
      })
      .finally(() => {
        setIsFilesSaving(false);
      });
  };

  const downloadCompanyFile = (companyFile: AdminCompanyFile): void => {
    if (company === null || downloadingCompanyFileId !== null) {
      return;
    }

    const fileName = companyFile.file.file_public_name ?? companyFile.file.file_original_name;

    setDownloadingCompanyFileId(companyFile.link_file_id);
    setFilesErrorMessage(null);

    adminApi
      .downloadCompanyFile(company.company_id, companyFile.link_file_id, fileName)
      .catch((error: Error) => {
        setFilesErrorMessage(error.message);
      })
      .finally(() => {
        setDownloadingCompanyFileId(null);
      });
  };

  const downloadAgreementScan = (): void => {
    if (
      company === null
      || companyAgreement === null
      || companyAgreement.company_id !== company.company_id
      || companyAgreement.signed_scan_file_id === null
      || downloadingCompanyFileId !== null
    ) {
      return;
    }

    const scanFileId = companyAgreement.signed_scan_file_id;

    setDownloadingCompanyFileId(scanFileId);
    setErrorMessage(null);

    adminApi
      .downloadCompanyFile(company.company_id, scanFileId)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setDownloadingCompanyFileId(null);
      });
  };

  const saveCompany = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (company === null || companyForm === null) {
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    const payload = createCompanyUpdatePayload(companyForm);
    const request = companyLogoFile === null
      ? adminApi.updateCompany(company.company_id, payload)
      : adminApi.uploadCompanyFile(companyLogoFile).then((storedFile) => {
        const nextPayload = {
          ...payload,
          company_logo_file_id: storedFile.file_id,
        };

        return adminApi.updateCompany(company.company_id, nextPayload);
      });

    request
      .then((updatedCompany) => {
        setCompany(updatedCompany);
        setCompanyForm(null);
        setCompanyLogoFile(null);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const approveCompany = (): void => {
    if (company === null || isStatusTransitionSaving) {
      return;
    }

    setIsStatusTransitionSaving(true);
    setErrorMessage(null);

    adminApi
      .approveCompany(company.company_id)
      .then(setCompany)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsStatusTransitionSaving(false);
      });
  };

  const signCompanyContract = (): void => {
    if (company === null || isStatusTransitionSaving) {
      return;
    }

    setIsStatusTransitionSaving(true);
    setErrorMessage(null);

    adminApi
      .signCompanyContract(company.company_id)
      .then(setCompany)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsStatusTransitionSaving(false);
      });
  };

  const openDeliveryZoneForm = (deliveryZone?: AdminCompanyDeliveryZone): void => {
    const deliveryZonePath = deliveryZone === undefined
      ? `/admin/companies/${entityId}/delivery-zones/new`
      : `/admin/companies/${entityId}/delivery-zones/${deliveryZone.group_id}/edit`;

    onNavigate(deliveryZonePath);
  };

  const closeDeliveryZoneForm = (): void => {
    setDeliveryZoneForm(null);
    setDeliveryZoneFormGroupId(null);
    setDeliveryZoneFormEditableZoneIds([]);
    onNavigate(`/admin/companies/${entityId}/delivery-zones`);
  };

  const removeCompanyDeliveryZone = (deliveryZone: AdminCompanyDeliveryZone): void => {
    const zoneNames = deliveryZone.zones.map((zone) => zone.zone_name).join(', ');
    const shouldDelete = window.confirm(`Убрать группу зон "${zoneNames}" из доставки компании?`);

    if (!shouldDelete) {
      return;
    }

    const nextDeliveryZones = companyDeliveryZones.filter((item) => item.group_id !== deliveryZone.group_id);
    saveCompanyDeliveryZones(nextDeliveryZones);
  };

  const saveDeliveryZone = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (deliveryZoneForm === null) {
      return;
    }

    const nextDeliveryZone = companyDeliveryZoneFromForm(
      deliveryZoneForm,
      companyDeliveryZones,
      availableDeliveryZones,
      deliveryZoneFormEditableZoneIds,
    );
    const filteredDeliveryZones = companyDeliveryZones.filter((item) => item.group_id !== nextDeliveryZone.group_id);
    const nextDeliveryZones = [...filteredDeliveryZones, nextDeliveryZone];

    saveCompanyDeliveryZones(nextDeliveryZones);
  };

  function saveCompanyDeliveryZones(nextDeliveryZones: AdminCompanyDeliveryZone[]): void {
    if (company === null || isDeliveryZonesSaving || isDeliveryZonesLoading) {
      return;
    }

    setIsDeliveryZonesSaving(true);
    setDeliveryZonesErrorMessage(null);

    const payload = createCompanyDeliveryZonesPayload(nextDeliveryZones, companyDeliveryZones);

    adminApi
      .updateCompanyDeliveryZones(company.company_id, payload)
      .then((updatedDeliveryZones) => {
        setCompanyDeliveryZones(updatedDeliveryZones);
        setDeliveryZoneForm(null);
        setDeliveryZoneFormGroupId(null);
        setDeliveryZoneFormEditableZoneIds([]);
        onNavigate(`/admin/companies/${entityId}/delivery-zones`);
      })
      .catch((error: Error) => {
        setDeliveryZonesErrorMessage(error.message);
      })
      .finally(() => {
        setIsDeliveryZonesSaving(false);
      });
  }

  function fetchCompanyPriceImports(nextFilters: PriceImportFilters, page = 1): void {
    setIsPriceImportsLoading(true);
    setPriceImportsErrorMessage(null);

    priceImportApi
      .getImports(buildPriceImportListParams(nextFilters, page))
      .then(setCompanyPriceImports)
      .catch((error: Error) => {
        setPriceImportsErrorMessage(error.message);
      })
      .finally(() => {
        setIsPriceImportsLoading(false);
      });
  }

  const submitCompanyPriceImportFilters = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAppliedCompanyPriceImportFilters(companyPriceImportFilters);
    fetchCompanyPriceImports(companyPriceImportFilters);
  };

  const resetCompanyPriceImportFilters = (): void => {
    const nextFilters: PriceImportFilters = {
      ...defaultPriceImportFilters,
      company_id: entityId,
    };

    setCompanyPriceImportFilters(nextFilters);
    setAppliedCompanyPriceImportFilters(nextFilters);
    fetchCompanyPriceImports(nextFilters);
  };

  const changeCompanyPriceImportFile = (file: File | null): void => {
    setCompanyPriceImportFile(file);

    if (file !== null && file.size > PRICE_FILE_MAX_SIZE_BYTES) {
      setPriceImportsErrorMessage('Размер файла не должен превышать 10 МБ.');

      return;
    }

    setPriceImportsErrorMessage(null);
  };

  const saveCompanyPriceImport = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (company === null || companyPriceImportFile === null || isPriceImportSaving) {
      return;
    }

    if (companyPriceImportFile.size > PRICE_FILE_MAX_SIZE_BYTES) {
      setPriceImportsErrorMessage('Размер файла не должен превышать 10 МБ.');

      return;
    }

    setIsPriceImportSaving(true);
    setPriceImportsErrorMessage(null);

    priceImportApi
      .uploadImportFile(companyPriceImportFile)
      .then((storedFile) => {
        return priceImportApi.createImport({
          company_id: company.company_id,
          file_id: storedFile.file_id,
        });
      })
      .then(() => {
        setCompanyPriceImportFile(null);
        fetchCompanyPriceImports(appliedCompanyPriceImportFilters);
      })
      .catch((error: Error) => {
        setPriceImportsErrorMessage(error.message);
      })
      .finally(() => {
        setIsPriceImportSaving(false);
      });
  };

  const isDeliveryAddressFormRoute = activeTab === 'delivery-addresses' && deliveryAddressFormMode !== undefined;
  const isDeliveryZoneFormRoute = activeTab === 'delivery-zones' && deliveryZoneFormMode !== undefined;

  return (
    <section className="admin-page">
      <IconButton icon="←" label="Назад к списку" onClick={() => onNavigate('/admin/companies')} variant="back" />

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {company !== null ? (
        <>
          <CompanyDetailHeader
            activeTab={activeTab}
            company={company}
            onNavigate={onNavigate}
          />

          {activeTab === 'info' ? (
            <>
              <CompanyAccessPanel key={company.company_id} company={company} dictionaries={dictionaries} onChange={setCompany} />
              <CompanyInfoTab
              agreement={companyAgreement}
              company={company}
              companyStatuses={companyStatuses}
              isDownloadingAgreementScan={companyAgreement?.signed_scan_file_id !== null && companyAgreement?.signed_scan_file_id === downloadingCompanyFileId}
              isStatusTransitionSaving={isStatusTransitionSaving}
              onApprove={approveCompany}
              onDownloadAgreementScan={downloadAgreementScan}
              onEdit={openEditForm}
              onSignContract={signCompanyContract}
            />
            </>
          ) : null}

          {activeTab === 'users' ? (
            <CompanyUsersTab
              companyUsers={companyUsers}
              errorMessage={usersErrorMessage}
              isLoading={isUsersLoading}
              onPageChange={(page) => fetchCompanyUsers(currentCompanyId, page)}
              onNavigate={onNavigate}
            />
          ) : null}

          {activeTab === 'chats' ? (
            <CompanyChatsTab
              chats={companyChats}
              companyId={currentCompanyId}
              errorMessage={chatsErrorMessage}
              isLoading={isChatsLoading}
            />
          ) : null}

          {activeTab === 'files' ? (
            <CompanyFilesTab
              companyFileContexts={companyFileContexts}
              companyFileForm={companyFileForm}
              companyFiles={companyFiles}
              errorMessage={filesErrorMessage}
              downloadingFileId={downloadingCompanyFileId}
              isLoading={isFilesLoading}
              isSaving={isFilesSaving}
              onAdd={openCompanyFileForm}
              onChangeForm={setCompanyFileForm}
              onCloseForm={closeCompanyFileForm}
              onDownload={downloadCompanyFile}
              onSubmitForm={saveCompanyFile}
            />
          ) : null}

          {activeTab === 'delivery-addresses' ? (
            isDeliveryAddressFormRoute ? (
              <CompanyDeliveryAddressFormPage
                companyId={currentCompanyId}
                deliveryAddressForm={deliveryAddressForm}
                errorMessage={deliveryAddressesErrorMessage}
                isLoading={isDeliveryAddressesLoading}
                isSaving={isDeliveryAddressesSaving}
                onChangeForm={setDeliveryAddressForm}
                onCloseForm={closeDeliveryAddressForm}
                onSubmitForm={saveDeliveryAddress}
              />
            ) : (
              <CompanyDeliveryAddressesTab
                companyDeliveryAddresses={companyDeliveryAddresses}
                errorMessage={deliveryAddressesErrorMessage}
                isLoading={isDeliveryAddressesLoading}
                onAdd={() => openDeliveryAddressForm()}
                onEdit={openDeliveryAddressForm}
                onRemove={deleteDeliveryAddress}
              />
            )
          ) : null}

          {activeTab === 'delivery-zones' ? (
            isDeliveryZoneFormRoute ? (
              <CompanyDeliveryZoneFormPage
                availableDeliveryZones={availableDeliveryZones}
                companyDeliveryZones={companyDeliveryZones}
                deliveryZoneForm={deliveryZoneForm}
                deliveryZoneFormEditableZoneIds={deliveryZoneFormEditableZoneIds}
                errorMessage={deliveryZonesErrorMessage}
                isLoading={isDeliveryZonesLoading}
                isSaving={isDeliveryZonesSaving}
                onChangeForm={setDeliveryZoneForm}
                onCloseForm={closeDeliveryZoneForm}
                onSubmitForm={saveDeliveryZone}
              />
            ) : (
              <CompanyDeliveryZonesTab
                companyDeliveryZones={companyDeliveryZones}
                errorMessage={deliveryZonesErrorMessage}
                isLoading={isDeliveryZonesLoading}
                onAdd={() => openDeliveryZoneForm()}
                onEdit={openDeliveryZoneForm}
                onRemove={removeCompanyDeliveryZone}
              />
            )
          ) : null}

          {activeTab === 'prices' ? (
            <CompanyPriceImportsTab
              companyPriceImportFile={companyPriceImportFile}
              companyPriceImportFilters={companyPriceImportFilters}
              errorMessage={priceImportsErrorMessage}
              imports={companyPriceImports}
              isLoading={isPriceImportsLoading}
              isSaving={isPriceImportSaving}
              onChangeFile={changeCompanyPriceImportFile}
              onChangeFilters={setCompanyPriceImportFilters}
              onNavigate={onNavigate}
              onResetFilters={resetCompanyPriceImportFilters}
              onSubmitFilters={submitCompanyPriceImportFilters}
              onSubmitImport={saveCompanyPriceImport}
              onPageChange={(page) => fetchCompanyPriceImports(appliedCompanyPriceImportFilters, page)}
            />
          ) : null}

          {activeTab === 'offers' ? <CompanySupplierOffersTab companyId={currentCompanyId} /> : null}
        </>
      ) : null}

      {companyForm !== null ? (
        <CompanyEditModal
          form={companyForm}
          isSaving={isSaving}
          companyStatuses={companyStatuses}
          onChange={setCompanyForm}
          onClose={closeEditForm}
          onLogoFileChange={setCompanyLogoFile}
          logoFile={companyLogoFile}
          onSubmit={saveCompany}
        />
      ) : null}

      {!isLoading && errorMessage === null && company === null ? <p className="admin-empty">Компания не найдена.</p> : null}
    </section>
  );
}

function CompanyDetailHeader({
  activeTab,
  company,
  onNavigate,
}: {
  activeTab: CompanyDetailTab;
  company: AdminCompany;
  onNavigate: (path: string) => void;
}) {
  return (
    <article className="admin-detail-card admin-company-header admin-company-hero">
      <div className="admin-company-hero__main">
        <span className="admin-company-hero__avatar" aria-hidden="true">{getCompanyInitials(company.company_name)}</span>
        <div className="admin-company-hero__identity">
          <p className="admin-eyebrow">Компания #{company.company_id}</p>
          <h1>{company.company_name}</h1>
          <div className="admin-company-hero__meta">
            <span>ИНН {company.company_inn}</span>
            <span>{company.company_email}</span>
            <span>{company.company_phone}</span>
          </div>
        </div>
        <span className={getCompanyStatusClassName(company.company_status_id)}>{getCompanyStageLabel(company.company_status_id)}</span>
      </div>
      <TabNavigation
        ariaLabel="Разделы компании"
        items={companyDetailTabs.map((item) => ({ title: item.label, value: item.tab }))}
        onChange={(tab) => onNavigate(`/admin/companies/${company.company_id}/${tab}`)}
        value={activeTab}
      />
    </article>
  );
}

function getCompanyInitials(companyName: string): string {
  const meaningfulWords = companyName.replace(/[«»"']/g, '').split(/\s+/).filter((word) => word.length > 2);
  const initials = meaningfulWords.slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');

  return initials || 'К';
}

function getCompanyStageLabel(statusId: number): string {
  if (statusId === COMPANY_STATUS_BLOCKED) {
    return 'Заблокирована';
  }
  if (statusId === COMPANY_STATUS_REJECTED) {
    return 'Отклонена';
  }
  if (statusId === COMPANY_STATUS_ARCHIVED) {
    return 'В архиве';
  }
  if (statusId === COMPANY_STATUS_MODERATION) {
    return 'На проверке';
  }

  if (statusId === COMPANY_STATUS_CONTRACT_SIGNING || statusId === COMPANY_STATUS_SIGNED_SCAN_UPLOADED) {
    return 'Оформление договора';
  }

  if (statusId === COMPANY_STATUS_ORIGINAL_RECEIVED) {
    return 'Документы получены';
  }

  if (statusId === COMPANY_STATUS_APPROVED) {
    return 'Подтверждена';
  }

  return 'Статус уточняется';
}

function CompanyInfoTab({
  agreement,
  company,
  companyStatuses,
  isDownloadingAgreementScan,
  isStatusTransitionSaving,
  onApprove,
  onDownloadAgreementScan,
  onEdit,
  onSignContract,
}: {
  agreement: AdminCompanyAgreement | null;
  company: AdminCompany;
  companyStatuses: AdminDictionaryItem[];
  isDownloadingAgreementScan: boolean;
  isStatusTransitionSaving: boolean;
  onApprove: () => void;
  onDownloadAgreementScan: () => void;
  onEdit: () => void;
  onSignContract: () => void;
}) {
  const agreementGeneratedAt = formatDateTime(agreement?.generated_at ?? '');
  const agreementDownloadedAt = formatDateTime(agreement?.downloaded_at ?? '');
  const agreementSignedScanUploadedAt = formatDateTime(agreement?.signed_scan_uploaded_at ?? '');
  const agreementReviewedAt = formatDateTime(agreement?.reviewed_at ?? '');
  const agreementOriginalReceivedAt = formatDateTime(agreement?.original_received_at ?? '');

  return (
    <>
      <CompanyAccessStatusCard
        agreement={agreement}
        company={company}
        companyStatus={companyStatuses.find((status) => status.id === company.company_status_id) ?? null}
        isSaving={isStatusTransitionSaving}
        onApprove={onApprove}
        onSignContract={onSignContract}
      />
      <DetailCard
        title="Информация"
        eyebrow="Карточка компании"
        actions={<IconButton icon="✎" label="Редактировать информацию" onClick={onEdit} />}
        sections={[
        {
          title: 'Статус и служебные данные',
          fields: [
            ['ID', String(company.company_id)],
            ['Статус', getDictionaryNameById(companyStatuses, company.company_status_id)],
            ['Номер пользователя', String(company.company_created_by)],
            ['Создана', formatDateTime(company.company_created_at)],
            ['Обновлена', formatDateTime(company.company_updated_at)],
          ],
        },
        {
          title: 'Регистрационные реквизиты',
          fields: [
            ['ИНН', company.company_inn],
            ['КПП', company.company_kpp ?? '—'],
            ['ОГРН / ОГРНИП', company.company_ogrn],
            ['Справка из налоговой', company.company_registration_certificate_file_id ? `Файл №${company.company_registration_certificate_file_id}` : 'Не загружена'],
            ['Юридический адрес', company.company_registration_address ?? '—'],
            ['Фактический адрес', company.company_actual_address ?? '—'],
          ],
        },
        {
          title: 'Контакты',
          fields: [
            ['Электронная почта', company.company_email],
            ['Телефон', company.company_phone],
            ['ЭДО', company.company_edo_id ?? '—'],
          ],
        },
        {
          title: 'Банковские реквизиты',
          fields: [
            ['Банк', company.company_bank_name ?? '—'],
            ['Р/С', company.company_rs ?? '—'],
            ['К/С', company.company_ks ?? '—'],
            ['БИК', company.company_bik ?? '—'],
            ['Минимальная сумма заказа', formatOptionalNumber(company.company_default_min_order_sum)],
          ],
        },
        {
          title: 'Модерация',
          fields: [
            ['Причина отказа', company.company_reject_comment ?? '—'],
          ],
        },
        ]}
      />
      {agreement === null ? null : (
        <DetailCard
          title="Договор с платформой"
          eyebrow="Документ"
          actions={agreement.signed_scan_file_id === null ? null : (
            <button
              className="workspace-secondary-button"
              disabled={isDownloadingAgreementScan}
              onClick={onDownloadAgreementScan}
              type="button"
            >
              {isDownloadingAgreementScan ? 'Скачиваем…' : 'Скачать подписанный скан'}
            </button>
          )}
          sections={[
            {
              title: 'Реквизиты и история',
              fields: [
                ['Номер договора', agreement.agreement_number],
                ['Статус', agreement.status_name],
                ['Доступ к кабинету', agreement.has_workspace_access ? 'Открыт' : 'Не открыт'],
                ['Сформирован', agreementGeneratedAt],
                ['Скачан компанией', agreementDownloadedAt],
                ['Подписанный скан', agreement.signed_scan_file_id === null ? 'Не загружен' : 'Загружен'],
                ['Скан загружен', agreementSignedScanUploadedAt],
                ['Скан проверен', agreementReviewedAt],
                ['Оригинал получен', agreementOriginalReceivedAt],
              ],
            },
          ]}
        />
      )}
    </>
  );
}

function CompanyAccessStatusCard({
  agreement,
  company,
  companyStatus,
  isSaving,
  onApprove,
  onSignContract,
}: {
  agreement: AdminCompanyAgreement | null;
  company: AdminCompany;
  companyStatus: AdminDictionaryItem | null;
  isSaving: boolean;
  onApprove: () => void;
  onSignContract: () => void;
}) {
  if (company.company_sale_status_id !== undefined && company.company_status_id !== COMPANY_STATUS_APPROVED && company.company_status_id !== COMPANY_STATUS_MODERATION) {
    return null;
  }
  const statusName = companyStatus?.name ?? agreement?.status_name ?? '';
  const statusDescription = companyStatus?.description ?? agreement?.status_description ?? '';

  if (company.company_status_id === COMPANY_STATUS_MODERATION) {
    return (
      <section className="company-access-status company-access-status_moderation">
        <div>
          <span>Следующий шаг</span>
          <strong>{statusName}</strong>
          <p>{statusDescription}</p>
        </div>
        <button className="workspace-secondary-button" disabled={isSaving} onClick={onApprove} type="button">
          {isSaving ? 'Подтверждаем…' : 'Подтвердить компанию и открыть покупки'}
        </button>
      </section>
    );
  }

  if (
    (company.company_sale_status_id ?? company.company_status_id) === COMPANY_STATUS_CONTRACT_SIGNING
    || (company.company_sale_status_id ?? company.company_status_id) === COMPANY_STATUS_SIGNED_SCAN_UPLOADED
  ) {
    const hasSignedScan = agreement?.status_code === 'signed_scan_uploaded';

    return (
      <section className="company-access-status company-access-status_contract">
        <div>
          <span>Оформление договора для продаж</span>
          <strong>{statusName}</strong>
          <p>{statusDescription}</p>
        </div>
        <button className="workspace-secondary-button" disabled={isSaving || !hasSignedScan} onClick={onSignContract} type="button">
          {isSaving ? 'Открываем продажи…' : 'Принять скан и открыть продажи'}
        </button>
      </section>
    );
  }

  if (
    company.company_status_id === COMPANY_STATUS_APPROVED
    || company.company_status_id === COMPANY_STATUS_ORIGINAL_RECEIVED
  ) {
    return (
      <section className="company-access-status company-access-status_active">
        <div>
          <span>Компания активна</span>
          <strong>{statusName}</strong>
          <p>{statusDescription}</p>
        </div>
      </section>
    );
  }

  return null;
}

function CompanyPriceImportsTab({
  companyPriceImportFile,
  companyPriceImportFilters,
  errorMessage,
  imports,
  isLoading,
  isSaving,
  onChangeFile,
  onChangeFilters,
  onNavigate,
  onResetFilters,
  onSubmitFilters,
  onSubmitImport,
  onPageChange,
}: {
  companyPriceImportFile: File | null;
  companyPriceImportFilters: PriceImportFilters;
  errorMessage: string | null;
  imports: AdminListResult<AdminPriceImport>;
  isLoading: boolean;
  isSaving: boolean;
  onChangeFile: (file: File | null) => void;
  onChangeFilters: (filters: PriceImportFilters) => void;
  onNavigate: (path: string) => void;
  onResetFilters: () => void;
  onSubmitFilters: (event: FormEvent<HTMLFormElement>) => void;
  onSubmitImport: (event: FormEvent<HTMLFormElement>) => void;
  onPageChange: (page: number) => void;
}) {
  const isPriceImportFileTooLarge = companyPriceImportFile !== null
    && companyPriceImportFile.size > PRICE_FILE_MAX_SIZE_BYTES;

  return (
    <section className="admin-detail-card">
      <div className="admin-detail-card__header">
        <div>
          <p className="admin-eyebrow">Прайсы компании</p>
          <h3>Импорты прайсов</h3>
        </div>
      </div>

      <ValidatedForm className="admin-price-upload" onSubmit={onSubmitImport}>
        <div className="admin-price-upload__intro">
          <span className="admin-price-upload__icon" aria-hidden="true">⇧</span>
          <div>
            <strong>Загрузить новый прайс</strong>
            <small>CSV, XLS, XLSX или TXT, не более 10 МБ. После загрузки файл появится в списке и будет отправлен на обработку.</small>
          </div>
        </div>
        <label className="admin-field admin-price-upload__file">
          <span>Файл прайса</span>
          <input
            accept=".csv,.xls,.xlsx,.txt"
            aria-label="Файл прайса"
            key={companyPriceImportFile?.name ?? 'empty-price-import'}
            onChange={(event) => onChangeFile(event.target.files?.[0] ?? null)}
            type="file"
          />
        </label>
        <button className="admin-submit" disabled={companyPriceImportFile === null || isPriceImportFileTooLarge || isSaving} type="submit">
          {isSaving ? 'Загружаем...' : 'Загрузить прайс'}
        </button>
      </ValidatedForm>

      <ValidatedForm className="admin-filter-bar admin-filter-bar_company_prices" onSubmit={onSubmitFilters}>
        <label className="admin-field">
          <span>Статус</span>
          <select
            onChange={(event) => onChangeFilters({ ...companyPriceImportFilters, status_id: event.target.value })}
            value={companyPriceImportFilters.status_id}
          >
            <option value="">Все</option>
            {renderImportStatusOptions()}
          </select>
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={onResetFilters} />
      </ValidatedForm>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {!isLoading ? (
        <div className="admin-table" role="table" aria-label="Импорты прайсов компании">
          <div className="admin-table__head admin-table__row_price_import" role="row">
            <span>Номер</span>
            <span>Файл</span>
            <span>Статус</span>
            <span>Строки</span>
            <span>Результат</span>
            <span>Создан</span>
            <span>Действия</span>
          </div>
          {imports.items.map((item) => (
            <div className="admin-table__row admin-table__row_price_import" key={item.import_id} role="row">
              <strong>{item.import_id}</strong>
              <span>{item.file_public_name ?? item.file_original_name ?? '—'}</span>
              <span>{item.status_name ?? 'Статус уточняется'}</span>
              <span>{item.import_rows_matched}/{item.import_rows_total}</span>
              <span>{formatImportResult(item.import_reject_comment ?? item.import_last_error)}</span>
              <span>{formatDateTime(item.import_created_at)}</span>
              <div className="admin-row-actions">
                <button className="company-request-item__action" type="button" onClick={() => onNavigate(`/admin/imports/${item.import_id}`)}>
                  Открыть
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {!isLoading && imports.items.length === 0 ? <p className="admin-empty">Импорты не найдены.</p> : null}
      <PaginationMeta isLoading={isLoading} meta={imports.meta} onPageChange={onPageChange} />
    </section>
  );
}

function CompanyUsersTab({
  companyUsers,
  errorMessage,
  isLoading,
  onPageChange,
  onNavigate,
}: {
  companyUsers: AdminListResult<AdminCompanyUser>;
  errorMessage: string | null;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onNavigate: (path: string) => void;
}) {
  return (
    <article className="admin-detail-card">
      <div className="admin-detail-card__header">
        <div>
          <p className="admin-eyebrow">Доступ</p>
          <h2>Пользователи и роли</h2>
        </div>
      </div>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      <div className="admin-table" role="table" aria-label="Пользователи компании">
        <div className="admin-table__head admin-table__row_company_user" role="row">
          <span>Номер</span>
          <span>Пользователь</span>
          <span>Электронная почта</span>
          <span>Телефон</span>
          <span>Роль</span>
          <span>Добавлен</span>
          <span />
        </div>
        {companyUsers.items.map((companyUser) => (
          <article className="admin-table__row admin-table__row_company_user" key={companyUser.company_user_id} role="row">
            <span>{companyUser.company_user_user_id}</span>
            <strong>{formatOptionalString(companyUser.user_full_name)}</strong>
            <span>{formatOptionalString(companyUser.user_email)}</span>
            <span>{formatOptionalString(companyUser.user_phone)}</span>
            <span>{formatOptionalString(companyUser.role_name)}</span>
            <span>{formatDateTime(companyUser.company_user_created_at)}</span>
            <IconButton
              icon="↗"
              label="Открыть пользователя"
              onClick={() => onNavigate(`/admin/users/${companyUser.company_user_user_id}`)}
              variant="row"
            />
          </article>
        ))}
      </div>

      {!isLoading && companyUsers.items.length === 0 ? <p className="admin-empty">Пользователи компании не найдены.</p> : null}
      <PaginationMeta isLoading={isLoading} meta={companyUsers.meta} onPageChange={onPageChange} />
    </article>
  );
}


function CompanyFilesTab({
  companyFileContexts,
  companyFileForm,
  companyFiles,
  errorMessage,
  downloadingFileId,
  isLoading,
  isSaving,
  onAdd,
  onChangeForm,
  onCloseForm,
  onDownload,
  onSubmitForm,
}: {
  companyFileContexts: AdminFileContext[];
  companyFileForm: CompanyFileFormState | null;
  companyFiles: AdminCompanyFile[];
  errorMessage: string | null;
  downloadingFileId: number | null;
  isLoading: boolean;
  isSaving: boolean;
  onAdd: () => void;
  onChangeForm: (form: CompanyFileFormState) => void;
  onCloseForm: () => void;
  onDownload: (companyFile: AdminCompanyFile) => void;
  onSubmitForm: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-page">
      <div className="admin-section-heading">
        <div>
          <p className="admin-eyebrow">Документы компании</p>
          <h2>Файлы</h2>
        </div>
        <IconButton icon="+" label="Загрузить файл" onClick={onAdd} variant="primary" />
      </div>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {companyFileForm !== null ? (
        <section className="admin-detail-card">
          <ValidatedForm className="admin-modal__grid" onSubmit={onSubmitForm}>
            <label className="admin-field">
              <span>Контекст</span>
              <select
                onChange={(event) => onChangeForm({ ...companyFileForm, context_code: event.target.value })}
                required
                value={companyFileForm.context_code}
              >
                <option value="" disabled>
                  Выберите контекст
                </option>
                {companyFileContexts.map((context) => (
                  <option key={context.context_id} value={context.context_code}>
                    {context.context_name} · {context.context_is_public ? 'Публичный' : 'Приватный'}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field admin-field_wide">
              <span>Файл</span>
              <input
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                onChange={(event) => {
                  const nextFile = event.target.files?.[0] ?? null;

                  onChangeForm({
                    ...companyFileForm,
                    file: nextFile,
                  });
                }}
                required
                type="file"
              />
            </label>
            <div className="admin-row-actions">
              <IconButton icon="✓" label={isSaving ? 'Загружаем' : 'Сохранить'} type="submit" variant="primary" />
              <IconButton icon="×" label="Отмена" onClick={onCloseForm} variant="ghost" />
            </div>
          </ValidatedForm>
        </section>
      ) : null}

      <div className="admin-table" role="table" aria-label="Файлы компании">
        <div className="admin-table__head admin-table__row_company_document" role="row">
          <span>Номер</span>
          <span>Контекст</span>
          <span>Публичность</span>
          <span>Имя файла</span>
          <span>Размер</span>
          <span>Загружен</span>
          <span>Действия</span>
        </div>
        {companyFiles.map((companyFile) => (
          <article className="admin-table__row admin-table__row_company_document" key={companyFile.link_id} role="row">
            <span>{companyFile.link_file_id}</span>
            <strong>{companyFile.link_context_name}</strong>
            <span className="admin-checkbox-pill">
              {companyFile.link_context_is_public ? 'Публичный' : 'Приватный'}
            </span>
            <span>{companyFile.file.file_public_name ?? companyFile.file.file_original_name}</span>
            <span>{formatFileSize(companyFile.file.file_size)}</span>
            <span>{formatDateTime(companyFile.link_created_at)}</span>
            <div className="admin-row-actions">
              <IconButton
                disabled={downloadingFileId !== null}
                icon="↓"
                label={downloadingFileId === companyFile.link_file_id ? 'Скачиваем' : 'Скачать'}
                onClick={() => onDownload(companyFile)}
                variant="row"
              />
            </div>
          </article>
        ))}
      </div>

      {!isLoading && companyFiles.length === 0 ? <p className="admin-empty">Файлы компании пока не загружены.</p> : null}
    </section>
  );
}

function CompanyDeliveryAddressesTab({
  companyDeliveryAddresses,
  errorMessage,
  isLoading,
  onAdd,
  onEdit,
  onRemove,
}: {
  companyDeliveryAddresses: AdminCompanyDeliveryAddress[];
  errorMessage: string | null;
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (deliveryAddress: AdminCompanyDeliveryAddress) => void;
  onRemove: (deliveryAddress: AdminCompanyDeliveryAddress) => void;
}) {
  return <CompanyDeliveryAddressesPanel companyDeliveryAddresses={companyDeliveryAddresses} errorMessage={errorMessage} isLoading={isLoading} onAdd={onAdd} onEdit={onEdit} onRemove={onRemove} />;
}

function CompanyDeliveryAddressFormPage({
  companyId,
  deliveryAddressForm,
  errorMessage,
  isLoading,
  isSaving,
  onChangeForm,
  onCloseForm,
  onSubmitForm,
}: {
  companyId: number;
  deliveryAddressForm: CompanyDeliveryAddressFormState | null;
  errorMessage: string | null;
  isLoading: boolean;
  isSaving: boolean;
  onChangeForm: (form: CompanyDeliveryAddressFormState) => void;
  onCloseForm: () => void;
  onSubmitForm: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-page">
      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {deliveryAddressForm !== null ? (
        <CompanyDeliveryAddressEditModal
          companyId={companyId}
          form={deliveryAddressForm}
          isSaving={isSaving}
          onChange={onChangeForm}
          onClose={onCloseForm}
          onSubmit={onSubmitForm}
        />
      ) : null}

      {!isLoading && errorMessage === null && deliveryAddressForm === null ? (
        <p className="admin-empty">Адрес доставки не найден.</p>
      ) : null}
    </section>
  );
}

function CompanyDeliveryAddressEditModal({
  companyId,
  form,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}: {
  companyId: number;
  form: CompanyDeliveryAddressFormState;
  isSaving: boolean;
  onChange: (form: CompanyDeliveryAddressFormState) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const [addressSuggestions, setAddressSuggestions] = useState<AdminDeliveryAddressSuggestion[]>([]);
  const [isAddressSuggestionsLoading, setIsAddressSuggestionsLoading] = useState(false);
  const [addressSuggestionsError, setAddressSuggestionsError] = useState<string | null>(null);
  const addressSuggestionsRequestRef = useRef(0);
  const selectedCoords = getDeliveryAddressFormCoords(form);

  useEffect(() => {
    const query = form.address_address.trim();

    if (query.length < 3 || form.address_selection_key !== '') {
      setAddressSuggestions([]);
      setIsAddressSuggestionsLoading(false);
      setAddressSuggestionsError(null);

      return;
    }

    const timeoutId = window.setTimeout(() => {
      const requestId = addressSuggestionsRequestRef.current + 1;
      addressSuggestionsRequestRef.current = requestId;
      setIsAddressSuggestionsLoading(true);
      setAddressSuggestionsError(null);

      adminApi.getCompanyDeliveryAddressSuggestions(companyId, query)
        .then((suggestions) => {
          if (addressSuggestionsRequestRef.current !== requestId) {
            return;
          }

          setAddressSuggestions(suggestions);
        })
        .catch((requestError: Error) => {
          if (addressSuggestionsRequestRef.current !== requestId) {
            return;
          }

          setAddressSuggestions([]);
          setAddressSuggestionsError(requestError.message);
        })
        .finally(() => {
          if (addressSuggestionsRequestRef.current !== requestId) {
            return;
          }

          setIsAddressSuggestionsLoading(false);
        });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [companyId, form]);

  const handleAddressInputChange = (value: string): void => {
    setAddressSuggestionsError(null);
    onChange({
      ...form,
      address_address: value,
      address_lat: '',
      address_lng: '',
      address_selection_key: '',
    });
  };

  const handleAddressSuggestionSelect = (suggestion: AdminDeliveryAddressSuggestion): void => {
    const lat = suggestion.geo_lat;
    const lng = suggestion.geo_lng;

    if (lat === null || lat === undefined || lng === null || lng === undefined) {
      return;
    }

    setAddressSuggestions([]);
    setAddressSuggestionsError(null);
    onChange({
      ...form,
      address_address: suggestion.value,
      address_lat: String(lat),
      address_lng: String(lng),
      address_selection_key: buildDeliveryAddressSelectionKey(suggestion.value, lat, lng),
    });
  };

  return (
    <section className="workspace-section" aria-label="Редактирование адреса доставки">
      <ValidatedForm className="company-create-form company-create-form_page delivery-address-form" onSubmit={onSubmit}>
        <section className="company-form-section">
          <div className="workspace-panel__header workspace-panel__header_page">
            <div>
              <p className="admin-eyebrow">Адрес доставки</p>
              <h2>Параметры адреса</h2>
            </div>
            <button className="company-request-item__action company-request-item__action_secondary" type="button" onClick={onClose}>
              Отмена
            </button>
          </div>

          <label className="auth-field company-form-field_full">
            <span>Название адреса</span>
            <input maxLength={255} onChange={(event) => onChange({ ...form, address_name: event.target.value })} placeholder="Основной склад" required type="text" value={form.address_name} />
          </label>
          <label className="auth-field company-form-field_full">
            <span>Адрес доставки</span>
            <input
              data-selection-valid={form.address_selection_key !== ''}
              data-validation="address-selection"
              onChange={(event) => handleAddressInputChange(event.target.value)}
              placeholder="Москва, ул. Складская, 2"
              required
              type="text"
              value={form.address_address}
            />
          </label>

          <p className="address-suggestion-hint">Начните вводить адрес, затем выберите подходящий вариант из списка.</p>
          {isAddressSuggestionsLoading ? <p className="address-suggestion-status">Ищем адрес...</p> : null}
          {addressSuggestionsError !== null ? <p className="auth-message auth-message_error">{addressSuggestionsError}</p> : null}
          {addressSuggestions.length > 0 ? (
            <div className="address-suggestion-list" role="listbox" aria-label="Подсказки адреса">
              {addressSuggestions.map((suggestion) => {
                const suggestionKey = buildDeliveryAddressSelectionKey(
                  suggestion.value,
                  suggestion.geo_lat ?? null,
                  suggestion.geo_lng ?? null,
                );
                const isDisabled = suggestion.geo_lat === null || suggestion.geo_lat === undefined
                  || suggestion.geo_lng === null || suggestion.geo_lng === undefined;

                return (
                  <button key={suggestionKey === '' ? suggestion.value : suggestionKey} className="address-suggestion-item" disabled={isDisabled} onClick={() => handleAddressSuggestionSelect(suggestion)} type="button">
                    <strong>{suggestion.value}</strong>
                    <span>{suggestion.unrestricted_value}</span>
                    <span>{isDisabled ? 'Этот вариант нельзя выбрать' : 'Нажмите, чтобы выбрать адрес'}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="delivery-address-map-card">
            <div className="delivery-address-map-card__header">
              <span>Точка на карте</span>
              <p>
                {selectedCoords === null
                  ? 'Выберите адрес из списка, чтобы увидеть метку на карте.'
                  : 'Адрес выбран — точка отмечена на карте.'}
              </p>
            </div>
            <DeliveryAddressPreviewMap address={form.address_address} coords={selectedCoords} />
          </div>

          <div className="company-form-grid company-form-grid_two">
            <label className="auth-field">
              <span>Номер ЕГАИС</span>
              <input maxLength={50} onChange={(event) => onChange({ ...form, address_egais_number: event.target.value })} placeholder="При наличии" type="text" value={form.address_egais_number} />
            </label>
            <label className="auth-field">
              <span>Номер Меркурий</span>
              <input maxLength={50} onChange={(event) => onChange({ ...form, address_mercury_number: event.target.value })} placeholder="При наличии" type="text" value={form.address_mercury_number} />
            </label>
          </div>

          <div className="company-page__actions">
            <button className="company-request-item__action company-request-item__action_secondary" type="button" onClick={onClose}>
              Отмена
            </button>
            <button className="admin-submit" disabled={isSaving} type="submit">
              {isSaving ? 'Сохраняем...' : 'Сохранить адрес'}
            </button>
          </div>
        </section>
      </ValidatedForm>
    </section>
  );
}

function CompanyDeliveryZonesTab({
  companyDeliveryZones,
  errorMessage,
  isLoading,
  onAdd,
  onEdit,
  onRemove,
}: {
  companyDeliveryZones: AdminCompanyDeliveryZone[];
  errorMessage: string | null;
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (deliveryZone: AdminCompanyDeliveryZone) => void;
  onRemove: (deliveryZone: AdminCompanyDeliveryZone) => void;
}) {
  return <CompanyDeliveryZonesPanel companyDeliveryZones={companyDeliveryZones} errorMessage={errorMessage} isLoading={isLoading} onAdd={onAdd} onEdit={onEdit} onRemove={onRemove} />;
}

function CompanyDeliveryZoneFormPage({
  availableDeliveryZones,
  companyDeliveryZones,
  deliveryZoneForm,
  deliveryZoneFormEditableZoneIds,
  errorMessage,
  isLoading,
  isSaving,
  onChangeForm,
  onCloseForm,
  onSubmitForm,
}: {
  availableDeliveryZones: AdminDeliveryZone[];
  companyDeliveryZones: AdminCompanyDeliveryZone[];
  deliveryZoneForm: CompanyDeliveryZoneFormState | null;
  deliveryZoneFormEditableZoneIds: number[];
  errorMessage: string | null;
  isLoading: boolean;
  isSaving: boolean;
  onChangeForm: (form: CompanyDeliveryZoneFormState) => void;
  onCloseForm: () => void;
  onSubmitForm: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-page">
      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      {deliveryZoneForm !== null ? (
        <CompanyDeliveryZoneEditModal
          availableDeliveryZones={availableDeliveryZones}
          companyDeliveryZones={companyDeliveryZones}
          editableZoneIds={deliveryZoneFormEditableZoneIds}
          form={deliveryZoneForm}
          isSaving={isSaving}
          onChange={onChangeForm}
          onClose={onCloseForm}
          onSubmit={onSubmitForm}
        />
      ) : null}

      {!isLoading && errorMessage === null && deliveryZoneForm === null ? (
        <p className="admin-empty">Группа зон доставки не найдена.</p>
      ) : null}
    </section>
  );
}

function CompanyDeliveryZoneEditModal({
  availableDeliveryZones,
  companyDeliveryZones,
  editableZoneIds,
  form,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}: {
  availableDeliveryZones: AdminDeliveryZone[];
  companyDeliveryZones: AdminCompanyDeliveryZone[];
  editableZoneIds: number[];
  form: CompanyDeliveryZoneFormState;
  isSaving: boolean;
  onChange: (form: CompanyDeliveryZoneFormState) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const selectedZoneIds = getSelectedZoneIds(form);
  const selectedZoneIdSet = new Set(selectedZoneIds);
  const editableZoneIdSet = new Set(editableZoneIds);
  const usedZoneIds = companyDeliveryZones
    .flatMap((deliveryZone) => deliveryZone.zone_ids)
    .filter((zoneId) => !selectedZoneIdSet.has(zoneId) && !editableZoneIdSet.has(zoneId));
  const selectableZones = availableDeliveryZones.filter((deliveryZone) => {
    return !usedZoneIds.includes(deliveryZone.zone_id)
      || selectedZoneIdSet.has(deliveryZone.zone_id)
      || editableZoneIdSet.has(deliveryZone.zone_id);
  });
  const selectedZones = availableDeliveryZones.filter((deliveryZone) => selectedZoneIdSet.has(deliveryZone.zone_id));

  return (
    <section className="workspace-section" aria-label="Настройка зон доставки компании">
      <ValidatedForm
        className="delivery-zone-editor"
        data-require-zone-selection="true"
        data-selected-zone-count={selectedZones.length}
        onSubmit={onSubmit}
      >
        <div className="workspace-panel__header workspace-panel__header_page">
          <div>
            <p className="admin-eyebrow">Зоны доставки</p>
            <h2>Параметры доставки</h2>
          </div>
          <button className="company-request-item__action company-request-item__action_secondary" type="button" onClick={onClose}>
            Отмена
          </button>
        </div>

        <div className="delivery-zone-editor-layout">
          <section className="delivery-zone-map-panel" aria-label="Выбор зон на карте">
            <div className="delivery-zone-map-panel__header">
              <div>
                <h2>Карта зон</h2>
                <p>Клик по зоне добавляет или убирает ее из выбора. Зеленые зоны будут сохранены.</p>
              </div>
              <span>{selectedZones.length} выбрано</span>
            </div>

            <DeliveryZoneSelectionMap
              onToggleZone={(zoneId) => onChange(toggleDeliveryZoneSelection(form, zoneId))}
              selectableZoneIds={selectableZones.map((deliveryZone) => deliveryZone.zone_id)}
              selectedZoneIds={selectedZoneIds}
              zones={availableDeliveryZones}
            />

            <div className="delivery-zone-option-grid" role="list" aria-label="Список зон доставки">
              {selectableZones.map((deliveryZone) => {
                const isActive = selectedZoneIdSet.has(deliveryZone.zone_id);

                return (
                  <button
                    key={deliveryZone.zone_id}
                    className={`delivery-zone-option${isActive ? ' is-active' : ''}`}
                    type="button"
                    onClick={() => onChange(toggleDeliveryZoneSelection(form, deliveryZone.zone_id))}
                  >
                    <span className="delivery-zone-option__title">{deliveryZone.zone_name}</span>
                    <span className="delivery-zone-option__meta">Зона #{deliveryZone.zone_id}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="delivery-zone-form-panel" aria-label="Параметры доставки">
            <div className="delivery-zone-form-panel__header">
              <h2>Параметры доставки</h2>
              <p>Настройки применятся ко всем выбранным зонам.</p>
            </div>

            <SelectedDeliveryZonesSummary
              onToggleZone={(zoneId) => onChange(toggleDeliveryZoneSelection(form, zoneId))}
              selectedZones={selectedZones}
            />

            <div className="company-form-grid company-form-grid_two">
              <label className="auth-field">
                <span>Название группы</span>
                <input onChange={(event) => onChange({ ...form, group_name: event.target.value })} value={form.group_name} />
              </label>
              <label className="auth-checkbox">
                <input checked={form.is_enabled} onChange={(event) => onChange({ ...form, is_enabled: event.target.checked })} type="checkbox" />
                <span>Группа включена</span>
              </label>
              <label className="auth-field">
                <span>Часовой пояс</span>
                <select onChange={(event) => onChange({ ...form, schedule_timezone: event.target.value })} value={form.schedule_timezone}>
                  {deliveryTimeZones.map((timeZone) => <option key={timeZone} value={timeZone}>{timeZone}</option>)}
                </select>
              </label>
              <label className="auth-field">
                <span>Срок доставки, дней</span>
                <input
                  inputMode="numeric"
                  min="0"
                  onChange={(event) => onChange(setDeliveryZoneFormField(form, 'schedule_delivery_days', event.target.value))}
                  required
                  type="number"
                  value={form.schedule_delivery_days}
                />
              </label>
              <label className="auth-field">
                <span>Отсечка заказа</span>
                <input
                  onChange={(event) => onChange(setDeliveryZoneFormField(form, 'schedule_order_cutoff_time', event.target.value))}
                  type="time"
                  value={form.schedule_order_cutoff_time}
                />
              </label>
            </div>

            <div className="auth-field company-form-field_full">
              <span>Дни доставки</span>
              <div className="admin-weekday-grid">
                {deliveryWeekDays.map((day) => {
                  const isActive = form.schedule_days_of_week.includes(String(day.value));

                  return (
                    <label className={`admin-checkbox-pill${isActive ? ' is-active' : ''}`} key={day.value}>
                      <input
                        checked={isActive}
                        onChange={(event) => onChange(toggleDeliveryWeekDay(form, String(day.value), event.target.checked))}
                        type="checkbox"
                      />
                      <span>{day.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label className="auth-checkbox">
              <input
                checked={form.schedule_term_without_days}
                onChange={(event) => onChange(setDeliveryZoneFormField(form, 'schedule_term_without_days', event.target.checked))}
                type="checkbox"
              />
              <span>Срок доставки указан без учета выбранных дней недели</span>
            </label>

            <div className="company-page__actions delivery-zone-editor__actions">
              <button className="company-request-item__action company-request-item__action_secondary" type="button" onClick={onClose}>
                Отмена
              </button>
              <button className="admin-submit delivery-zone-editor__submit" disabled={isSaving} type="submit">
                {isSaving ? 'Сохраняем...' : 'Сохранить зоны'}
              </button>
            </div>
          </section>
        </div>
      </ValidatedForm>
    </section>
  );
}

function DeliveryAddressPreviewMap({
  address,
  coords,
}: {
  address: string;
  coords: { lat: number; lng: number } | null;
}) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const handleMapReady = useCallback((map: L.Map) => {
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerRef.current;

    if (map === null || layerGroup === null) {
      return;
    }

    layerGroup.clearLayers();

    if (coords === null) {
      map.setView([55.751244, 37.618423], 9);


      return;
    }

    const marker = L.marker([coords.lat, coords.lng], {
      icon: deliveryAddressMarkerIcon,
    });

    if (address.trim() !== '') {
      marker.bindPopup(address);
    }

    marker.addTo(layerGroup);
    map.setView([coords.lat, coords.lng], 14);

  }, [address, coords]);

  return (
    <div className="delivery-address-map-card__map">
      <MapCanvas className="delivery-address-map" onReady={handleMapReady} zoom={9} aria-label="Карта адреса доставки" />
      {coords === null ? (
        <div className="delivery-address-map__empty">
          <strong>Адрес пока не выбран</strong>
          <span>Выберите подходящий вариант из списка — здесь появится метка.</span>
        </div>
      ) : null}
    </div>
  );
}

function DeliveryZoneSelectionMap({
  onToggleZone,
  selectableZoneIds,
  selectedZoneIds,
  zones,
}: {
  onToggleZone: (zoneId: number) => void;
  selectableZoneIds: number[];
  selectedZoneIds: number[];
  zones: AdminDeliveryZone[];
}) {
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const viewBoundsKeyRef = useRef<string | null>(null);

  const handleMapReady = useCallback((map: L.Map) => {
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerRef.current;

    if (map === null || layerGroup === null) {
      return;
    }

    layerGroup.clearLayers();

    const selectableZoneIdSet = new Set(selectableZoneIds);
    const selectedZoneIdSet = new Set(selectedZoneIds);
    const boundsList: L.LatLngBounds[] = [];
    const selectedBoundsList: L.LatLngBounds[] = [];

    zones.forEach((zone) => {
      const geoJson = normalizeGeoJson(zone.zone_coords);

      if (geoJson === null) {
        return;
      }

      const isSelected = selectedZoneIdSet.has(zone.zone_id);
      const isSelectable = selectableZoneIdSet.has(zone.zone_id);
      const geoJsonLayer = L.geoJSON(geoJson, {
        style: {
          className: isSelected ? 'delivery-zone-map-shape_selected' : 'delivery-zone-map-shape',
          color: isSelected ? '#15803d' : isSelectable ? zone.zone_stroke_color : '#94a3b8',
          fillColor: isSelected ? '#86efac' : isSelectable ? zone.zone_fill_color : '#cbd5e1',
          fillOpacity: isSelected ? 0.46 : isSelectable ? 0.22 : 0.1,
          opacity: isSelectable ? 0.98 : 0.42,
          weight: isSelected ? 5 : 2,
        },
        onEachFeature: (_feature, layer) => {
          const zoneLabel = zone.zone_region_name ? `${zone.zone_name} · ${zone.zone_region_name}` : zone.zone_name;
          const tooltipParts = [zoneLabel];

          if (isSelected) {
            tooltipParts.push('выбрана');
          } else if (!isSelectable) {
            tooltipParts.push('уже подключена');
          }

          layer.bindTooltip(tooltipParts.join(' • '), { sticky: true });

          if (isSelectable) {
            layer.on('click', (event: L.LeafletMouseEvent) => {
              L.DomEvent.stopPropagation(event.originalEvent);
              onToggleZone(zone.zone_id);
            });
          }
        },
      });
      geoJsonLayer.addTo(layerGroup);

      if (isSelected) {
        geoJsonLayer.bringToFront();
      }

      const bounds = geoJsonLayer.getBounds();

      if (bounds.isValid()) {
        boundsList.push(bounds);

        if (isSelected) {
          selectedBoundsList.push(bounds);
        }
      }
    });

    const visibleBounds = selectedBoundsList.length > 0 ? selectedBoundsList : boundsList;

    if (visibleBounds.length === 0) {
      if (viewBoundsKeyRef.current === null) {
        map.setView([55.751244, 37.618423], 8);
      }

      return;
    }

    const mapBounds = visibleBounds.reduce((currentBounds, nextBounds) => currentBounds.extend(nextBounds), visibleBounds[0]);
    const nextViewBoundsKey = buildViewBoundsKey(visibleBounds, selectableZoneIds, zones);

    if (viewBoundsKeyRef.current === nextViewBoundsKey) {
      return;
    }

    viewBoundsKeyRef.current = nextViewBoundsKey;
    map.fitBounds(mapBounds, {
      maxZoom: 11,
      padding: [24, 24],
    });

  }, [onToggleZone, selectableZoneIds, selectedZoneIds, zones]);

  return <MapCanvas className="delivery-zone-map" onReady={handleMapReady} scrollWheelZoom aria-label="Карта выбора зон доставки" />;
}

function SelectedDeliveryZonesSummary({
  onToggleZone,
  selectedZones,
}: {
  onToggleZone: (zoneId: number) => void;
  selectedZones: AdminDeliveryZone[];
}) {
  if (selectedZones.length === 0) {
    return (
      <p className="delivery-zone-selection-hint">Выберите регионы доставки для создания зоны.</p>
    );
  }

  return (
    <div className="delivery-zone-selected-card">
      <span>Выбранные регионы доставки</span>
      <strong>
        {formatRussianCount(selectedZones.length, {
          one: 'зона',
          few: 'зоны',
          many: 'зон',
        })}
      </strong>
      <div className="delivery-zone-selected-list">
        {selectedZones.map((zone) => (
          <div className="delivery-zone-selected-list__item" key={zone.zone_id}>
            <div className="delivery-zone-selected-list__content">
              <b>{zone.zone_name}</b>
              <small>Зона #{zone.zone_id}</small>
            </div>
            <button
              aria-label={`Отключить ${zone.zone_name}`}
              className="delivery-zone-selected-list__remove"
              onClick={() => onToggleZone(zone.zone_id)}
              type="button"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompanyEditModal({
  companyStatuses,
  form,
  isSaving,
  logoFile,
  onChange,
  onClose,
  onLogoFileChange,
  onSubmit,
}: {
  companyStatuses: AdminDictionaryItem[];
  form: CompanyEditFormState;
  isSaving: boolean;
  logoFile: File | null;
  onChange: (form: CompanyEditFormState) => void;
  onClose: () => void;
  onLogoFileChange: (file: File | null) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-modal" aria-label="Редактирование компании" role="dialog" aria-modal="true">
      <ValidatedForm className="admin-modal__panel" onSubmit={onSubmit}>
        <div className="admin-modal__header">
          <div>
            <p className="admin-eyebrow">Компания</p>
            <h2>Редактирование</h2>
          </div>
          <IconButton icon="×" label="Закрыть" onClick={onClose} variant="row" />
        </div>

        <div className="admin-modal__sections">
          <fieldset className="admin-filter-group">
            <legend>Основное</legend>
            <div className="admin-modal__grid">
              <label className="admin-field admin-field_wide">
                <span>Название</span>
                <input
                  maxLength={500}
                  minLength={2}
                  onChange={(event) => onChange({ ...form, company_name: event.target.value })}
                  required
                  type="text"
                  value={form.company_name}
                />
              </label>
              <label className="admin-field admin-field_wide">
                <span>Логотип</span>
                <input
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(event) => onLogoFileChange(event.target.files?.[0] ?? null)}
                  type="file"
                />
                <small className="company-file-meta">
                  {logoFile !== null
                    ? `Новый файл: ${logoFile.name}`
                    : form.company_logo_file_id !== null
                      ? `Текущий логотип: файл #${form.company_logo_file_id}`
                      : 'Логотип пока не загружен.'}
                </small>
              </label>
              <label className="admin-field">
                <span>Статус</span>
                <select
                  aria-label="Статус компании"
                  id="company-edit-status"
                  onChange={(event) => onChange({ ...form, company_status_id: event.target.value })}
                  required
                  value={form.company_status_id}
                >
                  {renderDictionaryOptions(companyStatuses)}
                </select>
              </label>
              <label className="admin-field">
                <span>Минимальная сумма заказа</span>
                <input
                  min="0"
                  onChange={(event) => onChange({ ...form, company_default_min_order_sum: event.target.value })}
                  step="0.01"
                  type="number"
                  value={form.company_default_min_order_sum}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Регистрационные данные</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>ИНН</span>
                <input
                  inputMode="numeric"
                  maxLength={12}
                  minLength={10}
                  onChange={(event) => onChange({ ...form, company_inn: event.target.value })}
                  pattern="\d{10}|\d{12}"
                  required
                  type="text"
                  value={form.company_inn}
                />
              </label>
              <label className="admin-field">
                <span>КПП</span>
                <input
                  inputMode="numeric"
                  maxLength={9}
                  onChange={(event) => onChange({ ...form, company_kpp: event.target.value })}
                  pattern="\d{9}"
                  type="text"
                  value={form.company_kpp}
                />
              </label>
              <label className="admin-field">
                <span>ОГРН / ОГРНИП</span>
                <input
                  inputMode="numeric"
                  maxLength={15}
                  minLength={13}
                  onChange={(event) => onChange({ ...form, company_ogrn: event.target.value })}
                  pattern="\d{13}|\d{15}"
                  required
                  type="text"
                  value={form.company_ogrn}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Адреса</legend>
            <div className="admin-modal__grid">
              <label className="admin-field admin-field_wide">
                <span>Юридический адрес</span>
                <input
                  onChange={(event) => onChange({ ...form, company_registration_address: event.target.value })}
                  required
                  type="text"
                  value={form.company_registration_address}
                />
              </label>
              <label className="admin-field admin-field_wide">
                <span>Фактический адрес</span>
                <input
                  onChange={(event) => onChange({ ...form, company_actual_address: event.target.value })}
                  required
                  type="text"
                  value={form.company_actual_address}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Банк</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>Р/С</span>
                <input
                  inputMode="numeric"
                  maxLength={20}
                  minLength={20}
                  onChange={(event) => onChange({ ...form, company_rs: event.target.value })}
                  pattern="\d{20}"
                  required
                  type="text"
                  value={form.company_rs}
                />
              </label>
              <label className="admin-field">
                <span>К/С</span>
                <input
                  inputMode="numeric"
                  maxLength={20}
                  minLength={20}
                  onChange={(event) => onChange({ ...form, company_ks: event.target.value })}
                  pattern="\d{20}"
                  required
                  type="text"
                  value={form.company_ks}
                />
              </label>
              <label className="admin-field">
                <span>БИК</span>
                <input
                  inputMode="numeric"
                  maxLength={9}
                  minLength={9}
                  onChange={(event) => onChange({ ...form, company_bik: event.target.value })}
                  pattern="\d{9}"
                  required
                  type="text"
                  value={form.company_bik}
                />
              </label>
              <label className="admin-field">
                <span>Банк</span>
                <input
                  onChange={(event) => onChange({ ...form, company_bank_name: event.target.value })}
                  required
                  type="text"
                  value={form.company_bank_name}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Контакты</legend>
            <div className="admin-modal__grid">
              <label className="admin-field">
                <span>Телефон</span>
                <PhoneInput
                  inputMode="tel"
                  onValueChange={(value) => onChange({ ...form, company_phone: value })}
                  required
                  value={form.company_phone}
                />
              </label>
              <label className="admin-field">
                <span>Электронная почта</span>
                <input
                  onChange={(event) => onChange({ ...form, company_email: event.target.value })}
                  required
                  type="email"
                  value={form.company_email}
                />
              </label>
              <label className="admin-field">
                <span>ЭДО</span>
                <input
                  onChange={(event) => onChange({ ...form, company_edo_id: event.target.value })}
                  type="text"
                  value={form.company_edo_id}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-filter-group">
            <legend>Модерация</legend>
            <div className="admin-modal__grid">
              <label className="admin-field admin-field_wide">
                <span>Причина отказа</span>
                <input
                  onChange={(event) => onChange({ ...form, company_reject_comment: event.target.value })}
                  type="text"
                  value={form.company_reject_comment}
                />
              </label>
            </div>
          </fieldset>
        </div>

        <div className="admin-modal__actions">
          <IconButton icon="✓" label={isSaving ? 'Сохраняем' : 'Сохранить'} type="submit" variant="primary" />
        </div>
      </ValidatedForm>
    </section>
  );
}

interface DetailSection {
  title: string;
  fields: Array<[string, string]>;
}

function DetailCard({
  actions,
  eyebrow,
  sections,
  title,
}: {
  actions?: ReactNode;
  eyebrow: string;
  sections: DetailSection[];
  title: string;
}) {
  return (
    <article className="admin-detail-card">
      <div className="admin-detail-card__header">
        <div>
          <p className="admin-eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        {actions ?? null}
      </div>
      <div className="admin-detail-sections">
        {sections.map((section) => (
          <section className="admin-detail-section" key={section.title}>
            <h3>{section.title}</h3>
            <dl className="admin-detail-grid">
              {section.fields.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </article>
  );
}

function buildUserListParams(filters: UserFilters, page = 1): AdminListParams {
  return {
    page,
    per_page: 20,
    user_id: normalizeOptionalNumber(filters.user_id),
    user_email: filters.user_email.trim(),
    user_full_name: filters.user_full_name.trim(),
    user_phone: filters.user_phone.trim(),
    user_email_confirmed: normalizeOptionalBoolean(filters.user_email_confirmed),
    status_id: normalizeOptionalNumber(filters.status_id),
  };
}

function buildCompanyListParams(filters: CompanyFilters, quickFilter: CompanyQuickFilter = 'all', page = 1): AdminListParams {
  const params: AdminListParams = {
    page,
    per_page: 20,
    company_id: normalizeOptionalNumber(filters.company_id),
    company_name: filters.company_name.trim(),
    company_registration_address: filters.company_registration_address.trim(),
    company_actual_address: filters.company_actual_address.trim(),
    company_inn: filters.company_inn.trim(),
    company_kpp: filters.company_kpp.trim(),
    company_ogrn: filters.company_ogrn.trim(),
    company_rs: filters.company_rs.trim(),
    company_ks: filters.company_ks.trim(),
    company_bik: filters.company_bik.trim(),
    company_bank_name: filters.company_bank_name.trim(),
    company_phone: filters.company_phone.trim(),
    company_email: filters.company_email.trim(),
    company_edo_id: filters.company_edo_id.trim(),
    company_reject_comment: filters.company_reject_comment.trim(),
    company_created_by: normalizeOptionalNumber(filters.company_created_by),
    status_id: normalizeOptionalNumber(filters.status_id),
  };

  if (quickFilter !== 'all') {
    params.workflow_status = quickFilter;
  }

  return params;
}

function buildDeliveryZoneListParams(filters: DeliveryZoneFilters) {
  return {
    page: 1,
    per_page: 200,
    search: filters.search.trim(),
    zone_id: normalizeOptionalNumber(filters.zone_id),
  };
}

function buildCatalogCategoryListParams(filters: CatalogCategoryFilters) {
  return {
    page: 1,
    per_page: 200,
    search: filters.search.trim(),
    category_id: normalizeOptionalNumber(filters.category_id),
  };
}

function buildCatalogProductListParams(filters: CatalogProductFilters) {
  return {
    page: 1,
    per_page: 50,
    search: filters.search.trim(),
    product_id: normalizeOptionalNumber(filters.product_id),
    product_category_id: normalizeOptionalNumber(filters.product_category_id),
    product_status_id: normalizeOptionalNumber(filters.product_status_id),
  };
}

function buildPriceImportListParams(filters: PriceImportFilters, page = 1): AdminPriceImportListParams {
  return {
    page,
    per_page: 20,
    company_id: normalizeOptionalNumber(filters.company_id),
    company_name: filters.company_name.trim(),
    status_id: normalizeOptionalNumber(filters.status_id),
  };
}

function userToEditForm(user: AdminUser): UserEditFormState {
  return {
    user_email: user.user_email,
    user_full_name: user.user_full_name ?? '',
    user_phone: user.user_phone ?? '',
    user_status_id: String(user.user_status_id),
    user_email_confirmed: user.user_email_confirmed ? '1' : '0',
  };
}

function createUserUpdatePayload(form: UserEditFormState): AdminUserUpdateRequest {
  return {
    user_email: form.user_email.trim(),
    user_full_name: normalizeOptionalStringInput(form.user_full_name),
    user_phone: normalizeOptionalStringInput(form.user_phone),
    user_status_id: Number(form.user_status_id),
    user_email_confirmed: form.user_email_confirmed === '1',
  };
}

function companyToEditForm(company: AdminCompany): CompanyEditFormState {
  return {
    company_name: company.company_name,
    company_logo_file_id: company.company_logo_file_id ?? null,
    company_status_id: String(company.company_status_id),
    company_registration_address: company.company_registration_address ?? '',
    company_actual_address: company.company_actual_address ?? '',
    company_inn: company.company_inn,
    company_kpp: company.company_kpp ?? '',
    company_ogrn: company.company_ogrn,
    company_rs: company.company_rs ?? '',
    company_ks: company.company_ks ?? '',
    company_bik: company.company_bik ?? '',
    company_bank_name: company.company_bank_name ?? '',
    company_phone: company.company_phone,
    company_email: company.company_email,
    company_edo_id: company.company_edo_id ?? '',
    company_default_min_order_sum: formatOptionalFormNumber(company.company_default_min_order_sum),
    company_reject_comment: company.company_reject_comment ?? '',
  };
}

function companyDeliveryAddressToForm(deliveryAddress: AdminCompanyDeliveryAddress): CompanyDeliveryAddressFormState {
  return {
    address_name: deliveryAddress.address_name,
    address_address: deliveryAddress.address_address,
    address_lat: deliveryAddress.address_coords === null || deliveryAddress.address_coords === undefined
      ? ''
      : String(deliveryAddress.address_coords.lat),
    address_lng: deliveryAddress.address_coords === null || deliveryAddress.address_coords === undefined
      ? ''
      : String(deliveryAddress.address_coords.lng),
    address_selection_key: buildDeliveryAddressSelectionKey(
      deliveryAddress.address_address,
      deliveryAddress.address_coords === null || deliveryAddress.address_coords === undefined
        ? null
        : deliveryAddress.address_coords.lat,
      deliveryAddress.address_coords === null || deliveryAddress.address_coords === undefined
        ? null
        : deliveryAddress.address_coords.lng,
    ),
    address_egais_number: deliveryAddress.address_egais_number ?? '',
    address_mercury_number: deliveryAddress.address_mercury_number ?? '',
  };
}

function createCompanyDeliveryAddressPayload(form: CompanyDeliveryAddressFormState): AdminCompanyDeliveryAddressSaveRequest | null {
  const addressName = form.address_name.trim();
  const addressText = form.address_address.trim();
  const addressLat = form.address_lat.trim();
  const addressLng = form.address_lng.trim();

  if (addressName === '' || addressText === '' || form.address_selection_key === '') {
    return null;
  }

  if ((addressLat === '' && addressLng !== '') || (addressLat !== '' && addressLng === '')) {
    return null;
  }

  const hasCoords = addressLat !== '' && addressLng !== '';
  const lat = Number(addressLat);
  const lng = Number(addressLng);

  if (hasCoords && (Number.isNaN(lat) || Number.isNaN(lng))) {
    return null;
  }

  return {
    address_name: addressName,
    address_address: addressText,
    address_coords: hasCoords ? { lat, lng } : null,
    address_egais_number: normalizeOptionalStringInput(form.address_egais_number),
    address_mercury_number: normalizeOptionalStringInput(form.address_mercury_number),
  };
}

function buildDeliveryAddressSelectionKey(address: string, lat: number | string | null, lng: number | string | null): string {
  if (address === '' || lat === null || lng === null || lat === '' || lng === '') {
    return '';
  }

  return `${address}:${lat}:${lng}`;
}

function getDeliveryAddressFormCoords(form: CompanyDeliveryAddressFormState | null): { lat: number; lng: number } | null {
  if (form === null) {
    return null;
  }

  const latValue = form.address_lat.trim();
  const lngValue = form.address_lng.trim();

  if (latValue === '' || lngValue === '') {
    return null;
  }

  const lat = Number(latValue);
  const lng = Number(lngValue);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return { lat, lng };
}

function replaceCompanyDeliveryAddress(
  deliveryAddresses: AdminCompanyDeliveryAddress[],
  nextDeliveryAddress: AdminCompanyDeliveryAddress,
): AdminCompanyDeliveryAddress[] {
  return deliveryAddresses.map((deliveryAddress) => {
    if (deliveryAddress.address_id !== nextDeliveryAddress.address_id) {
      return deliveryAddress;
    }

    return nextDeliveryAddress;
  });
}

function deliveryZoneToForm(deliveryZone: AdminDeliveryZone): DeliveryZoneFormState {
  return {
    zone_region_id: formatOptionalFormNumber(deliveryZone.zone_region_id),
    zone_name: deliveryZone.zone_name,
    zone_description: deliveryZone.zone_description ?? '',
    zone_coords: formatGeoJsonFormValue(deliveryZone.zone_coords),
    zone_fill_color: deliveryZone.zone_fill_color,
    zone_stroke_color: deliveryZone.zone_stroke_color,
  };
}

function deliveryZoneFormPreview(form: DeliveryZoneFormState): AdminDeliveryZone {
  let zoneCoords: unknown = null;

  try {
    zoneCoords = parseGeoJsonInput(form.zone_coords);
  } catch {
    zoneCoords = null;
  }

  return {
    zone_id: 0,
    zone_region_id: normalizeOptionalNumber(form.zone_region_id),
    zone_name: form.zone_name.trim() || 'Новая зона',
    zone_description: normalizeOptionalStringInput(form.zone_description),
    zone_coords: zoneCoords,
    zone_fill_color: form.zone_fill_color,
    zone_stroke_color: form.zone_stroke_color,
    zone_created_at: '',
    zone_updated_at: '',
  };
}

function createDeliveryZonePayload(form: DeliveryZoneFormState): AdminDeliveryZoneCreateRequest | AdminDeliveryZoneUpdateRequest {
  return {
    zone_region_id: normalizeOptionalNumber(form.zone_region_id),
    zone_name: form.zone_name.trim(),
    zone_description: normalizeOptionalStringInput(form.zone_description),
    zone_coords: parseGeoJsonInput(form.zone_coords),
    zone_fill_color: form.zone_fill_color,
    zone_stroke_color: form.zone_stroke_color,
  };
}

function formatGeoJsonFormValue(value: unknown): string {
  const geoJson = normalizeGeoJson(value);

  if (geoJson === null) {
    return '';
  }

  return JSON.stringify(geoJson, null, 2);
}

function parseGeoJsonInput(value: string): unknown | null {
  const normalizedValue = value.trim();

  if (normalizedValue === '') {
    return null;
  }

  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(normalizedValue);
  } catch {
    throw new Error('Проверьте формат границ зоны.');
  }

  const geoJson = normalizeGeoJson(parsedValue);

  if (geoJson === null) {
    throw new Error('Границы зоны заполнены неверно.');
  }

  return geoJson;
}

function createCompanyUpdatePayload(form: CompanyEditFormState): AdminCompanyUpdateRequest {
  return {
    company_name: form.company_name.trim(),
    company_logo_file_id: form.company_logo_file_id,
    company_status_id: Number(form.company_status_id),
    company_registration_address: form.company_registration_address.trim(),
    company_actual_address: form.company_actual_address.trim(),
    company_inn: form.company_inn.trim(),
    company_kpp: normalizeOptionalStringInput(form.company_kpp),
    company_ogrn: form.company_ogrn.trim(),
    company_rs: form.company_rs.trim(),
    company_ks: form.company_ks.trim(),
    company_bik: form.company_bik.trim(),
    company_bank_name: form.company_bank_name.trim(),
    company_phone: form.company_phone.trim(),
    company_email: form.company_email.trim(),
    company_edo_id: normalizeOptionalStringInput(form.company_edo_id),
    company_default_min_order_sum: normalizeOptionalNumberInput(form.company_default_min_order_sum),
    company_reject_comment: normalizeOptionalStringInput(form.company_reject_comment),
  };
}

function companyDeliveryZoneToForm(deliveryZone: AdminCompanyDeliveryZone): CompanyDeliveryZoneFormState {
  return {
    zone_ids: deliveryZone.zone_ids.map(String),
    group_name: deliveryZone.group_name ?? '',
    is_enabled: deliveryZone.is_enabled,
    schedule_timezone: deliveryZone.schedule_timezone,
    schedule_delivery_days: String(deliveryZone.schedule_delivery_days),
    schedule_days_of_week: (deliveryZone.schedule_days_of_week ?? []).map(String),
    schedule_order_cutoff_time: deliveryZone.schedule_order_cutoff_time ?? '',
    schedule_term_without_days: deliveryZone.schedule_term_without_days,
  };
}

function companyDeliveryZoneFromForm(
  form: CompanyDeliveryZoneFormState,
  companyDeliveryZones: AdminCompanyDeliveryZone[],
  availableDeliveryZones: AdminDeliveryZone[],
  editableZoneIds: number[],
): AdminCompanyDeliveryZone {
  const normalizedZoneIds = Array.from(
    new Set(
      form.zone_ids
        .map((zoneId) => Number(zoneId))
        .filter((zoneId) => !Number.isNaN(zoneId) && zoneId > 0),
    ),
  ).sort((leftZoneId, rightZoneId) => leftZoneId - rightZoneId);
  const existingDeliveryZone = companyDeliveryZones.find((deliveryZone) => {
    if (editableZoneIds.length > 0) {
      return deliveryZone.zone_ids.length === editableZoneIds.length
        && deliveryZone.zone_ids.every((zoneId, index) => zoneId === editableZoneIds[index]);
    }

    return deliveryZone.zone_ids.length === normalizedZoneIds.length
      && deliveryZone.zone_ids.every((zoneId, index) => zoneId === normalizedZoneIds[index]);
  });
  const zones = normalizedZoneIds
    .map((zoneId) => {
      return availableDeliveryZones.find((deliveryZone) => deliveryZone.zone_id === zoneId)
        ?? existingDeliveryZone?.zones.find((deliveryZone) => deliveryZone.zone_id === zoneId)
        ?? null;
    })
    .filter((deliveryZone): deliveryZone is AdminDeliveryZone => deliveryZone !== null);

  return {
    group_id: existingDeliveryZone?.group_id ?? 0,
    company_id: existingDeliveryZone?.company_id ?? 0,
    group_name: form.group_name.trim() || null,
    is_enabled: form.is_enabled,
    schedule_timezone: form.schedule_timezone,
    zone_ids: normalizedZoneIds,
    zones,
    schedule_delivery_days: Number(form.schedule_delivery_days),
    schedule_days_of_week: normalizeWeekDays(form.schedule_days_of_week),
    schedule_order_cutoff_time: normalizeOptionalStringInput(form.schedule_order_cutoff_time),
    schedule_term_without_days: form.schedule_term_without_days,
  };
}

function createCompanyDeliveryZonesPayload(deliveryZones: AdminCompanyDeliveryZone[], expectedZones: AdminCompanyDeliveryZone[]): AdminCompanyDeliveryZonesUpdateRequest {
  const zones = deliveryZones.map((deliveryZone): AdminCompanyDeliveryZoneItemRequest => ({
    group_id: deliveryZone.group_id > 0 ? deliveryZone.group_id : null,
    group_name: deliveryZone.group_name ?? null,
    is_enabled: deliveryZone.is_enabled,
    schedule_timezone: deliveryZone.schedule_timezone,
    zone_ids: deliveryZone.zone_ids,
    schedule_delivery_days: deliveryZone.schedule_delivery_days,
    schedule_days_of_week: deliveryZone.schedule_days_of_week ?? null,
    schedule_order_cutoff_time: deliveryZone.schedule_order_cutoff_time ?? null,
    schedule_term_without_days: deliveryZone.schedule_term_without_days,
  }));

  return {
    expected_zones: expectedZones,
    zones,
  };
}

function toggleDeliveryWeekDay(
  form: CompanyDeliveryZoneFormState,
  dayOfWeek: string,
  shouldIncludeDay: boolean,
): CompanyDeliveryZoneFormState {
  if (!shouldIncludeDay) {
    const nextDaysOfWeek = form.schedule_days_of_week.filter((value) => value !== dayOfWeek);

    return {
      ...form,
      schedule_days_of_week: nextDaysOfWeek,
    };
  }

  const nextDaysOfWeek = [...form.schedule_days_of_week, dayOfWeek];
  const uniqueDaysOfWeek = Array.from(new Set(nextDaysOfWeek));
  uniqueDaysOfWeek.sort((left, right) => Number(left) - Number(right));

  return {
    ...form,
    schedule_days_of_week: uniqueDaysOfWeek,
  };
}

function getSelectedZoneIds(form: CompanyDeliveryZoneFormState | null): number[] {
  if (form === null) {
    return [];
  }

  return form.zone_ids
    .map((zoneId) => Number(zoneId))
    .filter((zoneId) => !Number.isNaN(zoneId) && zoneId > 0);
}

function setDeliveryZoneFormField<T extends keyof CompanyDeliveryZoneFormState>(
  form: CompanyDeliveryZoneFormState,
  field: T,
  value: CompanyDeliveryZoneFormState[T],
): CompanyDeliveryZoneFormState {
  return {
    ...form,
    [field]: value,
  };
}

function toggleDeliveryZoneSelection(
  form: CompanyDeliveryZoneFormState,
  zoneId: number,
): CompanyDeliveryZoneFormState {
  const zoneIdValue = String(zoneId);
  const isSelected = form.zone_ids.includes(zoneIdValue);
  const nextZoneIds = isSelected
    ? form.zone_ids.filter((selectedZoneId) => selectedZoneId !== zoneIdValue)
    : [...form.zone_ids, zoneIdValue];

  return {
    ...form,
    zone_ids: nextZoneIds,
  };
}

function normalizeWeekDays(values: string[]): number[] | null {
  if (values.length === 0) {
    return null;
  }

  return values.map(Number);
}

function normalizeOptionalNumber(value: string): number | null {
  const normalizedValue = value.trim();

  if (normalizedValue === '') {
    return null;
  }

  return Number(normalizedValue);
}

function normalizeOptionalBoolean(value: string): boolean | null {
  if (value === '1') {
    return true;
  }

  if (value === '0') {
    return false;
  }

  return null;
}

function normalizeOptionalStringInput(value: string): string | null {
  const normalizedValue = value.trim();

  if (normalizedValue === '') {
    return null;
  }

  return normalizedValue;
}

function normalizeOptionalNumberInput(value: string): number | null {
  const normalizedValue = value.trim();

  if (normalizedValue === '') {
    return null;
  }

  return Number(normalizedValue);
}

function formatDateTime(value: string): string {
  if (value === '') {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatOptionalFormNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
}

function formatOptionalNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return String(value);
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return new Intl.NumberFormat('ru-RU', {
    currency: 'RUB',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(value);
}

function formatCatalogQuantity(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 }).format(value);
}

function getFirstCatalogOfferLabel(sort: AdminCatalogViewSort): string {
  if (sort === 'delivery_asc') {
    return 'Самая быстрая доставка';
  }

  if (sort === 'price_desc') {
    return 'Самая высокая цена';
  }

  if (sort === 'delivery_desc') {
    return 'Самая долгая доставка';
  }

  return 'Лучшая цена';
}

function formatOptionalString(value: string | null | undefined): string {
  const normalizedValue = value?.trim() ?? '';

  if (normalizedValue === '') {
    return '—';
  }

  return normalizedValue;
}

function formatImportResult(value: string | null | undefined): string {
  const normalizedValue = value?.trim() ?? '';

  if (normalizedValue === '') {
    return '—';
  }

  const containsTechnicalDetails = /internal server error|failed to fetch|sqlstate|stack trace|exception|undefined|unexpected token|syntaxerror|yii\\|\/var\/|\/app\/|\.php:\d+/i.test(normalizedValue);

  return containsTechnicalDetails
    ? 'Не удалось обработать файл. Проверьте его и загрузите ещё раз.'
    : normalizedValue;
}

function getUserDetailTitle(user: AdminUser): string {
  const fullName = user.user_full_name?.trim() ?? '';

  if (fullName === '') {
    return user.user_email;
  }

  return fullName;
}

function getUserStatusClassName(statusId: number): string {
  return `admin-status admin-status_user_${statusId}`;
}

function getCompanyStatusClassName(statusId: number): string {
  if (statusId === COMPANY_STATUS_BLOCKED) {
    return 'admin-status admin-status_company_3';
  }
  return `admin-status admin-status_company_${statusId}`;
}

function getProductStatusClassName(statusId: number): string {
  return `admin-status admin-status_product_${statusId}`;
}

interface AdminImportStatusOption {
  id: number;
  code: string;
  name: string;
}

interface AdminPriceImportStatusUpdateFormState {
  status_id: string;
  comment: string;
}

interface AdminPriceImportMatchFormState {
  product_id: number;
  match_comment?: string;
}

type AdminPriceImportRowFilter = 'all' | 'requires_match' | 'matched';
type AdminPriceImportTool = 'status' | 'reject' | null;

const IMPORT_STATUS_READY_TO_IMPORT = 4;

interface PriceImportFilters {
  company_id: string;
  company_name: string;
  status_id: string;
}

const manualImportStatusOptions: AdminImportStatusOption[] = [
  { id: 1, code: 'new', name: 'Новая' },
  { id: 3, code: 'awaiting_admin', name: 'Ожидает сопоставления администратором' },
  { id: 10, code: 'admin_matching', name: 'В работе администратором' },
  { id: IMPORT_STATUS_READY_TO_IMPORT, code: 'ready_to_import', name: 'Ожидает обновления цен' },
  { id: 7, code: 'validation_error', name: 'Ошибка валидации' },
  { id: 8, code: 'import_error', name: 'Ошибка импорта' },
  { id: 9, code: 'rejected', name: 'Отклонена' },
];

function resolveImportStatusTone(statusCode?: string): 'danger' | 'neutral' | 'success' | 'warning' {
  if (statusCode === 'imported') {
    return 'success';
  }

  if (['validation_error', 'import_error', 'rejected'].includes(statusCode ?? '')) {
    return 'danger';
  }

  if (['new', 'checking', 'awaiting_admin', 'admin_matching'].includes(statusCode ?? '')) {
    return 'warning';
  }

  return 'neutral';
}

function resolveImportWorkflowStage(statusCode?: string): number {
  if (statusCode === 'imported') {
    return 4;
  }

  if (statusCode === 'ready_to_import' || statusCode === 'importing') {
    return 3;
  }

  if (['awaiting_admin', 'admin_matching', 'validation_error', 'import_error', 'rejected'].includes(statusCode ?? '')) {
    return 2;
  }

  return 1;
}

const defaultPriceImportFilters: PriceImportFilters = {
  company_id: '',
  company_name: '',
  status_id: '',
};

function resolveImportStatusUpdateSelection(currentStatusCode?: string): string {
  const currentOption = manualImportStatusOptions.find((option) => option.code === currentStatusCode);

  if (currentOption !== undefined) {
    return String(currentOption.id);
  }

  return '';
}

function hasDeliveryZoneCoords(deliveryZone: AdminDeliveryZone): boolean {
  if (deliveryZone.zone_has_coords !== undefined) return deliveryZone.zone_has_coords;
  const geoJson = normalizeGeoJson(deliveryZone.zone_coords);

  return geoJson !== null;
}

function normalizeGeoJson(value: unknown): GeoJsonObject | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  if (!('type' in value)) {
    return null;
  }

  return value as GeoJsonObject;
}


function buildViewBoundsKey(
  boundsList: L.LatLngBounds[],
  selectableZoneIds: number[],
  zones: AdminDeliveryZone[],
): string {
  const zoneKey = zones
    .map((zone) => `${zone.zone_id}:${JSON.stringify(zone.zone_coords)}`)
    .join('|');

  return `${zoneKey}__${selectableZoneIds.join(',')}__${boundsList.length}`;
}

export function PriceImportsPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [imports, setImports] = useState<AdminListResult<AdminPriceImport>>({ items: [], meta: {} });
  const [filters, setFilters] = useState<PriceImportFilters>(defaultPriceImportFilters);
  const [appliedFilters, setAppliedFilters] = useState<PriceImportFilters>(defaultPriceImportFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchImports = useCallback((nextFilters: PriceImportFilters, page = 1): void => {
    setIsLoading(true);
    setErrorMessage(null);

    priceImportApi
      .getImports(buildPriceImportListParams(nextFilters, page))
      .then(setImports)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchImports(defaultPriceImportFilters);
  }, [fetchImports]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAppliedFilters(filters);
    fetchImports(filters);
  };

  const resetFilters = (): void => {
    setFilters(defaultPriceImportFilters);
    setAppliedFilters(defaultPriceImportFilters);
    fetchImports(defaultPriceImportFilters);
  };

  return (
    <section className="admin-page">
      <AdminPageHeader
        description="Следите за обработкой прайсов и быстро переходите к строкам, которые требуют сопоставления."
        eyebrow="Обновление цен"
        title="Импорт прайсов"
        total={imports.meta.total}
      />
      <ValidatedForm className="admin-filter-bar admin-filter-bar_imports" onSubmit={handleSubmit}>
        <label className="admin-field">
          <span>Компания</span>
          <input
            onChange={(event) => setFilters({ ...filters, company_name: event.target.value })}
            placeholder="ООО Проверка"
            type="search"
            value={filters.company_name}
          />
        </label>
        <label className="admin-field">
          <span>Статус</span>
          <select
            onChange={(event) => setFilters({ ...filters, status_id: event.target.value })}
            value={filters.status_id}
          >
            <option value="">Все</option>
            {renderImportStatusOptions()}
          </select>
        </label>
        <IconButton icon="⌕" label="Найти" type="submit" variant="primary" />
        <IconButton icon="↺" label="Сбросить" onClick={resetFilters} />
      </ValidatedForm>

      <ListState errorMessage={errorMessage} isLoading={isLoading} />

      <div className="admin-page__body">
        {!isLoading ? (
          <div className="admin-table" role="table" aria-label="Импорты прайсов">
            <div className="admin-table__head admin-table__row_price_import" role="row">
              <span>Номер</span>
              <span>Компания</span>
              <span>Статус</span>
              <span>Строки</span>
              <span>Результат</span>
              <span>Создан</span>
              <span>Действия</span>
            </div>
            {imports.items.map((item) => (
              <div className="admin-table__row admin-table__row_price_import" key={item.import_id} role="row">
                <strong>{item.import_id}</strong>
                <span>{item.company_name ?? 'Компания не указана'}</span>
                <span>{item.status_name ?? 'Статус уточняется'}</span>
                <span>{item.import_rows_matched}/{item.import_rows_total}</span>
                <span>{formatImportResult(item.import_reject_comment ?? item.import_last_error)}</span>
                <span>{formatDateTime(item.import_created_at)}</span>
                <div className="admin-row-actions">
                  <button className="company-request-item__action" type="button" onClick={() => onNavigate(`/admin/imports/${item.import_id}`)}>
                    Открыть
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {!isLoading && imports.items.length === 0 ? <p className="admin-empty">Импорты не найдены.</p> : null}
        <PaginationMeta isLoading={isLoading} meta={imports.meta} onPageChange={(page) => fetchImports(appliedFilters, page)} />
      </div>
    </section>
  );
}

export function PriceImportDetailPage({
  dictionaries,
  entityId,
  onNavigate,
}: {
  dictionaries: AdminBootstrapDictionaries;
  entityId: string;
  onNavigate: (path: string) => void;
}) {
  const importId = Number(entityId);
  const [importItem, setImportItem] = useState<AdminPriceImport | null>(null);
  const [rows, setRows] = useState<AdminPriceImportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [statusUpdateForm, setStatusUpdateForm] = useState<AdminPriceImportStatusUpdateFormState>({
    comment: '',
    status_id: '',
  });
  const [matchForms, setMatchForms] = useState<Record<number, AdminPriceImportMatchFormState>>({});
  const [selectedProducts, setSelectedProducts] = useState<Record<number, SearchableSelectOption | null>>({});
  const [isSavingMatches, setIsSavingMatches] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isStartingProcessing, setIsStartingProcessing] = useState(false);
  const [isApplyingPrices, setIsApplyingPrices] = useState(false);
  const [activeAdminTool, setActiveAdminTool] = useState<AdminPriceImportTool>(null);
  const [rowFilter, setRowFilter] = useState<AdminPriceImportRowFilter>('all');
  const [productCreateForm, setProductCreateForm] = useState<CatalogProductFormState | null>(null);
  const [productCreateCategories, setProductCreateCategories] = useState<AdminCatalogCategory[]>([]);
  const [isProductCategoriesLoading, setIsProductCategoriesLoading] = useState(false);
  const [isProductCreating, setIsProductCreating] = useState(false);
  const [productCreateMessage, setProductCreateMessage] = useState<string | null>(null);

  const loadImport = useCallback((): void => {
    setIsLoading(true);
    setErrorMessage(null);

    Promise.all([
      priceImportApi.getImport(importId),
      priceImportApi.getAllImportRows(importId, {
        match_state: rowFilter === 'all' ? undefined : rowFilter,
      }),
    ])
      .then(([nextImport, nextRows]) => {
        setImportItem(nextImport);
        setRows(nextRows.items);
        setMatchForms((currentForms) => ({
          ...createImportMatchForms(nextRows.items),
          ...currentForms,
        }));
        setSelectedProducts((currentProducts) => ({
          ...createImportSelectedProducts(nextRows.items),
          ...currentProducts,
        }));
        setStatusUpdateForm((currentState) => ({
          comment: currentState.comment,
          status_id: resolveImportStatusUpdateSelection(nextImport.status_code),
        }));
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [importId, rowFilter]);

  useEffect(() => {
    loadImport();
  }, [loadImport]);

  const updateMatchForm = (rowId: number, patch: Partial<AdminPriceImportMatchFormState>): void => {
    setMatchForms((currentState) => ({
      ...currentState,
      [rowId]: {
        ...currentState[rowId],
        product_id: currentState[rowId]?.product_id ?? 0,
        match_comment: currentState[rowId]?.match_comment ?? '',
        ...patch,
      },
    }));
  };

  const selectProduct = (rowId: number, option: SearchableSelectOption | null): void => {
    setSelectedProducts((currentState) => ({ ...currentState, [rowId]: option }));
    updateMatchForm(rowId, { product_id: option === null ? 0 : Number(option.value) });
  };

  const searchCatalogProducts = (query: string): Promise<SearchableSelectOption[]> => {
    return adminApi
      .getCatalogProducts({ page: 1, per_page: 20, search: query })
      .then((result) => result.items.map(catalogProductToSelectOption));
  };

  const submitMatches = (): void => {
    if (importItem?.status_code !== 'admin_matching') {
      setErrorMessage('Сначала переведите импорт в статус «В работе администратором».');

      return;
    }

    const matches = rows.flatMap((row) => {
      const matchForm = matchForms[row.row_id];
      const productId = matchForm?.product_id ?? 0;

      if (productId < 1) {
        return [];
      }

      return [{
        match_comment: matchForm?.match_comment,
        product_id: productId,
        row_id: row.row_id,
      }];
    });

    if (matches.length === 0) {
      setErrorMessage('Выберите хотя бы один товар для сопоставления.');

      return;
    }

    setIsSavingMatches(true);
    setErrorMessage(null);

    priceImportApi
      .saveMatches(importId, { matches })
      .then(() => {
        loadImport();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSavingMatches(false);
      });
  };

  const openProductCreateModal = (): void => {
    setProductCreateMessage(null);
    setErrorMessage(null);
    setProductCreateForm(defaultCatalogProductForm(dictionaries));
    setIsProductCategoriesLoading(true);

    adminApi
      .getCatalogCategories({ page: 1, per_page: 200 })
      .then((result) => {
        setProductCreateCategories(result.items);
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsProductCategoriesLoading(false);
      });
  };

  const submitProductCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (productCreateForm === null) {
      return;
    }

    const payload = createCatalogProductCreatePayload(productCreateForm);

    if (payload.product_name === '' || payload.product_unit_id < 1 || payload.product_status_id < 1 || (payload.product_category_id ?? 0) < 1) {
      setErrorMessage('Заполните название, категорию, единицу измерения и статус товара.');

      return;
    }

    setIsProductCreating(true);
    setErrorMessage(null);

    adminApi
      .createCatalogProduct(payload)
      .then((createdProduct) => {
        setProductCreateForm(null);
        setProductCreateMessage(
          `Товар «${createdProduct.product_name}» создан. Артикул: ${createdProduct.product_sku}. Найдите и выберите его в строке импорта.`,
        );
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsProductCreating(false);
      });
  };

  const submitReject = (): void => {
    if (rejectComment.trim() === '') {
      setErrorMessage('Комментарий для отклонения обязателен.');

      return;
    }

    setIsRejecting(true);
    setErrorMessage(null);

    priceImportApi
      .rejectImport(importId, {
        comment: rejectComment,
      })
      .then(() => {
        setRejectComment('');
        setActiveAdminTool(null);
        loadImport();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsRejecting(false);
      });
  };

  const requestStatusUpdate = (payload: AdminPriceImportStatusUpdateRequest): void => {
    setIsStatusUpdating(true);
    setErrorMessage(null);

    priceImportApi
      .updateImportStatus(importId, payload)
      .then(() => {
        setStatusUpdateForm({
          comment: '',
          status_id: String(payload.status_id),
        });
        setActiveAdminTool(null);
        loadImport();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsStatusUpdating(false);
      });
  };

  const submitStatusUpdate = (): void => {
    const statusId = Number(statusUpdateForm.status_id);

    if (Number.isNaN(statusId) || statusId < 1) {
      setErrorMessage('Выберите новый статус импорта.');

      return;
    }

    const payload: AdminPriceImportStatusUpdateRequest = {
      status_id: statusId,
    };
    const comment = statusUpdateForm.comment.trim();

    if (comment !== '') {
      payload.comment = comment;
    }

    requestStatusUpdate(payload);
  };

  const submitReadyToImport = (): void => {
    if (importItem?.status_code !== 'admin_matching') {
      setErrorMessage('Сначала возьмите прайс в работу администратора.');

      return;
    }

    if (importItem.import_rows_unmatched > 0) {
      setErrorMessage('Перед обновлением цен сопоставьте все позиции прайса.');

      return;
    }

    requestStatusUpdate({
      status_id: IMPORT_STATUS_READY_TO_IMPORT,
      comment: 'Прайс проверен администратором и передан на обновление цен.',
    });
  };

  const processImport = (): void => {
    if (importItem?.status_code !== 'new') {
      setErrorMessage('Запустить обработку можно только для нового прайса.');

      return;
    }

    setIsStartingProcessing(true);
    setErrorMessage(null);

    priceImportApi
      .processImport(importId)
      .then((processedImport) => {
        setImportItem(processedImport);
        loadImport();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsStartingProcessing(false);
      });
  };

  const applyImportPrices = (): void => {
    if (importItem?.status_code !== 'ready_to_import') {
      setErrorMessage('Применить цены можно только после проверки и подтверждения прайса.');

      return;
    }

    setIsApplyingPrices(true);
    setErrorMessage(null);

    priceImportApi
      .applyImportPrices(importId)
      .then((appliedImport) => {
        setImportItem(appliedImport);
        loadImport();
      })
      .catch((error: Error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsApplyingPrices(false);
      });
  };

  const downloadImportFile = (): void => {
    if (importItem === null) {
      return;
    }

    const fileName = importItem.file_public_name ?? importItem.file_original_name ?? `price-import-${importItem.import_id}`;

    priceImportApi
      .downloadImport(importItem.import_id, fileName)
      .catch((error: Error) => {
        setErrorMessage(error.message);
      });
  };

  const isProcessing = importItem !== null && ['new', 'checking'].includes(importItem.status_code ?? '');
  const canStartProcessing = importItem?.status_code === 'new';
  const canApplyPrices = importItem?.status_code === 'ready_to_import';
  const isAdminMatching = importItem?.status_code === 'admin_matching';
  const canEditMatches = isAdminMatching && rowFilter !== 'matched';
  const canSubmitForPriceUpdate = isAdminMatching && importItem.import_rows_unmatched === 0;
  const importStatusTone = resolveImportStatusTone(importItem?.status_code);
  const processedRows = importItem?.import_rows_matched ?? 0;
  const totalRows = importItem?.import_rows_total ?? 0;
  const progressValue = totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0;
  const workflowStage = resolveImportWorkflowStage(importItem?.status_code);
  const hasWorkflowError = ['validation_error', 'import_error', 'rejected'].includes(importItem?.status_code ?? '');
  const isImportCompleted = importItem?.status_code === 'imported';

  return (
    <section className="admin-page admin-import-detail-page">
      <div className="admin-page__body admin-import-detail">
        <header className="admin-import-hero">
          <div className="admin-import-hero__navigation">
            <button className="admin-import-detail__back" type="button" onClick={() => onNavigate('/admin/imports')}>
              <span aria-hidden="true">←</span>
              Импорты
            </button>
            {importItem !== null ? (
              <span className={`admin-import-status admin-import-status_${importStatusTone}`}>
                {importItem.status_name ?? 'Статус уточняется'}
              </span>
            ) : null}
          </div>
          <div className="admin-import-hero__main">
            <span className="admin-import-hero__file-icon" aria-hidden="true">⇅</span>
            <div className="admin-import-hero__identity">
              <p>Прайс-лист поставщика</p>
              <h2>Импорт #{importId}</h2>
              {importItem !== null ? (
                <span>{importItem.file_public_name ?? importItem.file_original_name ?? 'Файл прайса'}</span>
              ) : null}
            </div>
            <button
              className="admin-import-hero__download"
              type="button"
              onClick={downloadImportFile}
              disabled={importItem === null}
            >
              <span aria-hidden="true">↓</span>
              Скачать файл
            </button>
          </div>
        </header>

        {errorMessage ? <p className="admin-import-alert admin-import-alert_error">{errorMessage}</p> : null}
        {isLoading ? <LoadingState ariaLabel="Загрузка импорта" /> : null}

        {importItem !== null ? (
          <article className={`admin-import-workflow${hasWorkflowError ? ' admin-import-workflow_error' : ''}`}>
            <div className="admin-import-workflow__summary">
              <div>
                <span>Прогресс сопоставления</span>
                <strong>{processedRows} из {totalRows}</strong>
              </div>
              <span>{progressValue}%</span>
            </div>
            <div className="admin-import-workflow__progress">
              <div
                aria-label={`Обработано ${progressValue}%`}
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={progressValue}
                className="admin-import-progress"
                role="progressbar"
              >
                <span style={{ width: `${progressValue}%` }} />
              </div>
            </div>
            <ol className="admin-import-workflow__steps" aria-label="Этапы обработки прайса">
              {['Файл получен', 'Проверка товаров', 'Обновление цен', 'Импорт завершён'].map((label, index) => {
                const stepNumber = index + 1;
                const stepState = isImportCompleted
                  ? 'complete'
                  : stepNumber < workflowStage
                  ? 'complete'
                  : stepNumber === workflowStage ? (hasWorkflowError ? 'error' : 'current') : 'pending';

                return (
                  <li
                    aria-current={stepState === 'current' || stepState === 'error' ? 'step' : undefined}
                    className={`admin-import-workflow__step admin-import-workflow__step_${stepState}`}
                    key={label}
                  >
                    <span aria-hidden="true">{stepState === 'complete' ? '✓' : stepNumber}</span>
                    <strong>{label}</strong>
                  </li>
                );
              })}
            </ol>
            <div className="admin-import-workflow__details">
              <dl>
                <div>
                  <dt>Компания</dt>
                  <dd>{importItem.company_name ?? 'Компания не указана'}</dd>
                </div>
                <div>
                  <dt>Загружен</dt>
                  <dd>{formatDateTime(importItem.import_created_at)}</dd>
                </div>
                <div>
                  <dt>Требуют сопоставления</dt>
                  <dd>{importItem.import_rows_unmatched}</dd>
                </div>
              </dl>
              <div className="admin-import-compact-tools">
                {importItem.status_code !== 'imported' ? (
                  <div className="admin-import-admin-tools">
                    <button
                      aria-expanded={activeAdminTool === 'status'}
                      className="admin-import-admin-tools__trigger"
                      type="button"
                      onClick={() => setActiveAdminTool(activeAdminTool === 'status' ? null : 'status')}
                    >
                      Управление статусом
                    </button>
                    {activeAdminTool === 'status' ? <div className="admin-import-admin-tools__body">
                      <label className="admin-field">
                        <span>Новый статус</span>
                        <select
                          onChange={(event) => setStatusUpdateForm({ ...statusUpdateForm, status_id: event.target.value })}
                          value={statusUpdateForm.status_id}
                        >
                          <option value="">Выберите статус</option>
                          {manualImportStatusOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="admin-field">
                        <span>Комментарий</span>
                        <input
                          value={statusUpdateForm.comment}
                          onChange={(event) => setStatusUpdateForm({ ...statusUpdateForm, comment: event.target.value })}
                        />
                      </label>
                      <button className="workspace-secondary-button" type="button" disabled={isStatusUpdating} onClick={submitStatusUpdate}>
                        {isStatusUpdating ? 'Сохраняем…' : 'Сменить статус'}
                      </button>
                    </div> : null}
                  </div>
                ) : null}
                {(['awaiting_admin', 'admin_matching', 'ready_to_import'].includes(importItem.status_code ?? '')) ? (
                  <div className="admin-import-admin-tools admin-import-admin-tools_danger">
                    <button
                      aria-expanded={activeAdminTool === 'reject'}
                      className="admin-import-admin-tools__trigger"
                      type="button"
                      onClick={() => setActiveAdminTool(activeAdminTool === 'reject' ? null : 'reject')}
                    >
                      Отклонить импорт
                    </button>
                    {activeAdminTool === 'reject' ? <div className="admin-import-admin-tools__body admin-import-admin-tools__body_reject">
                      <label className="admin-field">
                        <span>Причина отклонения</span>
                        <input value={rejectComment} onChange={(event) => setRejectComment(event.target.value)} />
                      </label>
                      <button className="workspace-secondary-button" type="button" disabled={isRejecting} onClick={submitReject}>
                        {isRejecting ? 'Отклоняем…' : 'Отклонить'}
                      </button>
                    </div> : null}
                  </div>
                ) : null}
              </div>
            </div>
            {isImportCompleted ? (
              <p className="admin-import-alert admin-import-alert_success admin-import-final-status">
                <strong>Импорт завершён.</strong> Цены из прайса применены к товарным предложениям.
              </p>
            ) : null}
            {importItem.import_reject_comment || importItem.import_last_error ? (
              <p className="admin-import-compact-result">
                {formatImportResult(importItem.import_reject_comment ?? importItem.import_last_error)}
              </p>
            ) : null}
            {isProcessing ? (
              <div className="admin-import-alert admin-import-alert_processing">
                <LoadingState ariaLabel="Прайс обрабатывается" compact />
                <div>
                  <strong>Прайс ожидает проверки файла</strong>
                  <p>Cron проверит файл и определит, требуется ли сопоставление администратором.</p>
                </div>
              </div>
            ) : null}
          </article>
        ) : null}

        {importItem !== null ? (
          <section className="admin-import-manual-actions" aria-label="Ручной запуск обработки прайса">
            <div className="admin-import-manual-actions__intro">
              <span aria-hidden="true">⚙</span>
              <div>
                <strong>Ручная обработка</strong>
                <p>Запустите нужный этап вручную, если фоновая обработка ещё не выполнила его.</p>
              </div>
            </div>
            <div className="admin-import-manual-actions__buttons">
              <button
                className="admin-import-process-button"
                disabled={!canStartProcessing || isStartingProcessing || isApplyingPrices}
                type="button"
                onClick={processImport}
              >
                <span aria-hidden="true">▶</span>
                {isStartingProcessing ? 'Обрабатываем прайс…' : 'Запустить обработку прайса'}
              </button>
              <button
                className="admin-import-apply-prices"
                disabled={!canApplyPrices || isApplyingPrices || isStartingProcessing}
                type="button"
                onClick={applyImportPrices}
              >
                <span aria-hidden="true">✓</span>
                {isApplyingPrices ? 'Применяем цены…' : 'Применить цены из прайса'}
              </button>
            </div>
          </section>
        ) : null}

        <section className="admin-detail-card admin-import-rows" aria-label="Строки импорта">
          <div className="admin-detail-card__header">
            <div>
              <h3>Товары из прайса</h3>
              {!isAdminMatching ? (
                <p>Для редактирования переведите импорт в статус «В работе администратором».</p>
              ) : null}
            </div>
          </div>
          {importItem?.status_code === 'awaiting_admin' ? (
            <p className="admin-import-alert admin-import-alert_processing">
              Импорт ожидает администратора. Переведите его в статус «В работе администратором», чтобы начать сопоставление.
            </p>
          ) : null}
          {productCreateMessage !== null ? (
            <p className="admin-import-alert admin-import-alert_success">{productCreateMessage}</p>
          ) : null}
          <div className="admin-import-action-dock" aria-label="Управление сопоставлениями">
            <div className="admin-import-row-filter" role="group" aria-label="Фильтр строк импорта">
              <button
                aria-pressed={rowFilter === 'all'}
                className={rowFilter === 'all' ? 'is-active' : ''}
                onClick={() => setRowFilter('all')}
                type="button"
              >
                Все <span>{importItem?.import_rows_total ?? 0}</span>
              </button>
              <button
                aria-pressed={rowFilter === 'requires_match'}
                className={rowFilter === 'requires_match' ? 'is-active' : ''}
                onClick={() => setRowFilter('requires_match')}
                type="button"
              >
                Требуют сопоставления <span>{importItem?.import_rows_unmatched ?? 0}</span>
              </button>
              <button
                aria-pressed={rowFilter === 'matched'}
                className={rowFilter === 'matched' ? 'is-active' : ''}
                onClick={() => setRowFilter('matched')}
                type="button"
              >
                Сопоставленные <span>{importItem?.import_rows_matched ?? 0}</span>
              </button>
            </div>
            <div className="admin-import-action-dock__buttons">
              <button
                className="admin-import-action-dock__add"
                disabled={!isAdminMatching || isSavingMatches}
                type="button"
                onClick={openProductCreateModal}
              >
                <span aria-hidden="true">＋</span>
                Добавить товар
              </button>
              <button
                className="admin-import-matches-save"
                disabled={!canEditMatches || isSavingMatches || isStatusUpdating || rows.length === 0}
                type="button"
                onClick={submitMatches}
              >
                <span aria-hidden="true">✓</span>
                {isSavingMatches ? 'Сохраняем…' : 'Сохранить сопоставления'}
              </button>
              {isAdminMatching ? (
                <button
                  className="admin-import-apply-prices"
                  disabled={!canSubmitForPriceUpdate || isSavingMatches || isStatusUpdating}
                  type="button"
                  onClick={submitReadyToImport}
                >
                  <span aria-hidden="true">→</span>
                  {isStatusUpdating ? 'Передаём…' : 'Передать на обновление цен'}
                </button>
              ) : null}
            </div>
          </div>
          {rows.length > 0 ? (
            <div className="admin-import-table-wrap">
              <table className="admin-import-table">
                <thead>
                  <tr>
                    <th scope="col">Строка</th>
                    <th scope="col">Позиция поставщика</th>
                    <th scope="col">Цена и условия</th>
                    <th scope="col">Товар каталога</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const selectedProduct = selectedProducts[row.row_id] ?? null;
                    const supplierProductName = getDistinctSupplierProductName(row);
                    const isRowMatched = row.product_id !== null && row.product_id !== undefined;

                    return (
                      <tr className={isRowMatched ? 'is-matched' : 'is-unmatched'} key={row.row_id}>
                        <td>
                          <strong className="admin-import-table__row-number">#{row.row_number}</strong>
                          <span className={`admin-import-table__status${isRowMatched ? ' is-matched' : ''}`}>
                            {row.row_status_name ?? 'Статус уточняется'}
                          </span>
                        </td>
                        <td>
                          <strong>{row.row_product_name ?? 'Без названия'}</strong>
                          <span>Артикул: {row.row_supplier_sku}</span>
                          {supplierProductName !== null ? <small>{supplierProductName}</small> : null}
                        </td>
                        <td>
                          <strong>{formatMoney(row.row_price)}</strong>
                          <span>Мин. количество: {formatCatalogQuantity(row.row_min_quantity)}</span>
                          <span>Единица измерения: {row.row_unit_name ?? '—'}</span>
                          <span>Срок хранения: {row.row_shelf_life ?? '—'}</span>
                          <span>Комментарий: {row.row_comment ?? '—'}</span>
                        </td>
                        <td className="admin-import-table__product-cell">
                          <AsyncSearchableSelect
                            ariaLabel={`Товар каталога для строки ${row.row_number}`}
                            inputId={`import-row-product-${row.row_id}`}
                            isDisabled={!canEditMatches || isSavingMatches}
                            loadOptions={searchCatalogProducts}
                            onChange={(option) => selectProduct(row.row_id, option)}
                            placeholder={<CatalogProductSearchIcon />}
                            value={selectedProduct}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {rows.length === 0 && !isProcessing && !isLoading ? (
            <div className="admin-import-empty">
              <strong>В этом импорте пока нет строк</strong>
              <p>Если обработка завершена, проверьте сообщение об ошибке или содержимое исходного файла.</p>
            </div>
          ) : null}
        </section>
      </div>
      {productCreateForm !== null ? (
        <ImportProductCreateModal
          categories={productCreateCategories}
          dictionaries={dictionaries}
          form={productCreateForm}
          isCategoriesLoading={isProductCategoriesLoading}
          isSaving={isProductCreating}
          onChange={setProductCreateForm}
          onClose={() => setProductCreateForm(null)}
          onSubmit={submitProductCreate}
        />
      ) : null}
    </section>
  );
}

function createImportMatchForms(
  rows: AdminPriceImportRow[],
): Record<number, AdminPriceImportMatchFormState> {
  return rows.reduce<Record<number, AdminPriceImportMatchFormState>>((forms, row) => {
    forms[row.row_id] = {
      match_comment: row.row_match_comment ?? '',
      product_id: row.product_id ?? 0,
    };

    return forms;
  }, {});
}

function getDistinctSupplierProductName(row: AdminPriceImportRow): string | null {
  const rowProductName = row.row_product_name?.trim() ?? '';
  const supplierProductName = row.supplier_product_name?.trim() ?? '';

  if (supplierProductName === '') {
    return null;
  }

  if (supplierProductName.localeCompare(rowProductName, 'ru', { sensitivity: 'base' }) === 0) {
    return null;
  }

  return supplierProductName;
}

function createImportSelectedProducts(rows: AdminPriceImportRow[]): Record<number, SearchableSelectOption | null> {
  return rows.reduce<Record<number, SearchableSelectOption | null>>((products, row) => {
    products[row.row_id] = row.product_id === null || row.product_id === undefined
      ? null
      : {
        label: [row.product_sku, row.product_name].filter(Boolean).join(' — '),
        value: String(row.product_id),
      };

    return products;
  }, {});
}

function catalogProductToSelectOption(product: AdminCatalogProduct): SearchableSelectOption {
  return {
    label: `${product.product_sku} — ${product.product_name}`,
    value: String(product.product_id),
  };
}

function CatalogProductSearchIcon() {
  return (
    <span className="admin-import-product-search-icon" aria-hidden="true">
      <svg fill="none" viewBox="0 0 24 24">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m15.5 15.5 5 5" />
      </svg>
    </span>
  );
}

function ImportProductCreateModal({
  categories,
  dictionaries,
  form,
  isCategoriesLoading,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}: {
  categories: AdminCatalogCategory[];
  dictionaries: AdminBootstrapDictionaries;
  form: CatalogProductFormState;
  isCategoriesLoading: boolean;
  isSaving: boolean;
  onChange: (form: CatalogProductFormState) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="admin-modal" aria-label="Создание товара" role="dialog" aria-modal="true">
      <ValidatedForm className="admin-modal__panel admin-product-editor" onSubmit={onSubmit}>
        <div className="admin-modal__header">
          <div>
            <p className="admin-eyebrow">Каталог</p>
            <h2>Новый товар</h2>
          </div>
          <IconButton icon="×" label="Закрыть" onClick={onClose} variant="row" />
        </div>

        <div className="admin-product-editor__layout">
          <div className="admin-product-editor__main">
            <fieldset className="admin-form-section">
              <legend>Основная информация</legend>
              <label className="admin-field">
                <span>Название</span>
                <input autoComplete="off" onChange={(event) => onChange({ ...form, product_name: event.target.value })} placeholder="Например, масло подсолнечное 5 л" required type="text" value={form.product_name} />
              </label>
              <label className="admin-field">
                <span>Категория</span>
                <SearchableSelect ariaLabel="Категория" inputId="import-product-category" onChange={(value) => onChange({ ...form, product_category_id: value })} options={catalogCategorySelectOptions(categories)} placeholder={isCategoriesLoading ? 'Загружаем категории…' : 'Выберите категорию'} value={form.product_category_id} />
                <small>Категория определяет место товара в каталоге и доступность в фильтрах.</small>
              </label>
              <label className="admin-field">
                <span>Описание</span>
                <textarea onChange={(event) => onChange({ ...form, product_description: event.target.value })} placeholder="Ключевые характеристики товара" value={form.product_description} />
              </label>
            </fieldset>
          </div>
          <aside className="admin-product-editor__aside">
            <fieldset className="admin-form-section">
              <legend>Продажа</legend>
              <label className="admin-field">
                <span>Единица измерения</span>
                <SearchableSelect inputId="import-product-unit" onChange={(value) => onChange({ ...form, product_unit_id: value })} options={dictionarySelectOptions(dictionaries.product_units)} placeholder="Выберите единицу" value={form.product_unit_id} />
              </label>
              <label className="admin-field">
                <span>Статус</span>
                <SearchableSelect inputId="import-product-status" onChange={(value) => onChange({ ...form, product_status_id: value })} options={dictionarySelectOptions(dictionaries.product_statuses)} placeholder="Выберите статус" value={form.product_status_id} />
              </label>
            </fieldset>
            <div className="admin-product-editor__hint">
              <strong>После создания</strong>
              <p>Новый товар появится в поиске строки импорта. Выберите его и сохраните сопоставление.</p>
            </div>
          </aside>
        </div>

        <div className="admin-modal__actions">
          <button className="company-request-item__action" type="button" onClick={onClose}>Отмена</button>
          <button className="workspace-secondary-button" disabled={isSaving} type="submit">
            {isSaving ? 'Создаём…' : 'Создать товар'}
          </button>
        </div>
      </ValidatedForm>
    </section>
  );
}

function renderDictionaryOptions(items: AdminDictionaryItem[]): ReactNode {
  return items.map((item) => (
    <option key={item.id} value={String(item.id)}>
      {item.name}
    </option>
  ));
}

function dictionarySelectOptions(items: AdminDictionaryItem[]): SearchableSelectOption[] {
  return items.map((item) => ({
    label: item.name,
    value: String(item.id),
  }));
}

function catalogCategorySelectOptions(categories: AdminCatalogCategory[]): SearchableSelectOption[] {
  return categories.map((category) => ({
    label: category.category_parent_name === null || category.category_parent_name === undefined
      ? category.category_name
      : `${category.category_parent_name} / ${category.category_name}`,
    value: String(category.category_id),
  }));
}

function renderImportStatusOptions(): ReactNode {
  return manualImportStatusOptions.map((item) => (
    <option key={item.id} value={String(item.id)}>
      {item.name}
    </option>
  ));
}


function getDictionaryNameById(items: AdminDictionaryItem[], id: number): string {
  const foundItem = items.find((item) => item.id === id);

  if (foundItem === undefined) {
    return 'Неизвестно';
  }

  return foundItem.name;
}

function getDictionaryNameByValue(items: AdminDictionaryItem[], value: boolean): string {
  const foundItem = items.find((item) => item.value === value);

  if (foundItem === undefined) {
    return 'Неизвестно';
  }

  return foundItem.name;
}
